import { useCRM } from '../context/CRMContext';
import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { authFetch } from '../utils/apiClient';
import { 
    Network, Users, DollarSign, Activity, TrendingUp, TrendingDown, 
    Wallet, Search, ArrowLeft, Settings, Filter, ChevronRight, ChevronDown, ChevronUp,
    Award, Layers, X, Link as LinkIcon, Copy, FileImage, Download, History, Plus, Briefcase, Edit2,
    Calendar, Zap, Clock, Coins, PieChart
} from 'lucide-react';
import { MOCK_IB_TREE } from '../data/mockData';
import ClientProfile from '../components/ClientProfile';
import { generatePartnerPDFReport } from '../utils/pdfGenerator';

// --- CONFIGURATION: STANDARD MULTI-TIER REBATES ---
const STANDARD_COMMISSIONS = [
    { id: 't1', label: 'Tier 1 (Direct)', desc: 'Direct Clients', fx: 4.50, metals: 8.00, color: 'text-white' },
    { id: 't2', label: 'Tier 2 (Sub-IB)', desc: 'Referred by T1', fx: 1.00, metals: 3.00, color: 'text-slate-300' },
    { id: 't3', label: 'Tier 3 (Network)', desc: 'Referred by T2', fx: 1.00, metals: 2.00, color: 'text-slate-500' }
];

// --- CONFIGURATION: VOLUME PERFORMANCE BONUS ---
const PERFORMANCE_BONUS_TIERS = [
  { threshold: 4500, rate: 0.10, label: "Tier 3 (+10%)", color: "bg-purple-500" },
  { threshold: 1000, rate: 0.08, label: "Tier 2 (+8%)", color: "bg-blue-500" },
  { threshold: 450, rate: 0.04, label: "Tier 1 (+4%)", color: "bg-green-500" },
  { threshold: 0, rate: 0.00, label: "Base Tier (0%)", color: "bg-slate-600" }
];

// --- LOCAL COMPONENTS ---
const StatCard = ({ title, value, subtext, trend, icon: Icon, trendUp }) => (
  <div className="bg-slate-900 rounded-xl p-6 shadow-sm border border-slate-800 flex items-start justify-between">
    <div>
      <p className="text-sm font-medium text-slate-400 mb-1">{title}</p>
      <h3 className="text-2xl font-bold text-white">{value}</h3>
      <div className="flex items-center mt-2">
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full flex items-center ${trendUp ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}>
          {trendUp ? <TrendingUp size={12} className="mr-1"/> : <TrendingDown size={12} className="mr-1"/>}
          {trend}
        </span>
        <span className="text-xs text-slate-500 ml-2">{subtext}</span>
      </div>
    </div>
    <div className="p-3 bg-slate-800 rounded-lg text-amber-500 border border-slate-700 shadow-inner">
      <Icon size={24} />
    </div>
  </div>
);

const StatusBadge = ({ status }) => {
  const styles = {
    Active: "bg-green-500/10 text-green-400 border-green-500/20",
    Pending: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    Suspended: "bg-red-500/10 text-red-400 border-red-500/20",
    Onboarding: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    Inactive: "bg-red-500/10 text-red-400 border-red-500/20" // 🚨 Added Inactive
  };
  return (
    <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold border ${styles[status] || "bg-slate-800 text-slate-400"}`}>
      {status}
    </span>
  );
};

const LeadershipOverridesCard = ({ countryBonusTotal = 0, regionalBonusTotal = 0, countryCode, regionCode }) => {
    return (
        <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6">
            <h3 className="font-bold text-white mb-4 flex items-center gap-2">
                <span className="text-yellow-500">🌍</span> Leadership Overrides
            </h3>
            <div className="space-y-3">
                {(countryCode || countryBonusTotal > 0) && (
                    <div className="flex justify-between items-center bg-[#1a212d] p-3 rounded-lg border border-slate-800">
                        <div>
                            <p className="text-sm font-bold text-white flex items-center gap-2">
                                Country Leader 
                                {countryCode && <span className="text-[10px] bg-blue-900/30 text-blue-400 border border-blue-500/20 px-2 py-0.5 rounded font-mono">{countryCode}</span>}
                            </p>
                            <p className="text-[10px] text-slate-400 uppercase tracking-wider mt-1">NATION VOLUME</p>
                        </div>
                        <div className="text-right">
                            <p className="text-sm font-bold text-white">
                                <span className="text-green-400 font-normal mr-2">$1 / Lot</span>
                                ${countryBonusTotal.toLocaleString(undefined, {minimumFractionDigits: 2})}
                            </p>
                        </div>
                    </div>
                )}
                {(regionCode || regionalBonusTotal > 0) && (
                    <div className="flex justify-between items-center bg-[#1a212d] p-3 rounded-lg border border-slate-800">
                        <div>
                            <p className="text-sm font-bold text-white flex items-center gap-2">
                                Regional Manager
                                {regionCode && <span className="text-[10px] bg-purple-900/30 text-purple-400 border border-purple-500/20 px-2 py-0.5 rounded font-mono">{regionCode}</span>}
                            </p>
                            <p className="text-[10px] text-slate-400 uppercase tracking-wider mt-1">CONTINENT VOLUME</p>
                        </div>
                        <div className="text-right">
                            <p className="text-sm font-bold text-white">
                                <span className="text-green-400 font-normal mr-2">$1 / Lot</span>
                                ${regionalBonusTotal.toLocaleString(undefined, {minimumFractionDigits: 2})}
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const formatCurrency = (amount) => {
    const val = parseFloat(amount || 0);
    if (!val || isNaN(val) || val === 0) return "$0.00";
    const isNeg = val < 0;
    const absVal = Math.abs(val);
    const prefix = isNeg ? "-$" : "$";
    if (absVal < 0.0001) return isNeg ? "> -$0.0001" : "< $0.0001";
    if (absVal < 0.01) return `${prefix}${absVal.toFixed(4)}`;
    return `${prefix}${absVal.toFixed(2)}`;
};

const formatLots = (volume) => {
    const val = parseFloat(volume || 0);
    if (!val || isNaN(val) || val === 0) return "0.00";
    const absVal = Math.abs(val);
    if (absVal < 0.0001) return "< 0.0001";
    if (absVal < 0.01) return val.toFixed(4);
    return val.toFixed(2);
};

const formatAllocPct = (alloc) => {
    const val = parseFloat(alloc || 0);
    if (!val || isNaN(val) || val === 0) return "0.00%";
    const absVal = Math.abs(val);
    if (absVal < 0.01) return `${parseFloat(absVal.toFixed(6))}%`;
    return `${absVal.toFixed(2)}%`;
};

const PartnersPage = ({ currentUser }) => {

    const handlePrePayoutSync = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_XVALLEY_API_URL || 'https://innovative-rebirth-production.up.railway.app'}/api/commissions/trigger-pamm-prepayout`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
            });
            if (response.ok) {
                alert('Pre-payout sync started! The Defily Tree and PAMM Allocations are updating in the background.');
            } else {
                alert('Failed to trigger sync.');
            }
        } catch (error) {
            console.error('Sync error:', error);
            alert('Failed to trigger sync: ' + error.message);
        }
    };

  const {} = useCRM();
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedIB, setSelectedIB] = useState(null);
  const [viewMode, setViewMode] = useState('list'); 
  const [clientTierTab, setClientTierTab] = useState('t1'); 
  const [isPammMode, setIsPammMode] = useState(false);
  const [pammRuns, setPammRuns] = useState([]);
  const [selectedRunId, setSelectedRunId] = useState('all');
  const [pammLedger, setPammLedger] = useState([]);
  const [pammWriteLedgerOnly, setPammWriteLedgerOnly] = useState(false);
  const [clientMetricsMap, setClientMetricsMap] = useState({});
  // --- 🚨 TRUE GLOBAL VOLUME FIX ---
  const [trueGlobalVolume, setTrueGlobalVolume] = useState(0);

  // 🚨 NEW: PERFORMANCE FEE STATE
  const [profitLogs, setProfitLogs] = useState([]);

  useEffect(() => {
      const fetchTrueGlobalVolume = async () => {
          try {
              const thirtyDaysAgo = new Date();
              thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
              
              const { data } = await supabase
                  .from('daily_volume_ledger')
                  .select('volume')
                  .gte('created_at', thirtyDaysAgo.toISOString());
                  
              if (data) {
                  // This sums the raw, unduplicated lots straight from the database
                  const sum = data.reduce((acc, row) => acc + Number(row.volume || 0), 0);
                  setTrueGlobalVolume(sum);
              }
          } catch (err) {
              console.error("Error fetching true volume:", err);
          }
      };
      fetchTrueGlobalVolume();
  }, []);
  
  // --- NEW STATE: SEARCH & FILTER ---
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showFilterModal, setShowFilterModal] = useState(false);

  // 🚨 SERVER-SIDE SEARCH DEBOUNCER: Waits for the user to stop typing
  useEffect(() => {
      const timer = setTimeout(() => setDebouncedSearch(searchTerm), 500);
      return () => clearTimeout(timer);
  }, [searchTerm]);
// ✅ CORRECT:
const [filters, setFilters] = useState({
      minClients: 0,
      minRebate: 0
}); // <--- Close the filters state first

  // --- NEW STATE: PAMM PREVIEW MODAL ---
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [pammPayloadCache, setPammPayloadCache] = useState(null);
  const [isExecutingPayout, setIsExecutingPayout] = useState(false);

  // Modal States
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showAssetModal, setShowAssetModal] = useState(false);
  const [showPayoutHistory, setShowPayoutHistory] = useState(false);
  
  // Link Generation State
  const [newLinkData, setNewLinkData] = useState({ type: 'auto', customSuffix: '' });

  // --- NEW: PAYOUT MODAL STATE & LOGIC ---
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [payoutForm, setPayoutForm] = useState({ amount: '', method: 'USDT (TRC20)', address: '', notes: '' });
  const [isProcessingPayout, setIsProcessingPayout] = useState(false);

  const handleProcessPayout = async () => {
      if (!payoutForm.amount || isNaN(payoutForm.amount) || Number(payoutForm.amount) <= 0) {
          alert("Please enter a valid amount greater than 0.");
          return;
      }
      if (Number(payoutForm.amount) > computedWalletBalance) {
          alert("Cannot payout more than the available wallet balance.");
          return;
      }

      setIsProcessingPayout(true);
      try {
          const withdrawalRef = `PAY-${Math.floor(100000 + Math.random() * 900000)}`;
          const payoutRes = await authFetch('https://innovative-rebirth-production.up.railway.app/api/crm/partners/process-payout', {
              method: 'POST',
              body: JSON.stringify({
                  partnerEmail: selectedIB.email,
                  amount: Number(payoutForm.amount),
                  method: payoutForm.method,
                  address: payoutForm.address,
                  notes: payoutForm.notes,
                  referenceId: withdrawalRef
              })
          });
          const payoutJson = await payoutRes.json();
          if (!payoutRes.ok || !payoutJson.success) {
              throw new Error(payoutJson?.error || 'Failed to process payout on server.');
          }
          alert("Payout successfully recorded! The ledger will now recalculate.");
          setShowPayoutModal(false);
          window.location.reload();
          return;

          // 1. Save to the withdrawals_ledger table
          const { error } = await supabase.from('withdrawals_ledger').insert([{
              partner_id: selectedIB.email,
              amount: Number(payoutForm.amount),
              method: payoutForm.method,
              destination: payoutForm.address,
              notes: payoutForm.notes,
              status: 'Completed', 
              reference_id: withdrawalRef
          }]);
          if (error) throw error;

          // 2. 🚨 ADDED: Explicitly deduct from the wallet_ledger so it shows in the UI Headless Ledger
          const { error: ledgerErr } = await supabase.from('wallet_ledger').insert([{
              partner_id: selectedIB.email,
              transaction_type: 'withdrawal',
              amount: -Math.abs(Number(payoutForm.amount)),
              description: `Admin Processed Payout: ${payoutForm.method}`,
              created_at: new Date().toISOString()
          }]);
          if (ledgerErr) throw ledgerErr;

          alert("Payout successfully recorded! The ledger will now recalculate.");
          setShowPayoutModal(false);
          window.location.reload(); 
      } catch (err) {
          console.error("Payout Error:", err);
          alert("Failed to process payout: " + err.message);
      } finally {
          setIsProcessingPayout(false);
      }
  };

  // --- NEW: MANUAL BONUS LOGIC (BACKFILL) ---
  const [showManualBonusModal, setShowManualBonusModal] = useState(false);
  const [manualBonusForm, setManualBonusForm] = useState({ amount: '', type: 'challenge_bonus', description: '', clientEmail: '' });
  const [isProcessingManualBonus, setIsProcessingManualBonus] = useState(false);

  const handleAddManualBonus = async () => {
      if (!manualBonusForm.amount || isNaN(manualBonusForm.amount) || Number(manualBonusForm.amount) <= 0) {
          alert("Please enter a valid amount greater than 0.");
          return;
      }
      if (!manualBonusForm.description) {
          alert("Please enter a description (e.g., 'Missing $50K Challenge commission').");
          return;
      }

      setIsProcessingManualBonus(true);
      try {
          const amountNum = Number(manualBonusForm.amount);
          const bonusRes = await authFetch('https://innovative-rebirth-production.up.railway.app/api/crm/partners/manual-bonus', {
              method: 'POST',
              body: JSON.stringify({
                  partnerEmail: selectedIB.email,
                  type: manualBonusForm.type,
                  amount: amountNum,
                  description: manualBonusForm.description,
                  clientEmail: manualBonusForm.clientEmail
              })
          });
          const bonusJson = await bonusRes.json();
          if (!bonusRes.ok || !bonusJson.success) {
              throw new Error(bonusJson?.error || 'Failed to add manual bonus on server.');
          }
          alert("Manual bonus added successfully! The ledger will now recalculate.");
          setShowManualBonusModal(false);
          setManualBonusForm({ amount: '', type: 'challenge_bonus', description: '', clientEmail: '' });
          window.location.reload();
          return;
          
          // 1. Insert into wallet_ledger
          const { error: ledgerError } = await supabase.from('wallet_ledger').insert([{
              partner_id: selectedIB.email, 
              transaction_type: manualBonusForm.type, 
              amount: amountNum,
              description: manualBonusForm.description,
              created_at: new Date().toISOString()
          }]);
          if (ledgerError) throw ledgerError;

          // 2. Insert into activity_log for UI
          const { error: activityError } = await supabase.from('activity_log').insert([{
              partner_id: selectedIB.email,
              event_type: manualBonusForm.type,
              client_name: manualBonusForm.clientEmail ? manualBonusForm.clientEmail.split('@')[0] : 'Admin Override',
              amount: amountNum,
              symbol: 'USD',
              created_at: new Date().toISOString()
          }]);
          if (activityError) throw activityError;

          alert("Manual bonus added successfully! The ledger will now recalculate.");
          setShowManualBonusModal(false);
          setManualBonusForm({ amount: '', type: 'challenge_bonus', description: '', clientEmail: '' });
          window.location.reload();
      } catch (err) {
          console.error("Manual Bonus Error:", err);
          alert("Failed to add manual bonus: " + err.message);
      } finally {
          setIsProcessingManualBonus(false);
      }
  };
  // --- END MANUAL BONUS LOGIC ---

  // --- NEW: HEADLESS COMMISSION ENGINE STATE ---
  const [ledgerData, setLedgerData] = useState([]);
  const [selectedIBLedger, setSelectedIBLedger] = useState([]);
  const [selectedIBWithdrawals, setSelectedIBWithdrawals] = useState([]);
  const [isLoadingIBLedger, setIsLoadingIBLedger] = useState(false);
  const [totalUnpaid, setTotalUnpaid] = useState(0);
  const [isLoadingLedger, setIsLoadingLedger] = useState(true);
  const [ledgerTimeframe, setLedgerTimeframe] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [ledgerPage, setLedgerPage] = useState(1);
  const [ledgerView, setLedgerView] = useState('earnings'); 
  const LEDGER_PAGE_SIZE = 20;

  // --- NEW: LIVE NETWORK TREE STATE ---
  const [rawNetworkData, setRawNetworkData] = useState([]);
  const [defilyNodes, setDefilyNodes] = useState([]); // 🚨 NEW: Web3 Tree Data
  const [liveTree, setLiveTree] = useState({ t1: [], t2: [], t3: [], t4: [], t5: [], t6: [], t7: [], t8: [], master: [] }); // 🚨 ADDED 8 tiers and master array

  // --- NEW: HARVESTER MANAGEMENT STATE & LOGIC ---
  const [activePamms, setActivePamms] = useState([]);
  const [newPamm, setNewPamm] = useState({ account_id: '', strategy_name: '', strategy_provider_email: '', community_structure: 'standard', is_active: true });
  const [isLoadingPamms, setIsLoadingPamms] = useState(false);
  const [editingPammId, setEditingPammId] = useState(null);
  const [editingPammData, setEditingPammData] = useState(null);

  const handleEditClick = (pamm) => {
      setEditingPammId(pamm.id);
      setEditingPammData({
          strategy_name: pamm.strategy_name,
          account_id: pamm.account_id,
          strategy_provider_email: pamm.strategy_provider_email || '',
          community_structure: pamm.community_structure || 'standard'
      });
  };

  const handleSaveEditPamm = async () => {
      try {
          const { error } = await supabase.from('active_pamms').update({
              strategy_name: editingPammData.strategy_name,
              account_id: parseInt(editingPammData.account_id),
              strategy_provider_email: editingPammData.strategy_provider_email,
              community_structure: editingPammData.community_structure
          }).eq('id', editingPammId);
          if (error) throw error;
          setEditingPammId(null);
          fetchActivePamms();
      } catch (err) {
          alert('Failed to update harvester: ' + err.message);
      }
  };

  const fetchActivePamms = async () => {
      setIsLoadingPamms(true);
      try {
          const { data, error } = await supabase.from('active_pamms').select('*').order('created_at', { ascending: false });
          if (error) throw error;
          setActivePamms(data || []);
      } catch (err) {
          console.error("Error fetching active PAMMs:", err);
      } finally {
          setIsLoadingPamms(false);
      }
  };

  const handleAddPamm = async () => {
      if (!newPamm.account_id || !newPamm.strategy_name) {
          alert('Account ID and Strategy Name are required.');
          return;
      }
      try {
          const { error } = await supabase.from('active_pamms').insert([{
              account_id: parseInt(newPamm.account_id),
              strategy_name: newPamm.strategy_name,
              strategy_provider_email: newPamm.strategy_provider_email,
              community_structure: newPamm.community_structure,
              is_active: newPamm.is_active
          }]);
          if (error) throw error;
          alert('Harvester added successfully.');
          setNewPamm({ account_id: '', strategy_name: '', strategy_provider_email: '', community_structure: 'standard', is_active: true });
          fetchActivePamms();
      } catch (err) {
          alert('Failed to add harvester: ' + err.message);
      }
  };

  const handleTogglePamm = async (id, currentStatus) => {
      try {
          const { error } = await supabase.from('active_pamms').update({ is_active: !currentStatus }).eq('id', id);
          if (error) throw error;
          fetchActivePamms();
      } catch (err) {
          alert('Failed to toggle harvester: ' + err.message);
      }
  };

  const handleDeletePamm = async (id) => {
      if (!window.confirm('Are you sure you want to delete this harvester?')) return;
      try {
          const { error } = await supabase.from('active_pamms').delete().eq('id', id);
          if (error) throw error;
          fetchActivePamms();
      } catch (err) {
          alert('Failed to delete harvester: ' + err.message);
      }
  };

  // --- NEW: FETCH PAMM DATA ---
  // 🚨 FETCH LIVE PAMM ALLOCATIONS FOR SELECTED IB NETWORK
  useEffect(() => {
      if (selectedIB) {
          const fetchLiveAllocations = async () => {
              try {
                  const res = await fetch('https://innovative-rebirth-production.up.railway.app/api/commissions/pamm/live-allocations');
                  if (res.ok) {
                      const json = await res.json();
                      const allocs = json.allocations || [];
                      const mapping = {};
                      allocs.forEach(a => {
                          const em = String(a.email || '').toLowerCase().trim();
                          if (!mapping[em]) {
                              mapping[em] = {
                                  totalAllocationAmount: 0,
                                  totalLiveEquity: 0,
                                  allocationPercentage: 0,
                                  strategies: []
                              };
                          }
                          const rawEq = a.pamm_live_equity;
                          const eq = (rawEq !== null && rawEq !== undefined && !isNaN(Number(rawEq))) ? parseFloat(rawEq) : 0;
                          const dep = (eq <= 0) ? 0 : (parseFloat(a.allocation_amount) || 0);
                          const pct = (eq <= 0) ? 0 : (parseFloat(a.allocation_percentage) || 0);

                          if (eq > 0) {
                              mapping[em].totalAllocationAmount += dep;
                              mapping[em].totalLiveEquity += eq;
                              mapping[em].allocationPercentage += pct;
                              mapping[em].strategies.push({
                                  strategyName: a.master_name,
                                  masterId: a.master_id,
                                  allocationAmount: dep,
                                  liveEquity: eq,
                                  allocationPercentage: pct
                              });
                          }
                      });
                      setLivePammMap(mapping);
                  }
              } catch (e) {
                  console.warn("Could not fetch live PAMM allocations:", e);
              }
          };
          fetchLiveAllocations();
      }
  }, [selectedIB]);

  useEffect(() => {
      if (selectedIB || isPammMode) {
          fetch('https://innovative-rebirth-production.up.railway.app/api/commissions/pamm/runs')
              .then(res => res.json())
              .then(data => setPammRuns(data.runs || []))
              .catch(err => console.error("Error fetching PAMM runs:", err));
      }
  }, [selectedIB, isPammMode]);

  useEffect(() => {
      if (isPammMode && selectedIB) {
          const pEmail = selectedIB.actual_email || selectedIB.email;
          if (pEmail) {
              fetch(`https://innovative-rebirth-production.up.railway.app/api/commissions/pamm/ledger/${encodeURIComponent(pEmail)}?runId=${selectedRunId}`)
                  .then(res => res.json())
                  .then(data => {
                      const ledgerRows = data.ledger || [];
                      if (selectedRunId === 'all') {
                          // Aggregate locally
                          const aggregated = {};
                          const latestPerStrategy = {};

                          ledgerRows.forEach(row => {
                              const email = row.client_email || row.ClientID || row.client_id;
                              const stratName = row.pamm_commission_runs?.strategy_name || 'Unknown';

                              if (!aggregated[email]) {
                                  aggregated[email] = {
                                      client_email: email,
                                      profit_split: 0,
                                      pamm_volume: 0,
                                      pamm_rebate: 0,
                                      pamm_deposit: 0,
                                      allocation_percentage: 0
                                  };
                                  latestPerStrategy[email] = {};
                              }
                              
                              aggregated[email].profit_split += Number(row.profit_split || row.ProfitSplit || 0);
                              aggregated[email].pamm_volume += Number(row.pamm_volume || row.PammVolume || 0);
                              aggregated[email].pamm_rebate += Number(row.pamm_rebate || row.PammRebate || 0);
                              
                              // The ledger is ordered by created_at DESC, so the first time we see a strategy, it's the latest
                              if (latestPerStrategy[email][stratName] === undefined) {
                                  latestPerStrategy[email][stratName] = {
                                      deposit: Number(row.pamm_deposit || row.PammDeposit || row.allocation_amount || row.AllocationAmount || 0),
                                      alloc: Number(row.allocation_percentage || row.AllocationPercentage || 0)
                                  };
                              }
                          });

                          // Sum up the latest deposits per strategy to get the true total active deposit
                          // And determine the correct allocation percentage display string
                          Object.keys(aggregated).forEach(email => {
                              let totalActiveDeposit = 0;
                              const strats = Object.keys(latestPerStrategy[email]);
                              
                              strats.forEach(strat => {
                                  totalActiveDeposit += latestPerStrategy[email][strat].deposit;
                              });
                              
                              aggregated[email].pamm_deposit = totalActiveDeposit;
                              
                              if (strats.length === 1) {
                                  aggregated[email].allocation_percentage = formatAllocPct(latestPerStrategy[email][strats[0]].alloc);
                              } else if (strats.length > 1) {
                                  aggregated[email].allocation_percentage = 'Multiple';
                              } else {
                                  aggregated[email].allocation_percentage = '0.00%';
                              }
                          });

                          setPammLedger(Object.values(aggregated));
                      } else {
                          setPammLedger(ledgerRows);
                      }
                  })
                  .catch(err => console.error("Error fetching PAMM ledger:", err));
          }
      }
  }, [isPammMode, selectedIB, selectedRunId]);

  const [pammLifetimeLedger, setPammLifetimeLedger] = useState({});
  const [rawLifetimePammLedger, setRawLifetimePammLedger] = useState([]);
  const [expandedHistoryClient, setExpandedHistoryClient] = useState(null);
  const [expandedClientLedger, setExpandedClientLedger] = useState([]);
  const [isLoadingExpandedLedger, setIsLoadingExpandedLedger] = useState(false);

  useEffect(() => {
      if (!expandedHistoryClient?.email) {
          setExpandedClientLedger([]);
          return;
      }
      
      const fetchClientLedger = async () => {
          setIsLoadingExpandedLedger(true);
          try {
              const { data, error } = await supabase
                  .from('wallet_ledger')
                  .select('*')
                  .eq('partner_id', expandedHistoryClient.email)
                  .in('transaction_type', ['commission', 'pamm_commission', 'performance_bonus'])
                  .order('created_at', { ascending: false });
              
              if (error) throw error;
              setExpandedClientLedger(data || []);
          } catch (err) {
              console.error("Error fetching expanded client ledger:", err);
          } finally {
              setIsLoadingExpandedLedger(false);
          }
      };

      fetchClientLedger();
  }, [expandedHistoryClient]);

  useEffect(() => {
      if (selectedIB) {
          const pEmail = selectedIB.actual_email || selectedIB.email;
          if (pEmail) {
              fetch(`https://innovative-rebirth-production.up.railway.app/api/commissions/pamm/ledger/${encodeURIComponent(pEmail)}?runId=all`)
                  .then(res => res.json())
                  .then(data => {
                      const ledgerRows = data.ledger || [];
                      setRawLifetimePammLedger(ledgerRows);
                      const aggregated = {};
                      ledgerRows.forEach(row => {
                          const email = String(row.client_email || row.ClientID || row.client_id || '').toLowerCase().trim();
                          if (!aggregated[email]) {
                              aggregated[email] = { pamm_rebate: 0, profit_split: 0 };
                          }
                          aggregated[email].pamm_rebate += Number(row.pamm_rebate || row.PammRebate || 0);
                          aggregated[email].profit_split += Number(row.profit_split || row.ProfitSplit || 0);
                      });
                      setPammLifetimeLedger(aggregated);
                  })
                  .catch(err => console.error("Error fetching PAMM lifetime ledger:", err));
          }
      }
  }, [selectedIB]);
  const [allWithdrawals, setAllWithdrawals] = useState([]);
  
  // 🚨 NEW: State to hold dynamically fetched XValley financial data for the network tree
  const [liveFinancials, setLiveFinancials] = useState({});

  // 🚨 NEW: Function to lazily fetch live XValley data for a specific tier
  const fetchLiveFinancialsForTier = async (clientList) => {
      // Don't fetch if empty
      if (!clientList || clientList.length === 0) return;

      // Extract emails that we haven't fetched yet
      const emailsToFetch = clientList
          .map(c => c.email)
          .filter(email => email && !liveFinancials[email]);

      if (emailsToFetch.length === 0) return;

      // Process in small batches of 3 to avoid hammering the backend/XValley bridge
      for (let i = 0; i < emailsToFetch.length; i += 3) {
          const batch = emailsToFetch.slice(i, i + 3);
          
          await Promise.all(batch.map(async (email) => {
              try {
                  const res = await fetch(`${process.env.REACT_APP_XVALLEY_API_URL}/api/xvalley/account/${encodeURIComponent(email)}`);
                  if (res.ok) {
                      const data = await res.json();
                      if (data && data.allAccounts) {
                          // Sum up all LIVE accounts (ignore Demo)
                          let totalEquity = 0;
                          let totalBalance = 0;
                          let total10xEquity = 0;
                          data.allAccounts.forEach(acc => {
                              if (!acc.isDemo) {
                                  if (acc.is10x) {
                                      total10xEquity += Number(acc.equity || 0);
                                  } else {
                                      totalEquity += Number(acc.equity || 0);
                                      totalBalance += Number(acc.balance || 0);
                                  }
                              }
                          });

                          setLiveFinancials(prev => ({
                              ...prev,
                              [email]: {
                                  equity: totalEquity,
                                  balance: totalBalance,
                                  equity10x: total10xEquity,
                                  status: (totalBalance > 0 || total10xEquity > 0) ? 'Active' : 'Inactive'
                              }
                          }));
                      }
                  }
              } catch (err) {
                  // Silent fail for individual accounts so it doesn't break the loop
                  console.warn(`Failed to fetch live financials for ${email}`, err);
              }
          }));
          
          // Brief pause between batches
          await new Promise(r => setTimeout(r, 200));
      }
  };

  // 🚨 NEW: Trigger the financial fetcher whenever the active tab changes
  useEffect(() => {
      if (!selectedIB) return;
      
      let activeList = [];
      if (clientTierTab === 't1') activeList = liveTree.t1;
      if (clientTierTab === 't2') activeList = liveTree.t2;
      if (clientTierTab === 't3') activeList = liveTree.t3;
      if (clientTierTab === 'master') activeList = liveTree.master; // 🚨 ADDED master

      fetchLiveFinancialsForTier(activeList);
      setClientListPage(1);
  }, [clientTierTab, liveTree, selectedIB]);
  
  // --- NEW: PARTNER LEDGER UI STATE ---
  const [partnerLedgerTimeframe, setPartnerLedgerTimeframe] = useState('all');
  const [partnerLedgerStatus, setPartnerLedgerStatus] = useState('all');
  const [partnerLedgerPage, setPartnerLedgerPage] = useState(1);
  const [partnerWalletLedgerPage, setPartnerWalletLedgerPage] = useState(1);
  const [clientListPage, setClientListPage] = useState(1);

  // --- NEW: GLOBAL PAYOUTS TAB STATE ---
  const [mainTab, setMainTab] = useState('network'); // 'network' | 'pamm' | 'payouts'
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [showIssuesOnly, setShowIssuesOnly] = useState(false); // 🚨 Added toggle state for Admin Withdrawals

  // --- NEW: PAMM HUB STATE & LOGIC ---
  const [pammExclusions, setPammExclusions] = useState([]);
  const [pammLogs, setPammLogs] = useState([]);
  const [newPammAccountId, setNewPammAccountId] = useState('');
  const [newPammStrategyName, setNewPammStrategyName] = useState('');
  
  const [pammFile, setPammFile] = useState(null);
  const [pammMasterId, setPammMasterId] = useState('');
  const [pammStrategy, setPammStrategy] = useState('');
  const [pammCalculationMode, setPammCalculationMode] = useState('live'); // 'live' | 'csv'
  const [livePammMap, setLivePammMap] = useState({});
  const [pammStrategyProviderEmail, setPammStrategyProviderEmail] = useState('');
  const [pammCommunityStructure, setPammCommunityStructure] = useState('standard');
  const [pammStartDate, setPammStartDate] = useState('');
  const [pammEndDate, setPammEndDate] = useState('');
  const [isUploadingPamm, setIsUploadingPamm] = useState(false);

  useEffect(() => {
      if (mainTab === 'pamm') {
          const fetchPammExclusions = async () => {
              const { data } = await supabase.from('pamm_exclusions').select('*').order('created_at', { ascending: false });
              if (data) setPammExclusions(data);
          };
          const fetchPammLogs = async () => {
              const { data } = await supabase.from('pamm_payout_logs').select('*').order('created_at', { ascending: false });
              if (data) setPammLogs(data);
          };
          // 🚨 NEW: Fetch Performance Fee Logs
          const fetchProfitLogs = async () => {
              const { data } = await supabase.from('pamm_profit_logs').select('*').order('created_at', { ascending: false });
              if (data) setProfitLogs(data);
          };
          
          fetchPammExclusions();
          fetchPammLogs();
          fetchProfitLogs(); // <-- Call the new fetcher
          fetchActivePamms();
      }
  }, [mainTab]);

  const handleAddPammExclusion = async () => {
      if (!newPammAccountId || !newPammStrategyName) return alert("Please enter both Account ID and Strategy Name.");
      const { error } = await supabase.from('pamm_exclusions').insert([{
          account_id: newPammAccountId,
          strategy_name: newPammStrategyName
      }]);
      if (error) return alert("Failed to add exclusion: " + error.message);
      
      setNewPammAccountId('');
      setNewPammStrategyName('');
      const { data } = await supabase.from('pamm_exclusions').select('*').order('created_at', { ascending: false });
      if (data) setPammExclusions(data);
  };

  const handleDeletePammExclusion = async (id) => {
      if (!window.confirm("Remove this PAMM exclusion? It will be processed in daily sweeps again.")) return;
      const { error } = await supabase.from('pamm_exclusions').delete().eq('id', id);
      if (error) return alert("Failed to delete: " + error.message);
      
      const { data } = await supabase.from('pamm_exclusions').select('*').order('created_at', { ascending: false });
      if (data) setPammExclusions(data);
  };

  const handleUploadPammCsv = () => {
      if (!pammMasterId || !pammStrategy || !pammStartDate || !pammEndDate) {
          return alert("Please fill in Master Account ID, Strategy Name, and Date Range.");
      }

      if (pammCalculationMode === 'csv') {
          if (!pammFile) return alert("Please select a CSV file.");
          setIsUploadingPamm(true);

          const reader = new FileReader();
          reader.onload = async (e) => {
              try {
                  const text = e.target.result;
                  const lines = text.split('\n').filter(line => line.trim() !== '');
                  const headers = lines[0].split(',').map(h => h.replace(/"/g, '').trim().toLowerCase());
                  
                  const allocIndex = headers.findIndex(h => h === 'allocation');
                  const createdByIndex = headers.findIndex(h => h === 'createdby');
                  const allocAmountIndex = headers.findIndex(h => h === 'allocation amount');
                  const followerEquityIndex = headers.findIndex(h => h === 'follower equity');
                  const slaveIdIndex = headers.findIndex(h => ['slave_id', 'slaveid', 'slave id', 'account_id', 'account id', 'trader_id', 'traderid'].includes(h));

                  if (allocIndex === -1 || createdByIndex === -1) {
                      throw new Error("CSV is missing 'Allocation' or 'CreatedBy' columns.");
                  }

                  const csvRows = [];
                  for (let i = 1; i < lines.length; i++) {
                      const row = lines[i].split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/);
                      const allocation = row[allocIndex]?.replace(/"/g, '').trim();
                      const createdBy = row[createdByIndex]?.replace(/"/g, '').trim();
                      const allocationAmount = allocAmountIndex !== -1 ? row[allocAmountIndex]?.replace(/"/g, '').trim() : '0';
                      const followerEquity = followerEquityIndex !== -1 ? row[followerEquityIndex]?.replace(/"/g, '').trim() : '0';
                      const slaveId = slaveIdIndex !== -1 ? row[slaveIdIndex]?.replace(/"/g, '').trim() : null;

                      if (allocation && createdBy) {
                          csvRows.push({ 
                              allocation, 
                              createdBy, 
                              allocationAmount, 
                              followerEquity,
                              ...(slaveId ? { slave_id: slaveId, slaveId } : {})
                          });
                      }
                  }

                  await executePammCalculation(csvRows, false);
              } catch (err) {
                  alert(`Error parsing CSV: ${err.message}`);
                  setIsUploadingPamm(false);
              }
          };
          reader.readAsText(pammFile);
      } else {
          // ⚡ 1-Click Run from Supabase Table
          setIsUploadingPamm(true);
          executePammCalculation([], true);
      }
  };

  const executePammCalculation = async (csvRows, useLiveDb) => {
      try {
          const payload = {
              masterAccountId: pammMasterId,
              strategyName: pammStrategy,
              strategyProviderEmail: pammStrategyProviderEmail,
              communityStructure: pammCommunityStructure,
              startDate: pammStartDate,
              endDate: pammEndDate,
              useLiveDb: useLiveDb,
              csvRows: csvRows,
              dryrun: !pammWriteLedgerOnly,
              writeLedgerOnly: pammWriteLedgerOnly
          };

          const response = await authFetch('https://innovative-rebirth-production.up.railway.app/api/commissions/process-pamm-csv', {
              method: 'POST',
              body: JSON.stringify(payload)
          });

          const data = await response.json();
          if (data.success && data.dryrun) {
              setPammPayloadCache({ ...payload, dryrun: false });
              setPreviewData(data);
              setPreviewModalOpen(true);
              setPammFile(null);
          } else if (data.success) {
              if (pammWriteLedgerOnly) {
                  alert("✅ PAMM Historical Data Backfilled Successfully!");
              } else {
                  alert(`✅ Success: ${data.message || 'PAMM Calculation Completed'}`);
              }
              setPammFile(null);
              setPammStartDate('');
              setPammEndDate('');
              setPammMasterId('');
              setPammStrategy('');
              setPammStrategyProviderEmail('');
              setPammCommunityStructure('standard');
          } else {
              alert(`❌ Error: ${data.error}`);
          }
      } catch (err) {
          alert(`Calculation Error: ${err.message}`);
      } finally {
          setIsUploadingPamm(false);
      }
  };

  const handleExecutePammPayouts = async () => {
      if (!pammPayloadCache) return;
      setIsExecutingPayout(true);
      try {
          const response = await authFetch('https://innovative-rebirth-production.up.railway.app/api/commissions/process-pamm-csv', {
              method: 'POST',
              body: JSON.stringify(pammPayloadCache)
          });

          const data = await response.json();
          if (data.success) {
              alert(`✅ Payouts Executed Successfully!`);
              setPreviewModalOpen(false);
              setPreviewData(null);
              setPammPayloadCache(null);
          } else {
              alert(`❌ Error executing payouts: ${data.error}`);
          }
      } catch (err) {
          alert(`Execution Error: ${err.message}`);
      } finally {
          setIsExecutingPayout(false);
      }
  };

  // --- NEW: COMMISSION EDITOR STATE ---
  const [commissionConfig, setCommissionConfig] = useState({ type: 'multi_tier', rate_fx: 20, rate_non_fx: 20 });
  const [isSavingCommission, setIsSavingCommission] = useState(false);

  // --- NEW: INLINE SUB-IB COMMISSION EDITOR ---
  const [expandedClient, setExpandedClient] = useState(null);
  const [subIbForm, setSubIbForm] = useState({ rate_fx: 0, rate_non_fx: 0 });
  const [isSavingSubIb, setIsSavingSubIb] = useState(false);

  const handleSaveSubIbConfig = async (subClientEmail) => {
      setIsSavingSubIb(true);
      try {
          const newConfig = { type: 'sub_ib_allocation', rate_fx: subIbForm.rate_fx, rate_non_fx: subIbForm.rate_non_fx };
          
          await supabase.from('user_broker_accounts').update({ commission_config: newConfig }).eq('email', subClientEmail);
          await supabase.from('profiles').update({ commission_config: newConfig }).eq('email', subClientEmail);

          alert('Sub-IB Allocation cut saved!');
          // 🚨 THE FIX: Close the editor instead of reloading the page
          setExpandedClient(null); 
      } catch (err) {
          alert("Failed to save cut: " + err.message);
      } finally {
          setIsSavingSubIb(false);
      }
  };

  // --- 🚨 THE BRIDGE: Resolves UUIDs back to Emails for the UI ---
  const resolvePartnerEmail = (idOrEmail) => {
      if (!idOrEmail) return 'Unknown';
      const searchStr = String(idOrEmail).toLowerCase().trim();
      
      const match = rawNetworkData.find(u => 
          String(u.actual_email).toLowerCase().trim() === searchStr || 
          String(u.auth_uuid).toLowerCase().trim() === searchStr ||
          String(u.id).toLowerCase().trim() === searchStr
      );
      
      // 🚨 THE FIX: Strictly return the actual_email property, never the UUID
      return (match && match.actual_email) ? match.actual_email : idOrEmail; 
  };

  useEffect(() => {
      // Whenever you click a different IB, load their saved config (or default to standard)
      if (selectedIB) {
          setCommissionConfig(selectedIB.commission_config || { type: 'multi_tier', rate_fx: 20, rate_non_fx: 20 });
      }
  }, [selectedIB]);

  const handleSaveCommission = async () => {
      if (!selectedIB) return;
      setIsSavingCommission(true);
      try {
          // 1. Update the primary broker accounts table (Used by the backend Math Engine)
          const { error: brokerErr } = await supabase
              .from('user_broker_accounts')
              .update({ commission_config: commissionConfig })
              .eq('email', selectedIB.email);

          if (brokerErr) throw brokerErr;

          // 2. Update the profiles table (To keep your master database perfectly in sync)
          const { error: profileErr } = await supabase
              .from('profiles')
              .update({ commission_config: commissionConfig })
              .eq('email', selectedIB.email);

          if (profileErr) console.error("Profile sync warning:", profileErr);

          // 🚨 THE FIX: Update local state to show changes immediately without reloading
          setSelectedIB(prev => ({ ...prev, commission_config: commissionConfig }));

          alert(`Success! Commission structure for ${selectedIB.name} updated.`);
      } catch (err) {
          alert("Failed to update commission: " + err.message);
      } finally {
          setIsSavingCommission(false);
      }
  };

  // --- NEW: LEADERSHIP ASSIGNMENT STATE ---
  const [leaderForm, setLeaderForm] = useState({ email: '', role: 'country', regionCode: '' });
  const [isAssigningLeader, setIsAssigningLeader] = useState(false);

  const handleAssignLeadership = async () => {
      if (!leaderForm.email) {
          alert("Please select a partner from the dropdown.");
          return;
      }

      const action = leaderForm.regionCode ? `assign ${leaderForm.regionCode.toUpperCase()} to` : `REMOVE leadership from`;
      if (!window.confirm(`Are you sure you want to ${action} ${leaderForm.email}?`)) return;

      setIsAssigningLeader(true);
      try {
          // Send the command to your new Headless backend endpoint
          const res = await authFetch(`https://innovative-rebirth-production.up.railway.app/api/xvalley/assign-leadership`, {
              method: 'POST',
              body: JSON.stringify({
                  partnerEmail: leaderForm.email,
                  roleType: leaderForm.role,
                  regionCode: leaderForm.regionCode
              })
          });

          if (!res.ok) throw new Error("Failed to update partner role.");
          
          alert(`Success! Partner leadership role updated.`);
          setLeaderForm({ email: '', role: 'country', regionCode: '' });
          
          // Reload the page so the Live Network Engine re-downloads the fresh Supabase data
          window.location.reload();
      } catch (err) {
          alert(err.message);
      } finally {
          setIsAssigningLeader(false);
      }
  };
  // --- END LEADERSHIP STATE ---

  // 🚨 UNIFIED ADMIN PAYOUT PROCESSOR (Approvals & Declines + Emails)
  const processAdminPayout = async (payoutId, action) => {
      let reason = null;
      if (action === 'Declined') {
          reason = window.prompt("Are you sure you want to decline this payout? (Optional: Enter a reason for the email)");
          if (reason === null) return; // Admin clicked cancel
      } else if (action === 'Dismissed') {
          if (!window.confirm("Dismiss this record from the queue? No email will be sent and no wallet balance changes will be made.")) return;
          reason = 'Dismissed by Admin (Already Paid / Superseded)';
      } else {
          if (!window.confirm("Have you manually sent these funds? This will mark the transaction complete and send an email.")) return;
      }

      setIsUpdatingStatus(true);
      try {
          // Route ALL actions through the secure backend so emails fire reliably
          const res = await authFetch(`https://innovative-rebirth-production.up.railway.app/api/xvalley/mark-payout`, {
              method: 'POST',
              body: JSON.stringify({ payoutId, action, reason })
          });

          if (!res.ok) {
              const data = await res.json();
              throw new Error(data.error || "Server failed to process payout.");
          }

          // Optimistic UI Update
          setAllWithdrawals(prev => prev.map(w => w.id === payoutId ? { ...w, status: action } : w));
          alert(action === 'Dismissed' ? 'Payout dismissed silently (no email or wallet balance change).' : `Success! Payout marked as ${action} and email sent.`);
      } catch (err) {
          console.error("Payout Error:", err);
          alert("Failed to process status: " + err.message);
      } finally {
          setIsUpdatingStatus(false);
      }
  };

  // 🚨 UI Helper for the new color-coded statuses
  const getStatusColor = (status) => {
      switch (status) {
          case 'Pending Network':
          case 'Pending':
              return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
          case 'Failed':
              return 'bg-red-500/10 text-red-400 border-red-500/20';
          case 'Declined': // 🚨 Added Declined color mapping
              return 'bg-red-900/30 text-red-400 border-red-500/30';
          case 'Dismissed':
              return 'bg-slate-800/80 text-slate-500 border-slate-700/60 line-through';
          case 'Completed':
          case 'Paid':
              return 'bg-green-500/10 text-green-400 border-green-500/20';
          case 'Manual Review Required':
              return 'bg-orange-500/10 text-orange-400 border-orange-500/20';
          default:
              return 'bg-slate-800 text-slate-400 border-slate-700';
      }
  };

  // 1. Fetch the network and live financial ledgers (Now wired to Server-Side Search & Alt Revenue Ledger Fix!)
  useEffect(() => {
      const fetchNetworkAndFinances = async () => {
          try {
              // A. Fetch Users (Future-Proofed with 3000-Row Auto-Pagination)
              // We removed the server-side search so the network tree stays perfectly intact.
              let allUsers = [];
              let fetchMore = true;
              let step = 0;
              const CHUNK_SIZE = 1000; // 🚨 SUPABASE FIX: Must be 1000 to trigger the loop safely!

              while (fetchMore) {
                  const { data: usersChunk, error: userError } = await supabase
                      .from('user_broker_accounts')
                      .select('id, account_id, name, first_name, last_name, user_id, email, referral_code, referred_by, country_leader_for, regional_manager_for, commission_config')
                      .order('created_at', { ascending: true }) // 🚨 Required to stop users from falling between pages
                      .range(step * CHUNK_SIZE, (step + 1) * CHUNK_SIZE - 1);

                  if (userError) throw userError;
                  
                  allUsers = [...allUsers, ...usersChunk];
                  
                  // If the chunk we got is smaller than 3000, we've hit the end of the database.
                  if (usersChunk.length < CHUNK_SIZE) {
                      fetchMore = false;
                  }
                  step++;
              }
              const users = allUsers;

              // B. Fetch Aggregated Earnings (via new SQL View)
              const { data: wallets, error: walletError } = await supabase
                  .from('partner_earnings_summary')
                  .select('*')
                  .limit(100000);
              if (walletError) throw walletError;

              // C. Fetch Aggregated Withdrawals (via new SQL View)
              const { data: withdrawSummary, error: withdrawSumError } = await supabase
                  .from('partner_withdrawals_summary')
                  .select('*')
                  .limit(100000);
              if (withdrawSumError) console.error("Withdrawal Summary Error:", withdrawSumError);

              // C2. Fetch raw withdrawals for the Admin Payout Ledger View
              const { data: withdrawals, error: withdrawError } = await supabase
                  .from('withdrawals_ledger')
                  .select('id, partner_id, amount, status, method, destination, created_at, reference_id')
                  .order('created_at', { ascending: false })
                  .limit(100000);
              
              if (withdrawals) setAllWithdrawals(withdrawals);

              // D. Fetch Network Volume (via new SQL View)
              const { data: volumeData, error: volError } = await supabase
                  .from('partner_volume_summary')
                  .select('*')
                  .limit(100000);
              if (volError) console.error("Volume Error:", volError);

              // F. Fetch True Wallet Balances (Via partner_wallet_summary with fallback to wallet_ledger)
              const trueBalances = {};
              try {
                  const { data: summaryData, error: summaryErr } = await supabase
                      .from('partner_wallet_summary')
                      .select('partner_id, available_balance')
                      .limit(10000);
                  
                  if (!summaryErr && summaryData && summaryData.length > 0) {
                      summaryData.forEach(row => {
                          const email = (row.partner_id || '').toLowerCase().trim();
                          trueBalances[email] = parseFloat(row.available_balance || 0);
                      });
                  } else {
                      throw new Error('partner_wallet_summary not available');
                  }
              } catch (_balFallback) {
                  // Fallback: Paginated wallet_ledger sum
                  let allWalletLedger = [];
                  let fromL = 0;
                  const stepL = 1000;
                  while (true) {
                      const { data: chunk, error: chunkErr } = await supabase
                          .from('wallet_ledger')
                          .select('partner_id, amount')
                          .range(fromL, fromL + stepL - 1);
                      if (chunkErr) {
                          console.error("Error fetching wallet ledger chunks:", chunkErr);
                          break;
                      }
                      if (chunk && chunk.length > 0) {
                          allWalletLedger.push(...chunk);
                          if (chunk.length < stepL) break;
                          fromL += stepL;
                      } else {
                          break;
                      }
                  }
                  allWalletLedger.forEach(tx => {
                      const email = (tx.partner_id || '').toLowerCase().trim();
                      trueBalances[email] = (trueBalances[email] || 0) + parseFloat(tx.amount || 0);
                  });
              }
              // E. Fetch Individual Client Metrics (Volume, Deposits, & Rebates) with Range Pagination
              let allClientMetrics = [];
              let fromM = 0;
              const stepM = 1000;
              while (true) {
                  const { data: chunk, error: metricsError } = await supabase
                      .from('client_metrics')
                      .select('email, total_volume, total_deposits, fx_volume, cfd_volume, rebate_generated')
                      .range(fromM, fromM + stepM - 1);
                  if (metricsError) {
                      console.error("Client Metrics Error:", metricsError);
                      break;
                  }
                  if (chunk && chunk.length > 0) {
                      allClientMetrics.push(...chunk);
                      if (chunk.length < stepM) break;
                      fromM += stepM;
                  } else {
                      break;
                  }
              }
              const clientMetrics = allClientMetrics;

              // 🚨 NEW F. Fetch Defily Tree Nodes for Infinite Master Hierarchy
              try {
                  let allDefilyNodes = [];
                  let from = 0;
                  const step = 1000;
                  let hasMore = true;

                  while (hasMore) {
                      const { data: chunk, error: chunkError } = await supabase
                          .from('defily_tree_nodes')
                          .select('*')
                          .range(from, from + step - 1);
                      
                      if (chunkError) {
                          console.error("Error fetching defily nodes chunk:", chunkError);
                          break;
                      }
                      
                      if (chunk && chunk.length > 0) {
                          allDefilyNodes = [...allDefilyNodes, ...chunk];
                          if (chunk.length < step) {
                              hasMore = false;
                          } else {
                              from += step;
                          }
                      } else {
                          hasMore = false;
                      }
                  }

                  if (allDefilyNodes.length > 0) {
                      setDefilyNodes(allDefilyNodes);
                  }
              } catch (e) {
                  console.error("Failed to load defily nodes", e);
              }

              // Create a quick lookup map for the clients
              const newClientMetricsMap = {};
              if (clientMetrics) {
                  clientMetrics.forEach(m => {
                      newClientMetricsMap[(m.email || '').toLowerCase().trim()] = {
                          volume: Number(m.total_volume || 0),
                          deposits: Number(m.total_deposits || 0),
                          fx: Number(m.fx_volume || 0),
                          cfd: Number(m.cfd_volume || 0),
                          rebate_generated: Number(m.rebate_generated || 0) 
                      };
                  });
              }
              setClientMetricsMap(newClientMetricsMap);

              // Aggregate Finances
              const financeMap = {};
              
              if (wallets) {
                  wallets.forEach(w => {
                      const email = (w.partner_id || '').toLowerCase().trim();
                      if (!financeMap[email]) financeMap[email] = { earned: 0, withdrawn: 0, pending: 0, volume: 0, challenge: 0, membership: 0, pamm: 0 };
                      
                      financeMap[email].earned = Number(w.total_earned || 0);
                      financeMap[email].challenge = Number(w.total_challenge || 0);
                      financeMap[email].membership = Number(w.total_membership || 0);
                      financeMap[email].pamm = Number(w.total_pamm || 0);
                  });
              }

              if (withdrawSummary) {
                  withdrawSummary.forEach(w => {
                      const email = (w.partner_id || '').toLowerCase().trim();
                      if (!financeMap[email]) financeMap[email] = { earned: 0, withdrawn: 0, pending: 0, volume: 0, challenge: 0, membership: 0, pamm: 0 };
                      
                      financeMap[email].withdrawn = Number(w.total_withdrawn || 0);
                      financeMap[email].pending = Number(w.total_pending || 0);
                  });
              }

              if (volumeData) {
                  volumeData.forEach(v => {
                      const email = (v.partner_id || '').toLowerCase().trim();
                      if (!financeMap[email]) financeMap[email] = { earned: 0, withdrawn: 0, pending: 0, volume: 0, challenge: 0, membership: 0, pamm: 0 };
                      financeMap[email].volume = Number(v.total_volume || 0);
                  });
              }
              
              // Merge everything into the User Map
              if (users) {
                  const uniqueUsersMap = new Map();
                  
                  users.forEach(user => {
                      const strictEmail = String(user.email || '').toLowerCase().trim();
                      const authUuid = String(user.user_id || '').toLowerCase().trim();
                      const dbId = String(user.id || '').toLowerCase().trim();
                      
                      const mapKey = strictEmail || authUuid; 
                      if (!mapKey) return; 
                      
                      const existingUser = uniqueUsersMap.get(mapKey);
                      
                      const keysToCheck = [...new Set([authUuid, strictEmail, dbId])].filter(Boolean);
                      
                      let totalEarned = 0;
                      let totalWithdrawn = 0;
                      let totalPending = 0;
                      let totalNetworkVolume = 0;
                      let totalChallengeCommissions = 0;
                      let totalMembershipCommissions = 0;
                      let totalPammCommissions = 0; // 🚨 NEW

                      keysToCheck.forEach(k => {
                          if (financeMap[k]) {
                              totalEarned += financeMap[k].earned || 0;
                              totalWithdrawn += financeMap[k].withdrawn || 0;
                              totalPending += financeMap[k].pending || 0;
                              totalNetworkVolume += financeMap[k].volume || 0;
                              totalChallengeCommissions += financeMap[k].challenge || 0;
                              totalMembershipCommissions += financeMap[k].membership || 0;
                              totalPammCommissions += financeMap[k].pamm || 0; // 🚨 NEW
                              
                              // Reset the map correctly
                              financeMap[k] = { earned: 0, withdrawn: 0, pending: 0, volume: 0, challenge: 0, membership: 0, pamm: 0 }; 
                          }
                      });

                                            let totalTrueBalance = 0;
                      let hasTrueBalance = false;
                      keysToCheck.forEach(k => {
                          if (trueBalances[k] !== undefined) {
                              totalTrueBalance += trueBalances[k];
                              hasTrueBalance = true;
                          }
                      });
                      const walletBalance = hasTrueBalance ? (totalTrueBalance - totalPending) : (totalEarned - totalWithdrawn - totalPending);
                      
                      if (existingUser) {
                          uniqueUsersMap.set(mapKey, {
                              ...existingUser,
                              name: existingUser.name || user.name,
                              referral_code: existingUser.referral_code || user.referral_code,
                              // 🚨 THE FIX: Force the referred_by code to stick even if the other profile is blank
                              referred_by: existingUser.referred_by || user.referred_by || '', 
                              country_leader_for: existingUser.country_leader_for || user.country_leader_for, 
                              regional_manager_for: existingUser.regional_manager_for || user.regional_manager_for,
                              lifetime_earnings: (existingUser.lifetime_earnings || 0) + totalEarned,
                              wallet_balance: (existingUser.wallet_balance || 0) + walletBalance,
                              pending_payouts: (existingUser.pending_payouts || 0) + totalPending,
                              network_volume: (existingUser.network_volume || 0) + totalNetworkVolume,
                              challenge_earnings: (existingUser.challenge_earnings || 0) + totalChallengeCommissions,
                              membership_earnings: (existingUser.membership_earnings || 0) + totalMembershipCommissions,
                              pamm_earnings: (existingUser.pamm_earnings || 0) + totalPammCommissions
                          });
                      } else {
                          const metrics = newClientMetricsMap[strictEmail] || newClientMetricsMap[authUuid] || { volume: 0, deposits: 0, fx: 0, cfd: 0 };

                          uniqueUsersMap.set(mapKey, { 
                              ...user, 
                              actual_email: strictEmail,
                              auth_uuid: authUuid, 
                              normalized_email: strictEmail || authUuid,
                              country_leader_for: user.country_leader_for, 
                              regional_manager_for: user.regional_manager_for, 
                              commission_config: user.commission_config,
                              lifetime_earnings: totalEarned,
                              wallet_balance: walletBalance,
                              pending_payouts: totalPending,
                              network_volume: totalNetworkVolume,
                              client_volume: metrics.volume,
                              client_deposits: metrics.deposits,
                              client_fx: metrics.fx,
                              client_cfd: metrics.cfd,
                              client_rebate: metrics.rebate_generated,
                              challenge_earnings: totalChallengeCommissions,
                              membership_earnings: totalMembershipCommissions
                          });
                      }
                  });
                  
                  setRawNetworkData(Array.from(uniqueUsersMap.values()));
              }
          } catch (err) {
              console.error("Error fetching network & finances:", err);
          }
      };
      fetchNetworkAndFinances();
  }, [debouncedSearch]);

  // 2. Build the 3-Tier Tree instantly when a partner is clicked
  useEffect(() => {
      if (selectedIB) {
          const pCode = String(selectedIB.referral_code || '').toLowerCase().trim();
          const pEmail = String(selectedIB.actual_email || selectedIB.email || '').toLowerCase().trim();

          const isMasterIB = selectedIB.commission_config?.type === 'defily_master' || selectedIB.commission_config?.type === 'master_rate';
          const isDefilyUser = pCode && !isNaN(pCode) && Number.isInteger(parseFloat(pCode));

          let t1 = [], t2 = [], t3 = [], t4 = [], t5 = [], t6 = [], t7 = [], t8 = [], master = [];

          // 🚨 IF THEY ARE A DEFILY USER OR MASTER IB: Build the infinite Web3 Tree!
          if ((isMasterIB || isDefilyUser) && defilyNodes && defilyNodes.length > 0) {
              // Find their NFT ID
              let myDefilyNode = defilyNodes.find(n => n.email && n.email.toLowerCase().trim() === pEmail);
              if (!myDefilyNode && pCode && !isNaN(pCode)) {
                  myDefilyNode = defilyNodes.find(n => String(n.nft_id) === String(pCode));
              }
              
              if (myDefilyNode && myDefilyNode.nft_id !== undefined && myDefilyNode.nft_id !== null) {
                  const myNftId = myDefilyNode.nft_id;

                  // 🚨 THE DISCOVERY: DeFily explicitly pre-calculates the exact tiers and master assignments!
                  const t1Defily = defilyNodes.filter(n => n.sponsor1 === myNftId);
                  const t2Defily = defilyNodes.filter(n => n.sponsor2 === myNftId);
                  const t3Defily = defilyNodes.filter(n => n.sponsor3 === myNftId);
                  const t4Defily = defilyNodes.filter(n => n.sponsor4 === myNftId);
                  const t5Defily = defilyNodes.filter(n => n.sponsor5 === myNftId);
                  const t6Defily = defilyNodes.filter(n => n.sponsor6 === myNftId);
                  const t7Defily = defilyNodes.filter(n => n.sponsor7 === myNftId);
                  const t8Defily = defilyNodes.filter(n => n.sponsor8 === myNftId);
                  const masterDefily = defilyNodes.filter(n => n.master_ib === myNftId && n.nft_id !== myNftId);

                  const mapDefilyToUI = (defilyArray) => {
                      return defilyArray.map(dNode => {
                          // Try to find CRM data if they registered
                          const crmUser = rawNetworkData.find(u => 
                              (u.actual_email && dNode.email && u.actual_email.toLowerCase() === dNode.email.toLowerCase()) || 
                              String(u.referral_code) === String(dNode.nft_id)
                          ) || {};

                          const email = crmUser.actual_email || crmUser.email || crmUser.normalized_email || dNode.email || 'Unregistered Web3 Node';
                          const name = crmUser.name || crmUser.first_name || `Unregistered NFT #${dNode.nft_id}`;
                          const emailKey = String(email).toLowerCase().trim();
                          const metrics = clientMetricsMap[emailKey] || { deposits: 0 };
                          const pammAlloc = livePammMap[emailKey]?.totalAllocationAmount || 0;
                          const pammEquity = livePammMap[emailKey]?.totalLiveEquity || 0;
                          const pammPct = livePammMap[emailKey]?.allocationPercentage || 0;
                          const grossDeposit = metrics.deposits || crmUser.client_deposits || 0;
                          const unallocatedDeposit = Math.max(0, grossDeposit - pammAlloc);
                          
                          return {
                              id: dNode.nft_id,
                              name: name,
                              email: email,
                              commission_config: crmUser.commission_config || null,
                              status: (crmUser.client_volume || 0) > 0 ? "Active" : "Inactive",
                              kyc: (crmUser.kyc_approved === true || String(crmUser.kyc_status).toLowerCase() === 'approved') ? "Approved" : (['declined', 'rejected'].includes(String(crmUser.kyc_status).toLowerCase()) ? "Declined" : "Pending"),
                              deposit: unallocatedDeposit,
                              gross_deposit: grossDeposit,
                              pamm_deposit: pammAlloc,
                              pamm_live_equity: pammEquity,
                              pamm_alloc_pct: pammPct,
                              pamm_strategies: livePammMap[emailKey]?.strategies || [],
                              lots: crmUser.client_volume || 0,
                              fx_lots: crmUser.client_fx || 0,
                              cfd_lots: crmUser.client_cfd || 0,
                              rebate_generated: crmUser.client_rebate || 0,
                              referralCode: String(dNode.nft_id),
                              referredBy: String(dNode.sponsor1),
                              subPartners: []
                          };
                      });
                  };

                  t1 = mapDefilyToUI(t1Defily);
                  t2 = mapDefilyToUI(t2Defily);
                  t3 = mapDefilyToUI(t3Defily);
                  t4 = mapDefilyToUI(t4Defily);
                  t5 = mapDefilyToUI(t5Defily);
                  t6 = mapDefilyToUI(t6Defily);
                  t7 = mapDefilyToUI(t7Defily);
                  t8 = mapDefilyToUI(t8Defily);
                  master = mapDefilyToUI(masterDefily);
              }
          } 
          
          // 🌐 FALLBACK: If standard IB OR Web3 tree failed to find them
          if (t1.length === 0 && t2.length === 0 && t3.length === 0) {
              const isMatch = (refBy) => {
                  if (!refBy) return false;
                  const val = String(refBy).toLowerCase().trim();
                  return (val === pCode && pCode !== '') || (val === pEmail && pEmail !== '');
              };

              t1 = rawNetworkData.filter(u => isMatch(u.referred_by));
              const t1Codes = t1.map(u => String(u.referral_code || '').toLowerCase().trim()).filter(Boolean);
              const t1Emails = t1.map(u => String(u.actual_email || u.email || '').toLowerCase().trim()).filter(Boolean);

              t2 = rawNetworkData.filter(u => {
                  const refBy = String(u.referred_by || '').toLowerCase().trim();
                  return t1Codes.includes(refBy) || t1Emails.includes(refBy);
              });
              const t2Codes = t2.map(u => String(u.referral_code || '').toLowerCase().trim()).filter(Boolean);
              const t2Emails = t2.map(u => String(u.actual_email || u.email || '').toLowerCase().trim()).filter(Boolean);

              t3 = rawNetworkData.filter(u => {
                  const refBy = String(u.referred_by || '').toLowerCase().trim();
                  return t2Codes.includes(refBy) || t2Emails.includes(refBy);
              });
          }

          const mapToUI = (u, tierId) => {
            const emailKey = String(u.normalized_email || u.actual_email || u.email || '').toLowerCase().trim();
            const metrics = clientMetricsMap[emailKey] || { deposits: 0 };
            const pammAlloc = livePammMap[emailKey]?.totalAllocationAmount || 0;
            const pammEquity = livePammMap[emailKey]?.totalLiveEquity || 0;
            const pammPct = livePammMap[emailKey]?.allocationPercentage || 0;
            const grossDeposit = metrics.deposits || u.client_deposits || 0;
            const unallocatedDeposit = Math.max(0, grossDeposit - pammAlloc);
            const totalVol = u.client_volume || 0;
            const fxVol = u.client_fx || 0;
            const trueRebateFromDatabase = u.client_rebate || 0;
            const safeName = u.name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.normalized_email?.split('@')[0] || 'Unknown';

            return {
              id: u.id,
              name: safeName,
              email: u.normalized_email || u.actual_email || u.email,
              commission_config: u.commission_config, 
              status: (u.client_volume || 0) > 0 ? "Active" : "Inactive",
              kyc: "Approved", 
              lots: totalVol, 
              deposit: unallocatedDeposit,
              gross_deposit: grossDeposit,
              pamm_deposit: pammAlloc,
              pamm_live_equity: pammEquity,
              pamm_alloc_pct: pammPct,
              pamm_strategies: livePammMap[emailKey]?.strategies || [], 
              fx_lots: fxVol,
              cfd_lots: u.client_cfd || 0,
              rebate_generated: trueRebateFromDatabase
            };
        };

        // 🚨 PREVENT EMPTY MASTER TABS: If they are a Master IB but have no deep downline, show their T1-3 as their Master group
        if (isMasterIB && master.length === 0) {
            master = [...t1, ...t2, ...t3];
        }

        setLiveTree({ 
            t1: t1.map(u => mapToUI(u, 't1')), 
            t2: t2.map(u => mapToUI(u, 't2')), 
            t3: t3.map(u => mapToUI(u, 't3')),
            t4: (typeof t4 !== 'undefined' ? t4 : []).map(u => mapToUI(u, 't4')),
            t5: (typeof t5 !== 'undefined' ? t5 : []).map(u => mapToUI(u, 't5')),
            t6: (typeof t6 !== 'undefined' ? t6 : []).map(u => mapToUI(u, 't6')),
            t7: (typeof t7 !== 'undefined' ? t7 : []).map(u => mapToUI(u, 't7')),
            t8: (typeof t8 !== 'undefined' ? t8 : []).map(u => mapToUI(u, 't8')),
            master: master.map(u => u.subPartners ? u : mapToUI(u, 'master')) 
        });
      }
  }, [selectedIB, rawNetworkData, defilyNodes, livePammMap]);

  // --- HEADLESS LEDGER FETCH (RESTORED & EXPANDED) ---
  useEffect(() => {
      const fetchLedger = async () => {
          try {
              // 1. Fetch Standard Trading & Override Commissions with pagination
              let allWalletData = [];
              let fromW = 0;
              const stepW = 1000;
              while (true) {
                  const { data, error: walletError } = await supabase
                      .from('wallet_ledger')
                      .select('id, created_at, partner_id, amount, description, transaction_type')
                      .order('created_at', { ascending: false })
                      .range(fromW, fromW + stepW - 1);

                  if (walletError) {
                      console.error("Wallet ledger fetch error:", walletError);
                      break;
                  }
                  if (!data || data.length === 0) break;
                  allWalletData.push(...data);
                  if (data.length < stepW) break;
                  fromW += stepW;
              }

              // 2. Fetch Affiliate Sales (Challenges & Memberships)
              const { data: salesData, error: salesError } = await supabase
                  .from('sales_ledger')
                  .select('partner_id, commission_earned, product_type, created_at')
                  .limit(100000);

              if (salesError) console.error("Sales Ledger Error:", salesError);

              let combinedLedger = [...allWalletData];

              // 3. Map the Sales Ledger into distinct entries!
              if (salesData) {
                  const mappedSales = salesData.map((sale, index) => {
                      const isChallenge = String(sale.product_type).toLowerCase() === 'challenge';
                      return {
                          id: `sale-${index}`,
                          created_at: sale.created_at || new Date().toISOString(),
                          partner_id: sale.partner_id,
                          description: isChallenge ? 'Challenge Account Purchase Referral' : 'Pro/Elite Membership Subscription Referral',
                          amount: sale.commission_earned,
                          transaction_type: isChallenge ? 'challenge_bonus' : 'membership_bonus'
                      };
                  });
                  
                  combinedLedger = [...combinedLedger, ...mappedSales];
              }

              // 4. Re-sort the newly combined ledger chronologically
              combinedLedger.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

              if (combinedLedger.length > 0) {
                  setLedgerData(combinedLedger);
                  // Strict Floor Math to prevent overpaying
                  const total = combinedLedger.reduce((sum, item) => sum + (Math.floor(Number(item.amount || 0) * 100) / 100), 0);
                  setTotalUnpaid(total);
              }
          } catch (err) {
              console.error("🔴 Error fetching Supabase ledger:", err);
          } finally {
              setIsLoadingLedger(false);
          }
      };
      fetchLedger();
  }, []);

  // --- FETCH SELECTED IB FULL LEDGER ---
  useEffect(() => {
      const fetchIBLedger = async () => {
          if (!selectedIB) {
              setSelectedIBLedger([]);
              return;
          }
          setIsLoadingIBLedger(true);
          try {
              const pid1 = selectedIB.email || '';
              const pid2 = selectedIB.auth_uuid || '';
              
              let allWalletData = [];
              let fromW = 0;
              const stepW = 1000;
              while (true) {
                  const { data, error } = await supabase
                      .from('wallet_ledger')
                      .select('*')
                      .or(`partner_id.eq.${pid1},partner_id.eq.${pid2}`)
                      .order('created_at', { ascending: false })
                      .range(fromW, fromW + stepW - 1);
                  if (error) {
                      console.error("Error fetching IB wallet ledger:", error);
                      break;
                  }
                  if (data && data.length > 0) {
                      allWalletData.push(...data);
                      if (data.length < stepW) break;
                      fromW += stepW;
                  } else {
                      break;
                  }
              }
              const wData = allWalletData;
                  
              const { data: withData } = await supabase
                  .from('withdrawals_ledger')
                  .select('*')
                  .or(`partner_id.eq.${pid1},partner_id.eq.${pid2}`)
                  .order('created_at', { ascending: false });

              const { data: sData } = await supabase
                  .from('sales_ledger')
                  .select('partner_id, commission_earned, product_type, created_at')
                  .or(`partner_id.eq.${pid1},partner_id.eq.${pid2}`);

              let combined = [...(wData || [])];
              if (sData) {
                  const mappedSales = sData.map((sale, index) => {
                      const isChallenge = String(sale.product_type).toLowerCase() === 'challenge';
                      return {
                          id: `sale-${index}`,
                          created_at: sale.created_at || new Date().toISOString(),
                          partner_id: sale.partner_id,
                          description: isChallenge ? 'Challenge Account Purchase Referral' : 'Pro/Elite Membership Subscription Referral',
                          amount: sale.commission_earned,
                          transaction_type: isChallenge ? 'challenge_bonus' : 'membership_bonus'
                      };
                  });
                  combined = [...combined, ...mappedSales];
              }
              combined.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
              setSelectedIBLedger(combined);
              setSelectedIBWithdrawals(withData || []);
          } catch (err) {
              console.error("Error fetching IB ledger:", err);
          } finally {
              setIsLoadingIBLedger(false);
          }
      };
      fetchIBLedger();
  }, [selectedIB]);

  // --- LEDGER FILTERING, PAGINATION & EXPORT ---
  const getFilteredLedger = () => {
      const now = new Date();
      return ledgerData.filter(entry => {
          // 1. Filter by Ledger View (Earnings vs Withdrawals)
          const isWithdrawal = entry.transaction_type === 'withdrawal' || Number(entry.amount) < 0;
          if (ledgerView === 'earnings' && isWithdrawal) return false;
          if (ledgerView === 'withdrawals' && !isWithdrawal) return false;

          // 2. Filter by Timeframe
          if (ledgerTimeframe === 'all') return true;
          
          const entryDate = new Date(entry.created_at);
          
          // 🚨 NEW: Custom Date Range Logic
          if (ledgerTimeframe === 'custom') {
              const sDate = customStartDate ? new Date(customStartDate).getTime() : 0;
              // Set End Date to the very last millisecond of the day so it includes all trades on that day
              const eDate = customEndDate ? new Date(customEndDate).setHours(23,59,59,999) : Infinity;
              return entryDate.getTime() >= sDate && entryDate.getTime() <= eDate;
          }

          const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
          const entryTime = entryDate.getTime();

          switch (ledgerTimeframe) {
              case 'today': return entryTime >= todayStart;
              case 'week': return entryTime >= (now.getTime() - 7 * 86400000);
              case 'month': return entryTime >= (now.getTime() - 30 * 86400000);
              case 'quarter': return entryTime >= (now.getTime() - 90 * 86400000);
              case 'year': return entryTime >= (now.getTime() - 365 * 86400000);
              default: return true;
          }
      });
  };

  const filteredLedger = getFilteredLedger();
  const totalLedgerPages = Math.ceil(filteredLedger.length / LEDGER_PAGE_SIZE) || 1;
  const paginatedLedger = filteredLedger.slice((ledgerPage - 1) * LEDGER_PAGE_SIZE, ledgerPage * LEDGER_PAGE_SIZE);

  const exportToCSV = () => {
      const headers = ['Date', 'Partner Email', 'Description', 'Amount', 'Type'];
      const rows = filteredLedger.map(entry => [
          new Date(entry.created_at).toLocaleDateString(),
          entry.partner_id,
          `"${entry.description || ''}"`, // Quotes prevent commas in description from breaking CSV
          (Math.floor(Number(entry.amount || 0) * 100) / 100).toFixed(2), // Strict Round Down
          entry.transaction_type
      ]);
      
      const csvContent = [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `commission_ledger_${ledgerTimeframe}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
  };

  const exportPammLogsToCSV = () => {
      const headers = ['Date Run', 'Strategy Name', 'Start Date', 'End Date', 'Total Lots Traded'];
      const rows = pammLogs.map(log => [
          new Date(log.created_at).toLocaleDateString(),
          `"${log.strategy_name || ''}"`,
          log.start_date,
          log.end_date,
          Number(log.total_lots).toFixed(2)
      ]);
      
      const csvContent = [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', 'pamm_payout_history.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
  };
  // --- END HEADLESS STATE ---

  // --- DYNAMIC MAIN LIST GENERATOR ---
  const liveIBs = rawNetworkData
      .map(user => {
          // 🚨 THE FIX: Bulletproof, multi-identifier client counting
          const partnerCode = String(user.referral_code || '').toLowerCase().trim();
          const partnerEmail = String(user.actual_email || user.email || '').toLowerCase().trim();
          
          const directClients = rawNetworkData.filter(u => {
              const refBy = String(u.referred_by || '').toLowerCase().trim();
              if (!refBy) return false;
              return (refBy === partnerCode && partnerCode !== '') || 
                     (refBy === partnerEmail && partnerEmail !== '');
          });

          // 🚨 THE FIX: Safely construct the name using the new schema columns
          const safeName = user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.normalized_email?.split('@')[0] || 'Unknown';

          return {
              id: user.user_id || user.id,
              name: safeName,
              email: user.normalized_email,
              referral_code: user.referral_code,
              country_leader_for: user.country_leader_for,
              regional_manager_for: user.regional_manager_for,
              commission_config: user.commission_config, // 🚨 THE FIX: Pass the config to the UI so it doesn't default!
              level: "Partner",
              clients: directClients.length,
              lifetime_earnings: user.lifetime_earnings || 0,
              wallet_balance: user.wallet_balance || 0,
              pending_payouts: user.pending_payouts || 0,
              network_volume: user.network_volume || 0, 
              rebate: `$${(user.lifetime_earnings || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}`,
              challenge_earnings: user.challenge_earnings || 0,
              membership_earnings: user.membership_earnings || 0
          };
      })
      // 🚨 THE FIX: Strictly filter out users with 0 clients, unless they are a designated Leader or actively being searched
      .filter(ib => {
          if (debouncedSearch) return true; // Always show search results
          return ib.clients > 0 || ib.country_leader_for || ib.regional_manager_for;
      });

  // Filter Logic (Now connected to live data)
  const filteredIBs = liveIBs.filter(ib => {
      const matchesSearch = !debouncedSearch || 
                            ib.name.toLowerCase().includes(debouncedSearch.toLowerCase()) || 
                            ib.id.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
                            ib.email.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
                            (ib.referral_code && ib.referral_code.toLowerCase().includes(debouncedSearch.toLowerCase()));
      const matchesClients = ib.clients >= filters.minClients;
      return matchesSearch && matchesClients;
  });

  const applyFilters = () => setShowFilterModal(false);
  const clearFilters = () => { setFilters({ minClients: 0, minRebate: 0 }); setShowFilterModal(false); };

  // ==========================================
  // --- LIVE HERO STATS CALCULATIONS ---
  // ==========================================
  const totalPartners = liveIBs.length;
  const totalReferrals = liveIBs.reduce((sum, ib) => sum + (ib.clients || 0), 0);
  
  // 🚨 THE TRUE VOLUME FIX: We must calculate the total from the raw 'client_volume' to prevent multi-tier duplication.
  const monthlyVolume = rawNetworkData.reduce((sum, u) => sum + (Number(u.client_volume) || 0), 0);

  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  const completedWithdrawals = (allWithdrawals || []).filter(w => w.status === 'Completed' || w.status === 'Paid');
  const payouts30d = completedWithdrawals
      .filter(w => new Date(w.created_at) >= thirtyDaysAgo)
      .reduce((sum, w) => sum + Number(w.amount || 0), 0); 

  const twentyFourHoursAgo = new Date();
  twentyFourHoursAgo.setHours(twentyFourHoursAgo.getHours() - 24);
  const payouts24h = completedWithdrawals
      .filter(w => new Date(w.created_at) >= twentyFourHoursAgo)
      .reduce((sum, w) => sum + Number(w.amount || 0), 0); 

  const formatCompact = (num) => {
      if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
      if (num >= 1000) return (num / 1000).toFixed(1) + 'k';
      return num.toLocaleString(undefined, { maximumFractionDigits: 1 });
  };
  // ==========================================

  // --- RENDER: IB DETAIL VIEW ---
  // Define it here so modal can access it!
  // 🚨 CRITICAL FIX: Do NOT subtract pending withdrawals; the debit is already in wallet_ledger
  const computedWalletBalance = selectedIBLedger.reduce((sum, t) => sum + Number(t.amount || 0), 0);

  const renderIBDetail = () => {
      // 🚨 DYNAMIC TABS: Support up to 8 tiers for Defily
      const isDefily = selectedIB?.commission_config?.type?.includes('defily');
      const isMasterIB = selectedIB?.commission_config?.type === 'defily_master' || selectedIB?.commission_config?.type === 'master_rate';
      
      let dynamicTabs = [];
      if (isDefily) {
          for (let i = 1; i <= 8; i++) {
              dynamicTabs.push({ id: `t${i}`, label: `Tier ${i}`, desc: `Level ${i}`, color: 'text-white' });
          }
      } else {
          dynamicTabs = [...STANDARD_COMMISSIONS];
      }

      if (isMasterIB) {
          dynamicTabs.push({ id: 'master', label: 'Master (Infinity)', desc: 'Infinite Master Tree', color: 'text-amber-500' });
      }

      // Connect the UI tabs directly to our Live Supabase Engine
      let activeClientsList = [];
      if (clientTierTab === 'master') {
          activeClientsList = liveTree.master;
      } else {
          activeClientsList = liveTree[clientTierTab] || liveTree.t1;
      }

      // Fixed: Replaced synchronous filter of global ledgerData with targeted selectedIBLedger
      const ibLedger = selectedIBLedger;
      const totalCountryBonus = ibLedger.filter(t => t.transaction_type === 'country_bonus').reduce((sum, t) => sum + Number(t.amount || 0), 0);
      const totalRegionalBonus = ibLedger.filter(t => t.transaction_type === 'regional_bonus').reduce((sum, t) => sum + Number(t.amount || 0), 0);

      const currentVolume = selectedIB.network_volume || 0;
      const currentTier = PERFORMANCE_BONUS_TIERS.find(t => currentVolume >= t.threshold) || PERFORMANCE_BONUS_TIERS[3];
      const nextTier = PERFORMANCE_BONUS_TIERS.find(t => t.threshold > currentVolume);
      const progressToNext = nextTier ? ((currentVolume - currentTier.threshold) / (nextTier.threshold - currentTier.threshold)) * 100 : 100;

      // ==========================================
      // 🚨 NEW: CALCULATE EXACT REVENUE BREAKDOWN
      // ==========================================
      const totalRev = ibLedger.filter(t => Number(t.amount) > 0).reduce((sum, t) => sum + Number(t.amount || 0), 0);
      const challengeRev = ibLedger.filter(t => t.transaction_type === 'challenge_bonus').reduce((sum, t) => sum + Number(t.amount || 0), 0);
      const membershipRev = ibLedger.filter(t => t.transaction_type === 'membership_bonus').reduce((sum, t) => sum + Number(t.amount || 0), 0);
      const pammRev = ibLedger.filter(t => t.transaction_type === 'pamm_commission').reduce((sum, t) => sum + Number(t.amount || 0), 0);
      const leadershipRev = totalCountryBonus + totalRegionalBonus;
      
      // Standard trading rebates are whatever is left over from the total
      const tradingRev = Math.max(0, totalRev - challengeRev - membershipRev - pammRev - leadershipRev); 
      
      const getPct = (val) => totalRev > 0 ? ((val / totalRev) * 100) : 0;
      // ==========================================

      return (
          <div className="animate-fade-in space-y-6">
              <div className="flex items-center justify-between">
                  <button onClick={() => { setSelectedIB(null); setViewMode('list'); }} className="text-slate-400 hover:text-white flex items-center gap-2 text-sm font-bold"><ArrowLeft size={16} /> Back to Network List</button>
                  <div className="flex gap-2">
                      <button 
                          onClick={() => setShowManualBonusModal(true)}
                          className="bg-purple-600 hover:bg-purple-500 text-white px-4 py-2 rounded-lg text-sm font-bold transition-colors flex items-center gap-2"
                      >
                          <Plus size={16} /> Add Bonus
                      </button>
                      <button className="bg-slate-800 text-white px-4 py-2 rounded-lg text-sm font-bold border border-slate-700 hover:bg-slate-700 transition-colors">Statement</button>
                      <button 
                          onClick={() => { 
                              // Apply strict floor rounding and cap it to 2 decimal string
                              const strictRoundedBalance = Math.floor(Number(computedWalletBalance || 0) * 100) / 100;
                              setPayoutForm({...payoutForm, amount: strictRoundedBalance.toFixed(2)}); 
                              setShowPayoutModal(true); 
                          }} 
                          disabled={computedWalletBalance <= 0}
                          className="bg-amber-500 disabled:opacity-50 disabled:cursor-not-allowed text-slate-900 px-4 py-2 rounded-lg text-sm font-bold hover:bg-amber-400 transition-colors"
                      >
                          Process Payout
                      </button>
                  </div>
              </div>

              {/* IB Profile Header */}
              <div className="bg-slate-900 p-6 rounded-2xl border border-slate-800 flex justify-between items-center">
                  <div className="flex items-center gap-4">
                      <div className="w-16 h-16 bg-amber-500/10 rounded-xl flex items-center justify-center text-amber-500 border border-amber-500/20"><Network size={32} /></div>
                      <div>
                          <h2 className="text-2xl font-bold text-white">{selectedIB.name}</h2>
                          <div className="flex flex-wrap items-center gap-2 mt-1">
                              <span className="text-xs font-mono text-slate-400 bg-slate-950 px-2 py-1 rounded border border-slate-800" title="Supabase UUID">ID: {selectedIB.id}</span>
                              <span className="text-xs font-mono text-slate-400 bg-slate-950 px-2 py-1 rounded border border-slate-800" title="Email Address">Email: {selectedIB.email}</span>
                              <span className="text-xs text-blue-400 font-bold bg-blue-900/20 px-2 py-1 rounded border border-blue-500/20">{selectedIB.level}</span>
                          </div>
                      </div>
                  </div>
                  <div className="text-right">
                      <p className="text-xs text-slate-500 uppercase font-bold">Wallet Balance</p>
                      {/* 👇 LIVE WALLET BALANCE 👇 */}
                      <p className="text-3xl font-bold text-white">${computedWalletBalance.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                  </div>
              </div>

              {/* Dashboard Stats */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
                  {/* 👇 ADDED ( || 0 ) FALLBACKS TO EVERYTHING 👇 */}
                  <StatCard 
                      title="Total Revenue" 
                      value={`$${(totalRev || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}`} 
                      subtext="Lifetime earnings" 
                      trend="Live" 
                      trendUp={true} 
                      icon={DollarSign} 
                  />
                  <StatCard 
                      title="Alt Revenue" 
                      value={`$${((selectedIB.challenge_earnings || 0) + (selectedIB.membership_earnings || 0)).toLocaleString(undefined, {minimumFractionDigits: 2})}`} 
                      subtext={`C: $${selectedIB.challenge_earnings || 0} | M: $${selectedIB.membership_earnings || 0}`} 
                      trend="Live" 
                      trendUp={true} 
                      icon={Layers} 
                  />
                  <StatCard 
                      title="Active Clients" 
                      value={selectedIB.clients || 0} 
                      subtext="Live accounts" 
                      trend="Live" 
                      trendUp={true} 
                      icon={Users} 
                  />
                  <StatCard 
                      title="Network Volume" 
                      value={(selectedIB.network_volume || 0).toLocaleString(undefined, {minimumFractionDigits: 2})} 
                      subtext="Lots (Current Month)" 
                      trend="Live" 
                      trendUp={true} 
                      icon={Activity} 
                  />
                  <StatCard 
                      title="Pending Payouts" 
                      value={`$${(selectedIB.pending_payouts || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}`} 
                      subtext="In processing" 
                      trend="Live" 
                      trendUp={true} 
                      icon={Wallet} 
                  />
              </div>

              {/* Commission & Client Data Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  {/* LEFT: Commission & Bonus Structures */}
                  <div className="space-y-6">

                      {/* --- NEW: COMPREHENSIVE REVENUE BREAKDOWN --- */}
                      <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6 shadow-sm">
                          <h3 className="font-bold text-white mb-5 flex items-center gap-2"><Activity size={18} className="text-blue-500"/> Detailed Revenue Breakdown</h3>
                          <div className="space-y-5">
                              {/* 1. Trading Rebates */}
                              <div>
                                  <div className="flex justify-between text-xs mb-1.5">
                                      <span className="text-slate-400 font-bold flex items-center gap-1.5"><TrendingUp size={12} className="text-blue-400"/> Trading Rebates (FX/CFD)</span>
                                      <span className="text-white font-mono">${tradingRev.toLocaleString(undefined, {minimumFractionDigits: 2})} <span className="text-slate-500 ml-1">({getPct(tradingRev).toFixed(1)}%)</span></span>
                                  </div>
                                  <div className="h-1.5 w-full bg-slate-950 rounded-full overflow-hidden">
                                      <div className="h-full bg-blue-500 rounded-full transition-all duration-1000" style={{ width: `${getPct(tradingRev)}%` }}></div>
                                  </div>
                              </div>
                              {/* 1.5 PAMM Profit Splits */}
                              <div>
                                  <div className="flex justify-between text-xs mb-1.5">
                                      <span className="text-slate-400 font-bold flex items-center gap-1.5"><Briefcase size={12} className="text-indigo-400"/> PAMM Profit Splits</span>
                                      <span className="text-white font-mono">${pammRev.toLocaleString(undefined, {minimumFractionDigits: 2})} <span className="text-slate-500 ml-1">({getPct(pammRev).toFixed(1)}%)</span></span>
                                  </div>
                                  <div className="h-1.5 w-full bg-slate-950 rounded-full overflow-hidden">
                                      <div className="h-full bg-indigo-500 rounded-full transition-all duration-1000" style={{ width: `${getPct(pammRev)}%` }}></div>
                                  </div>
                              </div>
                              {/* 2. Challenge Sales */}
                              <div>
                                  <div className="flex justify-between text-xs mb-1.5">
                                      <span className="text-slate-400 font-bold flex items-center gap-1.5"><Award size={12} className="text-purple-400"/> Challenge Accounts</span>
                                      <span className="text-white font-mono">${challengeRev.toLocaleString(undefined, {minimumFractionDigits: 2})} <span className="text-slate-500 ml-1">({getPct(challengeRev).toFixed(1)}%)</span></span>
                                  </div>
                                  <div className="h-1.5 w-full bg-slate-950 rounded-full overflow-hidden">
                                      <div className="h-full bg-purple-500 rounded-full transition-all duration-1000" style={{ width: `${getPct(challengeRev)}%` }}></div>
                                  </div>
                              </div>
                              {/* 3. Membership Subscriptions */}
                              <div>
                                  <div className="flex justify-between text-xs mb-1.5">
                                      <span className="text-slate-400 font-bold flex items-center gap-1.5"><Layers size={12} className="text-amber-400"/> Memberships (Recurring)</span>
                                      <span className="text-white font-mono">${membershipRev.toLocaleString(undefined, {minimumFractionDigits: 2})} <span className="text-slate-500 ml-1">({getPct(membershipRev).toFixed(1)}%)</span></span>
                                  </div>
                                  <div className="h-1.5 w-full bg-slate-950 rounded-full overflow-hidden">
                                      <div className="h-full bg-amber-500 rounded-full transition-all duration-1000" style={{ width: `${getPct(membershipRev)}%` }}></div>
                                  </div>
                              </div>
                              {/* 4. Leadership Overrides */}
                              <div>
                                  <div className="flex justify-between text-xs mb-1.5">
                                      <span className="text-slate-400 font-bold flex items-center gap-1.5"><Network size={12} className="text-emerald-400"/> Leadership Overrides</span>
                                      <span className="text-white font-mono">${leadershipRev.toLocaleString(undefined, {minimumFractionDigits: 2})} <span className="text-slate-500 ml-1">({getPct(leadershipRev).toFixed(1)}%)</span></span>
                                  </div>
                                  <div className="h-1.5 w-full bg-slate-950 rounded-full overflow-hidden">
                                      <div className="h-full bg-emerald-500 rounded-full transition-all duration-1000" style={{ width: `${getPct(leadershipRev)}%` }}></div>
                                  </div>
                              </div>
                          </div>
                      </div>
                      {/* ------------------------------------------- */}

                      <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6">
                          <h3 className="font-bold text-white mb-4 flex items-center gap-2"><Settings size={18} className="text-slate-400"/> Partner Commission Plan</h3>
                          
                          <select 
                              value={commissionConfig.type} 
                              onChange={(e) => setCommissionConfig({ ...commissionConfig, type: e.target.value, rate_fx: commissionConfig.rate_fx || 0, rate_non_fx: commissionConfig.rate_non_fx || 0 })}
                              className="w-full bg-slate-950 border border-slate-700 text-white p-3 rounded-lg mb-4 focus:border-amber-500 outline-none"
                          >
                              <option value="multi_tier">Standard 3-Tier Multi-Level</option>
                              <option value="flat_rate">Independent Flat Rate</option>
                              <option value="master_rate">Master Franchise Contract (Owns Network)</option>
                              <option value="sub_ib_allocation">Sub-IB Allocation (Under a Master)</option>
                              <option disabled>──────────</option>
                              <option value="defily_scale">Defily: Dynamic Volume Scale ($5 - $10)</option>
                              <option value="defily_sub_ib">Defily: Fixed Sub-IB Cut</option>
                              <option value="defily_master">Defily: Master IB Rank (Pool Override)</option>
                          </select>

                          {commissionConfig.type === 'defily_scale' ? (
                              <div className="space-y-2">
                                  <p className="text-[10px] text-slate-500 uppercase font-bold mb-2">Active Dynamic Configuration</p>
                                  <div className="p-2 rounded border bg-blue-900/20 border-blue-500/50 flex justify-between items-center">
                                      <div><span className="text-xs font-bold block text-white">Tier 1 (Direct)</span></div>
                                      <div className="text-right"><span className="text-xs text-slate-400 font-mono">Scale: $5.00 - $10.00</span></div>
                                  </div>
                                  <div className="p-2 rounded border bg-slate-950 border-slate-800 flex justify-between items-center">
                                      <div><span className="text-xs font-bold block text-slate-300">Tier 2 (L2 Sponsor)</span></div>
                                      <div className="text-right"><span className="text-xs text-slate-400 font-mono">Flat: $2.00</span></div>
                                  </div>
                                  <div className="p-2 rounded border bg-slate-950 border-slate-800 flex justify-between items-center">
                                      <div><span className="text-xs font-bold block text-slate-500">Tier 3 (L3 Sponsor)</span></div>
                                      <div className="text-right"><span className="text-xs text-slate-400 font-mono">Flat: $1.00</span></div>
                                  </div>
                                  <p className="text-[10px] text-amber-500/70 mt-4 leading-snug">
                                      The Tier 1 rate dynamically scales from $5 to $10 automatically based on the partner's total monthly volume.
                                  </p>
                              </div>
                          ) : commissionConfig.type === 'defily_sub_ib' ? (
                              <div className="bg-amber-500/10 border border-amber-500/30 p-4 rounded-lg">
                                  <label className="text-xs text-amber-500 font-bold uppercase block mb-4">Fixed Sub-IB Cut ($ / Lot)</label>
                                  <input 
                                      type="number" 
                                      value={commissionConfig.rate_fx || 0} 
                                      onChange={(e) => setCommissionConfig({ ...commissionConfig, rate_fx: Number(e.target.value) })}
                                      className="w-full bg-slate-950 text-amber-500 font-bold p-3 rounded-lg border border-amber-500/50 text-xl outline-none" 
                                  />
                                  <p className="text-[10px] text-amber-500/70 mt-4 leading-snug">
                                      This partner receives a fixed payout and does not participate in the $5-$10 scaling system. The remaining funds from the $15 pool roll up to the Defily Master.
                                  </p>
                              </div>
                          ) : commissionConfig.type === 'defily_master' ? (
                              <div className="bg-purple-500/10 border border-purple-500/30 p-4 rounded-lg">
                                  <label className="text-xs text-purple-400 font-bold uppercase block mb-2">Master IB Override Active</label>
                                  <p className="text-[10px] text-purple-300/70 leading-snug">
                                      This partner holds the Master IB Rank. They will automatically sweep the remainder of the $15/lot rebate pool after all Level 1, Level 2, and Level 3 commissions are paid out in their downline.
                                  </p>
                              </div>
                          ) : commissionConfig.type === 'multi_tier' ? (
                              <div className="space-y-2">
                                  <p className="text-[10px] text-slate-500 uppercase font-bold mb-2">Active Standard Configuration</p>
                                  {STANDARD_COMMISSIONS.map((tier) => (
                                      <div key={tier.id} className={`p-2 rounded border flex justify-between items-center ${clientTierTab === tier.id ? 'bg-blue-900/20 border-blue-500/50' : 'bg-slate-950 border-slate-800'}`}>
                                          <div><span className={`text-xs font-bold block ${tier.color}`}>{tier.label}</span></div>
                                          <div className="text-right"><span className="text-xs text-slate-400 font-mono">FX: ${tier.fx} | XAU: ${tier.metals}</span></div>
                                      </div>
                                  ))}
                              </div>
                          ) : (
                              <div className="bg-amber-500/10 border border-amber-500/30 p-4 rounded-lg">
                                  <label className="text-xs text-amber-500 font-bold uppercase block mb-4">
                                      {commissionConfig.type === 'master_rate' ? 'Total Master Payout' : 
                                       commissionConfig.type === 'sub_ib_allocation' ? 'Allocated Sub-IB Cut' : 
                                       'Independent Flat Rate Payout'}
                                  </label>
                                  <div className="grid grid-cols-2 gap-4">
                                      <div>
                                          <span className="text-[10px] text-amber-500/70 font-bold uppercase block mb-1">FX ($ / Lot)</span>
                                          <input 
                                              type="number" 
                                              value={commissionConfig.rate_fx || 0} 
                                              onChange={(e) => setCommissionConfig({ ...commissionConfig, rate_fx: Number(e.target.value) })}
                                              className="w-full bg-slate-950 text-amber-500 font-bold p-3 rounded-lg border border-amber-500/50 text-xl outline-none" 
                                          />
                                      </div>
                                      <div>
                                          <span className="text-[10px] text-amber-500/70 font-bold uppercase block mb-1">Non-FX ($ / Lot)</span>
                                          <input 
                                              type="number" 
                                              value={commissionConfig.rate_non_fx || 0} 
                                              onChange={(e) => setCommissionConfig({ ...commissionConfig, rate_non_fx: Number(e.target.value) })}
                                              className="w-full bg-slate-950 text-amber-500 font-bold p-3 rounded-lg border border-amber-500/50 text-xl outline-none" 
                                          />
                                      </div>
                                  </div>
                                  {commissionConfig.type === 'master_rate' && (
                                      <p className="text-[10px] text-amber-500/70 mt-4 leading-snug">
                                          This partner receives these rates for ALL volume under them, minus any cuts assigned to their sub-IBs.
                                      </p>
                                  )}
                                  {commissionConfig.type === 'sub_ib_allocation' && (
                                      <p className="text-[10px] text-amber-500/70 mt-4 leading-snug">
                                          This partner receives a fixed cut. The remainder of the upline contract goes to their Master IB.
                                      </p>
                                  )}
                              </div>
                          )}
                          
                          <button 
                              onClick={handleSaveCommission}
                              disabled={isSavingCommission}
                              className="w-full mt-4 py-2 bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold rounded-lg border border-slate-700 transition-colors disabled:opacity-50"
                          >
                              {isSavingCommission ? 'Saving...' : 'Save Structure to Database'}
                          </button>
                      </div>
                      <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6">
                          <div className="flex justify-between items-end mb-4"><h3 className="font-bold text-white flex items-center gap-2"><Award size={18} className="text-amber-500"/> Monthly Bonus</h3><span className="text-xs font-bold text-white bg-slate-800 px-2 py-0.5 rounded border border-slate-700">Level: {currentTier.label}</span></div>
                          <div className="relative h-2 bg-slate-950 rounded-full overflow-hidden mb-2"><div className="absolute top-0 left-0 h-full bg-amber-500 transition-all duration-1000" style={{ width: `${progressToNext}%` }}></div></div><div className="flex justify-between text-[10px] text-slate-500 mb-6"><span>{currentVolume.toLocaleString()} Lots</span><span>Target: {nextTier ? nextTier.threshold.toLocaleString() : 'Max'}</span></div>
                          <div className="space-y-2">{PERFORMANCE_BONUS_TIERS.map((tier, i) => (<div key={i} className={`flex justify-between items-center text-xs p-2 rounded ${currentVolume >= tier.threshold ? 'bg-slate-800 text-white' : 'text-slate-500'}`}><div className="flex items-center gap-2"><div className={`w-1.5 h-1.5 rounded-full ${tier.color}`}></div><span>&gt; {tier.threshold} Lots</span></div><span className="font-bold">{tier.label}</span></div>))}</div>
                      </div>

                      {(selectedIB.country_leader_for || selectedIB.regional_manager_for || totalCountryBonus > 0 || totalRegionalBonus > 0) && (
                          <LeadershipOverridesCard 
                              countryBonusTotal={totalCountryBonus} 
                              regionalBonusTotal={totalRegionalBonus}
                              countryCode={selectedIB.country_leader_for}
                              regionCode={selectedIB.regional_manager_for} 
                          />
                      )}

                      {/* --- NEW: PARTNER PAYOUT LEDGER (UPGRADED) --- */}
                      <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6 flex flex-col h-[500px]">
                          <div className="flex justify-between items-center mb-4">
                              <h3 className="font-bold text-white flex items-center gap-2">
                                  <Wallet size={18} className="text-green-500"/> Payout Ledger
                              </h3>
                              <div className="flex gap-2">
                                  <select value={partnerLedgerStatus} onChange={(e) => { setPartnerLedgerStatus(e.target.value); setPartnerLedgerPage(1); }} className="bg-slate-950 border border-slate-700 text-slate-300 text-[10px] font-bold rounded px-2 py-1 outline-none">
                                      <option value="all">All Status</option>
                                      <option value="pending">Pending</option>
                                      <option value="completed">Completed</option>
                                  </select>
                                  <select value={partnerLedgerTimeframe} onChange={(e) => { setPartnerLedgerTimeframe(e.target.value); setPartnerLedgerPage(1); }} className="bg-slate-950 border border-slate-700 text-slate-300 text-[10px] font-bold rounded px-2 py-1 outline-none">
                                      <option value="all">All Time</option>
                                      <option value="month">30 Days</option>
                                      <option value="year">1 Year</option>
                                  </select>
                              </div>
                          </div>

                          <div className="overflow-y-auto pr-2 flex-1 no-scrollbar">
                              {(() => {
                                  let withdrawals = [...selectedIBWithdrawals];
                                  withdrawals = withdrawals.filter(w => {
                                      const pid = (w.partner_id || '').toLowerCase().trim();
                                      return pid === selectedIB.email || pid === selectedIB.auth_uuid;
                                  });
                                  
                                  // 2. Filter by Time
                                  const now = new Date();
                                  withdrawals = withdrawals.filter(w => {
                                      if (partnerLedgerTimeframe === 'all') return true;
                                      const diffDays = Math.ceil(Math.abs(now - new Date(w.created_at)) / (1000 * 60 * 60 * 24));
                                      if (partnerLedgerTimeframe === 'month') return diffDays <= 30;
                                      if (partnerLedgerTimeframe === 'year') return diffDays <= 365;
                                      return true;
                                  });

                                  // 3. Filter by Status
                                  withdrawals = withdrawals.filter(w => {
                                      if (partnerLedgerStatus === 'all') return true;
                                      const isCompleted = w.status === 'Completed' || w.status === 'Paid';
                                      if (partnerLedgerStatus === 'completed') return isCompleted;
                                      if (partnerLedgerStatus === 'pending') return !isCompleted;
                                      return true;
                                  });

                                  // 4. Pagination
                                  const PAGE_SIZE = 5;
                                  const totalPages = Math.ceil(withdrawals.length / PAGE_SIZE) || 1;
                                  const paginated = withdrawals.slice((partnerLedgerPage - 1) * PAGE_SIZE, partnerLedgerPage * PAGE_SIZE);

                                  if (withdrawals.length === 0) {
                                      return <p className="text-xs text-slate-500 italic text-center py-8">No payout history matches your filters.</p>;
                                  }

                                  return (
                                      <div className="space-y-3 flex flex-col h-full justify-between">
                                          <div className="space-y-3">
                                              {paginated.map((w, idx) => (
                                                  <div key={idx} className="bg-slate-950 p-3 rounded-lg border border-slate-800 hover:border-slate-700 transition-colors">
                                                      <div className="flex justify-between items-center mb-2">
                                                          <span className="text-[10px] text-slate-400 font-mono">{new Date(w.created_at).toLocaleDateString()} {new Date(w.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                                                          <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded border ${
                                                              w.status === 'Completed' || w.status === 'Paid' 
                                                              ? 'bg-green-500/10 text-green-400 border-green-500/20' 
                                                              : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                                                          }`}>
                                                              {w.status || 'Pending'}
                                                          </span>
                                                      </div>
                                                      <div className="flex justify-between items-end">
                                                          <div>
                                                              <p className="text-xs font-bold text-white">{w.method}</p>
                                                              <p className="text-[10px] text-slate-500 truncate max-w-[120px]" title={w.destination}>{w.destination || 'No destination'}</p>
                                                          </div>
                                                          <p className="text-sm font-bold text-white">
                                                              ${(Math.floor(Number(w.amount || 0) * 100) / 100).toLocaleString(undefined, {minimumFractionDigits: 2})}
                                                          </p>
                                                      </div>
                                                  </div>
                                              ))}
                                          </div>
                                          {totalPages > 1 && (
                                              <div className="flex justify-between items-center pt-2 mt-4 border-t border-slate-800 pb-2">
                                                  <button onClick={() => setPartnerLedgerPage(p => Math.max(1, p - 1))} disabled={partnerLedgerPage === 1} className="text-[10px] font-bold text-slate-400 hover:text-white disabled:opacity-30">Prev</button>
                                                  <span className="text-[10px] text-slate-500">{partnerLedgerPage} / {totalPages}</span>
                                                  <button onClick={() => setPartnerLedgerPage(p => Math.min(totalPages, p + 1))} disabled={partnerLedgerPage === totalPages} className="text-[10px] font-bold text-slate-400 hover:text-white disabled:opacity-30">Next</button>
                                              </div>
                                          )}
                                      </div>
                                  );
                              })()}
                          </div>
                      </div>

                      {/* --- NEW: PARTNER WALLET LEDGER --- */}
                      <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6 flex flex-col h-[500px]">
                          <div className="flex justify-between items-center mb-4">
                              <h3 className="font-bold text-white flex items-center gap-2">
                                  <Activity size={18} className="text-blue-500"/> Wallet Ledger
                              </h3>
                          </div>

                          <div className="overflow-y-auto pr-2 flex-1 no-scrollbar">
                              {(() => {
                                  // 1. All entries from wallet ledger
                                  let entries = [...selectedIBLedger];
                                  
                                  // 2. Pagination
                                  const PAGE_SIZE = 5;
                                  const totalPages = Math.ceil(entries.length / PAGE_SIZE) || 1;
                                  const paginated = entries.slice((partnerWalletLedgerPage - 1) * PAGE_SIZE, partnerWalletLedgerPage * PAGE_SIZE);

                                  if (entries.length === 0) {
                                      return <p className="text-xs text-slate-500 italic text-center py-8">No wallet activity found.</p>;
                                  }

                                  return (
                                      <div className="space-y-3 flex flex-col h-full justify-between">
                                          <div className="space-y-3">
                                              {paginated.map((entry, idx) => (
                                                  <div key={idx} className="bg-slate-950 p-3 rounded-lg border border-slate-800 hover:border-slate-700 transition-colors">
                                                      <div className="flex justify-between items-center mb-2">
                                                          <span className="text-[10px] text-slate-400 font-mono">{new Date(entry.created_at).toLocaleDateString()} {new Date(entry.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                                                          <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded border ${
                                                              Number(entry.amount) < 0 
                                                              ? 'bg-red-500/10 text-red-400 border-red-500/20' 
                                                              : 'bg-green-500/10 text-green-400 border-green-500/20'
                                                          }`}>
                                                              {entry.transaction_type?.replace('_', ' ') || 'transfer'}
                                                          </span>
                                                      </div>
                                                      <div className="flex justify-between items-end">
                                                          <div className="pr-4">
                                                              <p className="text-[10px] text-slate-400 break-words" title={entry.description}>{entry.description || 'No description'}</p>
                                                          </div>
                                                          <p className={`text-sm font-bold whitespace-nowrap ${Number(entry.amount) < 0 ? 'text-red-400' : 'text-green-400'}`}>
                                                              {Number(entry.amount) < 0 ? '-' : '+'}${(Math.abs(Math.floor(Number(entry.amount || 0) * 100) / 100)).toLocaleString(undefined, {minimumFractionDigits: 2})}
                                                          </p>
                                                      </div>
                                                  </div>
                                              ))}
                                          </div>
                                          {totalPages > 1 && (
                                              <div className="flex justify-between items-center pt-2 mt-4 border-t border-slate-800 pb-2">
                                                  <button onClick={() => setPartnerWalletLedgerPage(p => Math.max(1, p - 1))} disabled={partnerWalletLedgerPage === 1} className="text-[10px] font-bold text-slate-400 hover:text-white disabled:opacity-30">Prev</button>
                                                  <span className="text-[10px] text-slate-500">{partnerWalletLedgerPage} / {totalPages}</span>
                                                  <button onClick={() => setPartnerWalletLedgerPage(p => Math.min(totalPages, p + 1))} disabled={partnerWalletLedgerPage === totalPages} className="text-[10px] font-bold text-slate-400 hover:text-white disabled:opacity-30">Next</button>
                                              </div>
                                          )}
                                      </div>
                                  );
                              })()}
                          </div>
                      </div>
                  </div>

                  {/* RIGHT: Client List (3-TIER TABS) */}
                  <div className="lg:col-span-2 bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden flex flex-col self-start">
                      <div className="p-6 border-b border-slate-800 overflow-x-auto no-scrollbar">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4">
                <div className="flex items-center gap-4">
                    <h3 className="font-bold text-white">Referred Clients</h3>
                    <div className="flex bg-slate-950 p-1 rounded-lg border border-slate-800">
                        <button 
                            onClick={() => setIsPammMode(false)}
                            className={`px-3 py-1 rounded-md text-xs font-bold transition-all ${!isPammMode ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-400 hover:text-slate-200'}`}
                        >
                            FX/CFD Trading
                        </button>
                        <button 
                            onClick={() => setIsPammMode(true)}
                            className={`px-3 py-1 rounded-md text-xs font-bold transition-all ${isPammMode ? 'bg-purple-600 text-white shadow-sm' : 'text-slate-400 hover:text-slate-200'}`}
                        >
                            PAMM Activity
                        </button>
                    </div>
                    
                    <button 
                        onClick={async () => {
                            try {
                                const fxClients = [];
                                ['master', 't1', 't2', 't3', 't4', 't5', 't6', 't7', 't8'].forEach(tier => {
                                    if (liveTree[tier]) {
                                        liveTree[tier].forEach(client => {
                                            fxClients.push({ ...client, tierLabel: tier.toUpperCase() });
                                        });
                                    }
                                });

                                const pEmail = selectedIB.actual_email || selectedIB.email;
                                let pammClients = [];
                                try {
                                    const res = await fetch(`https://innovative-rebirth-production.up.railway.app/api/commissions/pamm/ledger/${encodeURIComponent(pEmail)}?runId=all`);
                                    if (res.ok) {
                                        const data = await res.json();
                                        pammClients = (data.ledger || []).map(p => {
                                            const match = fxClients.find(fx => (fx.email || '').toLowerCase() === (p.client_email || p.ClientID || p.client_id || '').toLowerCase());
                                            return { ...p, tierLabel: match ? match.tierLabel : 'Direct' };
                                        }).sort((a, b) => {
                                            const tA = a.tierLabel || 'Direct';
                                            const tB = b.tierLabel || 'Direct';
                                            if (tA < tB) return -1;
                                            if (tA > tB) return 1;
                                            const dA = new Date(a.created_at || 0);
                                            const dB = new Date(b.created_at || 0);
                                            return dB - dA;
                                        });
                                    }
                                } catch (e) { console.error(e); }

                                const summaryStats = {
                                    activeClients: (selectedIB.clients || 0),
                                    networkVolume: (selectedIB.network_volume || 0),
                                    totalEarnings: (selectedIB.lifetime_earnings || 0),
                                    pammDeposits: 0
                                };

                                generatePartnerPDFReport({ ...selectedIB, wallet_balance: computedWalletBalance }, summaryStats, fxClients, pammClients);
                            } catch (error) {
                                console.error('Failed to generate PDF', error);
                                alert('Failed to generate PDF');
                            }
                        }}
                        className="px-3 py-1 bg-red-600/20 text-red-400 hover:text-red-300 rounded-md text-xs font-bold transition-all ml-4 border border-red-500/30 flex items-center gap-2"
                    >
                        <Download size={12} />
                        View Full Report
                    </button>
                </div>
                {isPammMode ? (
                    <select 
                        value={selectedRunId} 
                        onChange={(e) => setSelectedRunId(e.target.value)}
                        className="bg-slate-950 text-white text-xs font-bold border border-slate-700 rounded-lg px-3 py-2 outline-none focus:border-purple-500 transition-colors"
                    >
                        <option value="all">All Time PAMM</option>
                        {pammRuns.map(run => (
                            <option key={run.RunID || run.id} value={run.RunID || run.id}>
                                {new Date(run.DateRangeStart || run.start_date).toLocaleDateString()} - {new Date(run.DateRangeEnd || run.end_date).toLocaleDateString()}
                            </option>
                        ))}
                    </select>
                ) : (
                    <button className="text-xs text-blue-400 hover:text-white transition-colors">View Full Report</button>
                )}
            </div>
            {/* Scrollable Pill Navigation for Tiers */}
            <div className="flex space-x-2 bg-slate-950 p-1 rounded-lg w-full max-w-full overflow-x-auto no-scrollbar border border-slate-800">
                {dynamicTabs.map(t => (
                    <button 
                        key={t.id} 
                        onClick={() => setClientTierTab(t.id)} 
                        className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all whitespace-nowrap ${clientTierTab === t.id ? (t.id === 'master' ? 'bg-amber-500/20 text-amber-500 shadow-sm border border-amber-500/30' : 'bg-slate-800 text-white shadow-sm') : 'text-slate-500 hover:text-slate-300'}`}
                    >
                        {t.id === 'master' ? '👑 ' : ''}{t.label}
                    </button>
                ))}
            </div>

            {/* --- NEW: TIER PERFORMANCE STATS (ACROSS ALL PAGES OF CURRENT TIER) --- */}
            {(() => {
                const tierClients = activeClientsList || [];
                const tierClientEmails = new Set(
                    tierClients.map(c => String(c.email || '').toLowerCase().trim()).filter(Boolean)
                );
                const activeTierLabel = dynamicTabs.find(t => t.id === clientTierTab)?.label || 'Tier';
                const nowMs = Date.now();
                const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;

                if (isPammMode) {
                    // === PAMM MODE ===
                    // A. Total PAMM Deposits in this tier across all clients
                    const tierTotalPammDeposits = tierClients.reduce((sum, client) => {
                        const cEmail = String(client.email || '').toLowerCase().trim();
                        const pDataRaw = (pammLedger || []).find(p => {
                            const pEmail = String(p.client_email || p.ClientID || p.client_id || '').toLowerCase().trim();
                            return pEmail === cEmail;
                        });
                        const dep = pDataRaw?.pamm_deposit ?? pDataRaw?.PammDeposit ?? pDataRaw?.allocation_amount ?? pDataRaw?.AllocationAmount ?? client.pamm_deposit ?? 0;
                        return sum + Number(dep || 0);
                    }, 0);

                    // B. Filter PAMM rows for this tier from rawLifetimePammLedger
                    const tierPammRows = (rawLifetimePammLedger || []).filter(row => {
                        const cEmail = String(row.client_email || row.ClientID || row.client_id || '').toLowerCase().trim();
                        return cEmail && tierClientEmails.has(cEmail);
                    });

                    const pammLifetimeRebates = tierPammRows.reduce((sum, r) => sum + Number(r.pamm_rebate || r.PammRebate || 0), 0);
                    const pammLifetimeProfitSplits = tierPammRows.reduce((sum, r) => sum + Number(r.profit_split || r.ProfitSplit || 0), 0);

                    const tierPammWeekRows = tierPammRows.filter(r => (nowMs - new Date(r.created_at).getTime()) <= sevenDaysMs);
                    const pammWeekRebates = tierPammWeekRows.reduce((sum, r) => sum + Number(r.pamm_rebate || r.PammRebate || 0), 0);
                    const pammWeekProfitSplits = tierPammWeekRows.reduce((sum, r) => sum + Number(r.profit_split || r.ProfitSplit || 0), 0);

                    let pammRecentRunRebates = 0;
                    let pammRecentRunProfitSplits = 0;
                    let pammRecentRunDateLabel = '';

                    let latestPammTimestamp = -1;
                    let latestPammDateStr = '';

                    tierPammRows.forEach(item => {
                        const dateVal = item.created_at || item.upload_date;
                        if (!dateVal) return;
                        const d = new Date(dateVal);
                        const time = d.getTime();
                        if (!isNaN(time) && time > latestPammTimestamp) {
                            latestPammTimestamp = time;
                            latestPammDateStr = d.toLocaleDateString();
                        }
                    });

                    if (latestPammDateStr) {
                        const latestDayPammItems = tierPammRows.filter(item => {
                            const dateVal = item.created_at || item.upload_date;
                            if (!dateVal) return false;
                            const d = new Date(dateVal);
                            return !isNaN(d.getTime()) && d.toLocaleDateString() === latestPammDateStr;
                        });

                        pammRecentRunRebates = latestDayPammItems.reduce((acc, curr) => acc + Number(curr.pamm_rebate || curr.PammRebate || 0), 0);
                        pammRecentRunProfitSplits = latestDayPammItems.reduce((acc, curr) => acc + Number(curr.profit_split || curr.ProfitSplit || 0), 0);
                        pammRecentRunDateLabel = latestPammDateStr;
                    }

                    return (
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5 mt-4 pt-4 border-t border-slate-800">
                            {/* 1. PAMM Deposits */}
                            <div className="bg-slate-950/80 p-3 rounded-xl border border-slate-800/80 hover:border-purple-500/30 transition-all flex flex-col justify-between">
                                <div className="flex items-center justify-between mb-1">
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">PAMM Deposits</span>
                                    <div className="p-1 rounded-md bg-purple-500/10 text-purple-400">
                                        <Layers size={13} />
                                    </div>
                                </div>
                                <div>
                                    <div className="text-sm sm:text-base font-bold font-mono text-purple-400">{formatCurrency(tierTotalPammDeposits)}</div>
                                    <div className="text-[10px] text-slate-500 truncate mt-0.5">{activeTierLabel} Capital</div>
                                </div>
                            </div>

                            {/* 2. Lifetime Rebates */}
                            <div className="bg-slate-950/80 p-3 rounded-xl border border-slate-800/80 hover:border-blue-500/30 transition-all flex flex-col justify-between">
                                <div className="flex items-center justify-between mb-1">
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Rebates (Lifetime)</span>
                                    <div className="p-1 rounded-md bg-blue-500/10 text-blue-400">
                                        <Award size={13} />
                                    </div>
                                </div>
                                <div>
                                    <div className="text-sm sm:text-base font-bold font-mono text-white">{formatCurrency(pammLifetimeRebates)}</div>
                                    <div className="text-[10px] text-slate-500 truncate mt-0.5">All-Time PAMM</div>
                                </div>
                            </div>

                            {/* 3. Week Rebates */}
                            <div className="bg-slate-950/80 p-3 rounded-xl border border-slate-800/80 hover:border-indigo-500/30 transition-all flex flex-col justify-between">
                                <div className="flex items-center justify-between mb-1">
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Rebates (Week)</span>
                                    <div className="p-1 rounded-md bg-indigo-500/10 text-indigo-400">
                                        <Calendar size={13} />
                                    </div>
                                </div>
                                <div>
                                    <div className="text-sm sm:text-base font-bold font-mono text-indigo-300">{formatCurrency(pammWeekRebates)}</div>
                                    <div className="text-[10px] text-slate-500 truncate mt-0.5">Past 7 Days</div>
                                </div>
                            </div>

                            {/* 4. Recent Day Rebates */}
                            <div className="bg-slate-950/80 p-3 rounded-xl border border-slate-800/80 hover:border-cyan-500/30 transition-all flex flex-col justify-between">
                                <div className="flex items-center justify-between mb-1">
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Rebates (Recent Day)</span>
                                    <div className="p-1 rounded-md bg-cyan-500/10 text-cyan-400">
                                        <Zap size={13} />
                                    </div>
                                </div>
                                <div>
                                    <div className="text-sm sm:text-base font-bold font-mono text-cyan-300">{formatCurrency(pammRecentRunRebates)}</div>
                                    <div className="text-[10px] text-slate-500 truncate mt-0.5">{pammRecentRunDateLabel ? `Date: ${pammRecentRunDateLabel}` : 'Latest Day'}</div>
                                </div>
                            </div>

                            {/* 5. Lifetime Profit Splits */}
                            <div className="bg-slate-950/80 p-3 rounded-xl border border-slate-800/80 hover:border-emerald-500/30 transition-all flex flex-col justify-between">
                                <div className="flex items-center justify-between mb-1">
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Profit Splits (All-Time)</span>
                                    <div className="p-1 rounded-md bg-emerald-500/10 text-emerald-400">
                                        <TrendingUp size={13} />
                                    </div>
                                </div>
                                <div>
                                    <div className="text-sm sm:text-base font-bold font-mono text-emerald-400">{formatCurrency(pammLifetimeProfitSplits)}</div>
                                    <div className="text-[10px] text-slate-500 truncate mt-0.5">Lifetime Perf Fee</div>
                                </div>
                            </div>

                            {/* 6. Recent Day Profit Splits */}
                            <div className="bg-slate-950/80 p-3 rounded-xl border border-slate-800/80 hover:border-amber-500/30 transition-all flex flex-col justify-between">
                                <div className="flex items-center justify-between mb-1">
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Profit Splits (Recent Day)</span>
                                    <div className="p-1 rounded-md bg-amber-500/10 text-amber-400">
                                        <Coins size={13} />
                                    </div>
                                </div>
                                <div>
                                    <div className="text-sm sm:text-base font-bold font-mono text-amber-300">{formatCurrency(pammRecentRunProfitSplits)}</div>
                                    <div className="text-[10px] text-slate-500 truncate mt-0.5">Week: {formatCurrency(pammWeekProfitSplits)}</div>
                                </div>
                            </div>
                        </div>
                    );
                } else {
                    // === FX/CFD TRADING MODE ===
                    // A. Total CFD/FX Deposits in this tier across all clients
                    const tierTotalFxDeposits = tierClients.reduce((sum, client) => {
                        return sum + Number(client.deposit || 0);
                    }, 0);

                    // B. Lifetime Rebates Generated by this tier
                    const fxLifetimeRebates = tierClients.reduce((sum, client) => {
                        return sum + Number(client.rebate_generated || 0);
                    }, 0);

                    // C. Week & Most Recent Commission Run
                    const tradingCommissions = (selectedIBLedger || []).filter(tx => 
                        tx.transaction_type === 'commission' || 
                        (tx.description && tx.description.toLowerCase().includes('ib rebates for trading volume'))
                    );

                    let allTiersLifetimeRebates = 0;
                    let allTiersVolume = 0;
                    const tierVolume = tierClients.reduce((sum, c) => sum + Number(c.lots || 0), 0);

                    ['master', 't1', 't2', 't3', 't4', 't5', 't6', 't7', 't8'].forEach(tierKey => {
                        if (liveTree[tierKey]) {
                            liveTree[tierKey].forEach(c => {
                                allTiersLifetimeRebates += Number(c.rebate_generated || 0);
                                allTiersVolume += Number(c.lots || 0);
                            });
                        }
                    });

                    let tierProportion = 1;
                    if (allTiersLifetimeRebates > 0) {
                        tierProportion = fxLifetimeRebates / allTiersLifetimeRebates;
                    } else if (allTiersVolume > 0) {
                        tierProportion = tierVolume / allTiersVolume;
                    }

                    const weekTradingCommissions = tradingCommissions
                        .filter(tx => (nowMs - new Date(tx.created_at).getTime()) <= sevenDaysMs)
                        .reduce((sum, tx) => sum + Number(tx.amount || 0), 0);

                    const fxWeekRebates = weekTradingCommissions * tierProportion;

                    // FX Most Recent Day: find latest date, then sum all runs/transactions of that date added together
                    let latestFxTimestamp = -1;
                    let latestFxDateStr = '';

                    tradingCommissions.forEach(tx => {
                        if (!tx.created_at) return;
                        const d = new Date(tx.created_at);
                        const time = d.getTime();
                        if (!isNaN(time) && time > latestFxTimestamp) {
                            latestFxTimestamp = time;
                            latestFxDateStr = d.toLocaleDateString();
                        }
                    });

                    const latestDayTradingTxs = tradingCommissions.filter(tx => {
                        if (!tx.created_at) return false;
                        const d = new Date(tx.created_at);
                        return !isNaN(d.getTime()) && d.toLocaleDateString() === latestFxDateStr;
                    });

                    const latestDayFxTotal = latestDayTradingTxs.reduce((sum, tx) => sum + Number(tx.amount || 0), 0);
                    const fxRecentRunRebates = latestDayFxTotal * tierProportion;
                    const fxRecentRunDateLabel = latestFxDateStr;

                    return (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 pt-4 border-t border-slate-800">
                            {/* 1. CFD/FX Deposits */}
                            <div className="bg-slate-950/80 p-3.5 rounded-xl border border-slate-800/80 hover:border-emerald-500/30 transition-all flex flex-col justify-between">
                                <div className="flex items-center justify-between mb-1.5">
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Total CFD/FX Deposits</span>
                                    <div className="p-1.5 rounded-md bg-emerald-500/10 text-emerald-400">
                                        <Wallet size={15} />
                                    </div>
                                </div>
                                <div>
                                    <div className="text-base font-bold font-mono text-emerald-400">{formatCurrency(tierTotalFxDeposits)}</div>
                                    <div className="text-[10px] text-slate-500 truncate mt-0.5">{activeTierLabel} ({tierClients.length} clients)</div>
                                </div>
                            </div>

                            {/* 2. Lifetime Rebates */}
                            <div className="bg-slate-950/80 p-3.5 rounded-xl border border-slate-800/80 hover:border-blue-500/30 transition-all flex flex-col justify-between">
                                <div className="flex items-center justify-between mb-1.5">
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Lifetime Rebates</span>
                                    <div className="p-1.5 rounded-md bg-blue-500/10 text-blue-400">
                                        <Award size={15} />
                                    </div>
                                </div>
                                <div>
                                    <div className="text-base font-bold font-mono text-white">{formatCurrency(fxLifetimeRebates)}</div>
                                    <div className="text-[10px] text-slate-500 truncate mt-0.5">All-Time Generated</div>
                                </div>
                            </div>

                            {/* 3. Weekly Rebates */}
                            <div className="bg-slate-950/80 p-3.5 rounded-xl border border-slate-800/80 hover:border-indigo-500/30 transition-all flex flex-col justify-between">
                                <div className="flex items-center justify-between mb-1.5">
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Weekly Rebates</span>
                                    <div className="p-1.5 rounded-md bg-indigo-500/10 text-indigo-400">
                                        <Calendar size={15} />
                                    </div>
                                </div>
                                <div>
                                    <div className="text-base font-bold font-mono text-indigo-300">{formatCurrency(fxWeekRebates)}</div>
                                    <div className="text-[10px] text-slate-500 truncate mt-0.5">Past 7 Days</div>
                                </div>
                            </div>

                            {/* 4. Most Recent Commission Run */}
                            <div className="bg-slate-950/80 p-3.5 rounded-xl border border-slate-800/80 hover:border-cyan-500/30 transition-all flex flex-col justify-between">
                                <div className="flex items-center justify-between mb-1.5">
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Most Recent Run</span>
                                    <div className="p-1.5 rounded-md bg-cyan-500/10 text-cyan-400">
                                        <Zap size={15} />
                                    </div>
                                </div>
                                <div>
                                    <div className="text-base font-bold font-mono text-cyan-300">{formatCurrency(fxRecentRunRebates)}</div>
                                    <div className="text-[10px] text-slate-500 truncate mt-0.5">{fxRecentRunDateLabel ? `Run on ${fxRecentRunDateLabel}` : 'Latest Daily Run'}</div>
                                </div>
                            </div>
                        </div>
                    );
                }
            })()}
        </div>
                      {(() => {
                          const CLIENTS_PER_PAGE = 10;
                          const totalClientPages = Math.ceil(activeClientsList.length / CLIENTS_PER_PAGE) || 1;
                          const paginatedClients = activeClientsList.slice((clientListPage - 1) * CLIENTS_PER_PAGE, clientListPage * CLIENTS_PER_PAGE);

                          return (
                              <div className="flex flex-col">
                                  <div className="overflow-x-auto">
                                      <table className="w-full text-left">
                              <thead className="bg-slate-950 text-xs font-bold text-slate-400 uppercase sticky top-0">
                                <tr>
                                    {clientTierTab === 'genesis' && (
                                        <>
                                            <th className="px-6 py-4 text-amber-500 font-mono uppercase">NFT ID</th>
                                            <th className="px-6 py-4 text-slate-500 font-mono uppercase">Sponsor ID</th>
                                        </>
                                    )}
                                    <th className="px-6 py-4">Name</th>
                                    <th className="px-6 py-4">KYC</th>
                                    {isPammMode ? (
                                        <>
                                            <th className="px-6 py-4 text-right">PAMM Deposits</th>
                                            <th className="px-6 py-4 text-right">Alloc %</th>
                                            <th className="px-6 py-4 text-right">PAMM Volume</th>
                                            <th className="px-6 py-4 text-right">Run Rebates</th>
                                            <th className="px-6 py-4 text-right">All-Time Rebates</th>
                                            <th className="px-6 py-4 text-right">Profit Split</th>
                                        </>
                                    ) : (
                                        <>
                                            <th className="px-6 py-4 text-right">Deposit</th>
                                            <th className="px-6 py-4 text-right">Live Equity</th>
                                            <th className="px-6 py-4 text-right">10X Equity</th>
                                            <th className="px-6 py-4 text-right">Volume</th>
                                            <th className="px-6 py-4 text-right">Rebate Generated</th>
                                        </>
                                    )}
                                    <th className="px-6 py-4 text-right">Status</th>
                                </tr>
                              </thead>
                             <tbody className="divide-y divide-slate-800 text-sm">
                                  {paginatedClients.map(client => {
                                      const liveData = liveFinancials[client.email] || {};
                                      const displayDeposit = client.deposit || 0;
                                      const liveEquity = liveData.equity;
                                      const liveBalance = liveData.balance;
                                      const displayStatus = liveData.status || client.status;

                                      return (
                                      <React.Fragment key={client.id}>
                                          <tr 
                                              onClick={() => setSelectedClient(client)} 
                                              className="hover:bg-slate-800/50 transition-colors cursor-pointer"
                                          >
                                              {/* Column 1: Name and Dedicated Editor Button */}
                                              <td className="px-6 py-4">
                                                  <div className="flex items-center gap-3">
                                                      <span className="font-bold text-white">{client.name}</span>
                                                      <button 
                                                          onClick={(e) => {
                                                              e.stopPropagation(); // 🚨 Strictly prevents the Profile Modal from opening
                                                              if (expandedClient === client.id) {
                                                                  setExpandedClient(null);
                                                              } else {
                                                                  setExpandedClient(client.id);
                                                                  setSubIbForm({
                                                                      rate_fx: client.commission_config?.rate_fx || 0,
                                                                      rate_non_fx: client.commission_config?.rate_non_fx || 0
                                                                  });
                                                              }
                                                          }}
                                                          title={expandedClient === client.id ? 'Close Editor' : 'Set Margin'}
                                                          className={`flex items-center justify-center p-1 rounded border transition-colors ${expandedClient === client.id ? 'bg-amber-500 text-slate-900 border-amber-500' : 'bg-slate-800 text-slate-400 border-slate-700 hover:text-amber-400 hover:border-amber-500/50'}`}
                                                      >
                                                          <Settings size={14} />
                                                      </button>
                                                      
                                                      {/* History Toggle Button */}
                                                      <button 
                                                          onClick={(e) => {
                                                              e.stopPropagation();
                                                              setExpandedHistoryClient(expandedHistoryClient?.id === client.id ? null : { id: client.id, email: client.email });
                                                          }}
                                                          className={`flex items-center justify-center p-1 rounded border transition-colors ${expandedHistoryClient?.id === client.id ? 'bg-indigo-500/20 text-indigo-400 border-indigo-500/50' : 'bg-slate-800 text-slate-400 border-slate-700 hover:text-indigo-400 hover:border-indigo-500/50'}`}
                                                          title={isPammMode ? "View PAMM Commission History" : "View FX/CFD Commission History"}
                                                      >
                                                          {expandedHistoryClient?.id === client.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                                      </button>
                                                  </div>
                                              </td>
                                              
                                              {/* Column 2: KYC */}
                                              <td className="px-6 py-4">
                                                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded border ${
                                                      client.kyc === 'Approved' ? 'bg-green-500/10 text-green-400 border-green-500/20' :
                                                      client.kyc === 'Declined' ? 'bg-red-500/10 text-red-400 border-red-500/20' :
                                                      'bg-slate-800 text-slate-400 border-slate-700'
                                                  }`}>
                                                      {client.kyc}
                                                  </span>
                                              </td>
                                              
                                               {/* Columns 3-5/7: Dynamic Data based on Mode */}
                                               {isPammMode ? (() => {
                                                   const pDataRaw = pammLedger.find(p => {
                                                       const pEmail = String(p.client_email || p.ClientID || p.client_id || '').toLowerCase().trim();
                                                       const cEmail = String(client.email || '').toLowerCase().trim();
                                                       return pEmail && cEmail && pEmail === cEmail;
                                                   });
                                                   const pData = {
                                                       pamm_deposit: pDataRaw?.pamm_deposit ?? pDataRaw?.PammDeposit ?? pDataRaw?.allocation_amount ?? pDataRaw?.AllocationAmount ?? client.pamm_deposit ?? 0,
                                                       allocation_percentage: pDataRaw?.allocation_percentage ?? pDataRaw?.AllocationPercentage ?? client.pamm_alloc_pct ?? 0,
                                                       pamm_volume: pDataRaw?.pamm_volume ?? pDataRaw?.PammVolume ?? 0,
                                                       pamm_rebate: pDataRaw?.pamm_rebate ?? pDataRaw?.PammRebate ?? 0,
                                                       profit_split: pDataRaw?.profit_split ?? pDataRaw?.ProfitSplit ?? 0
                                                   };
                                                   return (
                                                       <>
                                                           <td className="px-6 py-4 text-right">
                                                               <div className="flex flex-col items-end">
                                                                   <span className="font-mono text-white font-bold">${Number(pData.pamm_deposit).toFixed(2)}</span>
                                                                   {client.pamm_live_equity > 0 && (
                                                                       <span className="text-[10px] text-emerald-400 font-mono" title="Live Pool Equity">
                                                                           Eq: ${Number(client.pamm_live_equity).toFixed(2)}
                                                                       </span>
                                                                   )}
                                                               </div>
                                                           </td>
                                                           <td className="px-6 py-4 text-right">
                                                               <span className="font-mono text-white font-bold">
                                                                   {selectedRunId === 'all' && typeof pData.allocation_percentage === 'string'
                                                                       ? pData.allocation_percentage
                                                                       : formatAllocPct(pData.allocation_percentage)}
                                                               </span>
                                                           </td>
                                                           <td className="px-6 py-4 text-right">
                                                               <span className="font-mono text-slate-400 font-bold">{formatLots(pData.pamm_volume)}</span>
                                                           </td>
                                                           <td className="px-6 py-4 text-right text-blue-400 font-mono font-bold">
                                                               {formatCurrency(pData.pamm_rebate)}
                                                           </td>
                                                           <td className="px-6 py-4 text-right text-purple-400 font-mono font-bold">
                                                               {formatCurrency((pammLifetimeLedger[String(client.email).toLowerCase().trim()]?.pamm_rebate) || 0)}
                                                           </td>
                                                           <td className="px-6 py-4 text-right text-green-400 font-mono font-bold">
                                                               {formatCurrency(pData.profit_split)}
                                                           </td>
                                                       </>
                                                   );
                                               })() : (
                                                   <>
                                                       {/* Column 3: Historical Deposit / Live Balance */}
                                                       <td className="px-6 py-4 text-right">
                                                           <div className="flex flex-col items-end">
                                                               <span className="font-mono text-white font-bold">${Number(displayDeposit).toFixed(2)}</span>
                                                               {client.pamm_deposit > 0 && (
                                                                   <span className="text-[10px] text-purple-400 font-mono font-medium flex items-center gap-1 mt-0.5" title={`PAMM Allocated: $${Number(client.pamm_deposit).toFixed(2)}`}>
                                                                       <span className="w-1.5 h-1.5 rounded-full bg-purple-400"></span>
                                                                       +${Number(client.pamm_deposit).toFixed(2)} PAMM
                                                                   </span>
                                                               )}
                                                           </div>
                                                       </td>
                                                      {/* Column 3.5: Live Equity */}
                                                      <td className="px-6 py-4 text-right">
                                                          {liveEquity !== undefined ? (
                                                              <span className="font-mono text-blue-400 font-bold">${Number(liveEquity).toFixed(2)}</span>
                                                          ) : (
                                                              <span className="font-mono text-slate-600">-</span>
                                                          )}
                                                      </td>
                                                      
                                                      {/* Column 3.6: 10X Equity */}
                                                      <td className="px-6 py-4 text-right">
                                                          {liveData.equity10x !== undefined && liveData.equity10x > 0 ? (
                                                              <span className="font-mono text-purple-400 font-bold">${Number(liveData.equity10x).toFixed(2)}</span>
                                                          ) : (
                                                              <span className="font-mono text-slate-600">-</span>
                                                          )}
                                                      </td>
                                                      
                                                      {/* Column 4: Volume in Lots */}
                                                      <td className="px-6 py-4 text-right font-mono text-slate-400">{(client.lots || 0).toFixed(2)}</td>
                                                      
                                                      {/* Column 5: Rebate Generated */}
                                                      <td className="px-6 py-4 text-right text-green-400 font-mono font-bold">
                                                          ${(client.rebate_generated || 0).toFixed(2)}
                                                      </td>
                                                  </>
                                              )}
                                              
                                              {/* Column 6: Status */}
                                              <td className="px-6 py-4 text-right">
                                                  <StatusBadge status={displayStatus} />
                                              </td>
                                          </tr>

                                          {/* --- THE EXPANDED INLINE EDITOR --- */}
                                          {expandedClient === client.id && (
                                              <tr className="bg-slate-950 border-b-2 border-amber-500/30">
                                                  <td colSpan="6" className="p-4">
                                                      <div className="bg-slate-900 border border-slate-700 p-4 rounded-lg flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                                                          <div>
                                                              <h4 className="text-sm font-bold text-amber-500 flex items-center gap-2">
                                                                  <Settings size={14} /> Assign Sub-IB Cut
                                                              </h4>
                                                              <p className="text-[10px] text-slate-400 mt-1 max-w-sm">
                                                                  Assign a fixed payout rate to {client.name}. The remaining margin automatically rolls up to {selectedIB.name}.
                                                              </p>
                                                          </div>
                                                          <div className="flex items-end gap-4">
                                                              <div>
                                                                  <label className="text-[10px] font-bold text-slate-500 uppercase block mb-1">FX ($ / Lot)</label>
                                                                  <input 
                                                                      type="number" 
                                                                      className="bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white font-mono w-24 text-sm outline-none focus:border-amber-500" 
                                                                      value={subIbForm.rate_fx} 
                                                                      onChange={e => setSubIbForm({...subIbForm, rate_fx: Number(e.target.value)})} 
                                                                  />
                                                              </div>
                                                              <div>
                                                                  <label className="text-[10px] font-bold text-slate-500 uppercase block mb-1">Non-FX ($ / Lot)</label>
                                                                  <input 
                                                                      type="number" 
                                                                      className="bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white font-mono w-24 text-sm outline-none focus:border-amber-500" 
                                                                      value={subIbForm.rate_non_fx} 
                                                                      onChange={e => setSubIbForm({...subIbForm, rate_non_fx: Number(e.target.value)})} 
                                                                  />
                                                              </div>
                                                              <button 
                                                                  onClick={(e) => { e.stopPropagation(); handleSaveSubIbConfig(client.email); }} 
                                                                  disabled={isSavingSubIb}
                                                                  className="bg-amber-500 hover:bg-amber-400 text-slate-900 px-6 py-2 rounded font-bold text-xs disabled:opacity-50 transition-colors h-[38px]"
                                                              >
                                                                  {isSavingSubIb ? 'Saving...' : 'Save Cut'}
                                                              </button>
                                                          </div>
                                                      </div>
                                                  </td>
                                              </tr>
                                          )}

                                          {/* --- THE HISTORY SUB-TABLE --- */}
                                          {expandedHistoryClient?.id === client.id && (
                                              <tr className="bg-slate-900 border-b-2 border-indigo-500/30">
                                                  <td colSpan={isPammMode ? "8" : "7"} className="p-0">
                                                      <div className="bg-slate-950/50 p-4 inset-shadow">
                                                          {isLoadingExpandedLedger ? (
                                                              <div className="text-slate-500 text-xs italic">Loading ledger data...</div>
                                                          ) : (
                                                              <div className="space-y-6">
                                                                  {/* FX/CFD MODE: 1 Table */}
                                                                  {!isPammMode && (
                                                                      <div>
                                                                          <h4 className="text-sm font-bold text-indigo-400 mb-3 flex items-center gap-2">
                                                                              <History size={14} /> IB Trading Commissions (Earned By Client)
                                                                          </h4>
                                                                          {(() => {
                                                                              const fxHistory = expandedClientLedger.filter(r => r.transaction_type === 'commission');
                                                                              if (fxHistory.length === 0) return (
                                                                                  <div className="text-slate-500 text-xs italic bg-slate-900/50 p-4 border border-slate-800 rounded">
                                                                                      <p className="font-bold text-slate-400 mb-1">No IB rebate records found.</p>
                                                                                      <p>Note: The backend CRM does not store a per-client breakdown of FX/CFD volume commissions generated. The main ledger aggregates daily payouts per partner.</p>
                                                                                      <p className="mt-1">This table only shows IB rebates earned by this user acting as an IB.</p>
                                                                                  </div>
                                                                              );
                                                                              
                                                                              return (
                                                                                  <div className="overflow-x-auto rounded border border-slate-800">
                                                                                      <table className="w-full text-left text-xs">
                                                                                          <thead className="bg-slate-900 text-slate-400 uppercase font-bold">
                                                                                              <tr>
                                                                                                  <th className="px-4 py-2">Date</th>
                                                                                                  <th className="px-4 py-2">Description</th>
                                                                                                  <th className="px-4 py-2 text-right">Amount</th>
                                                                                              </tr>
                                                                                          </thead>
                                                                                          <tbody className="divide-y divide-slate-800">
                                                                                              {fxHistory.map((row, idx) => (
                                                                                                  <tr key={idx} className="hover:bg-slate-800/50 transition-colors">
                                                                                                      <td className="px-4 py-2 text-slate-300 font-mono">{new Date(row.created_at).toLocaleDateString()}</td>
                                                                                                      <td className="px-4 py-2 text-slate-300">{row.description}</td>
                                                                                                      <td className="px-4 py-2 text-right text-blue-400 font-bold font-mono">
                                                                                                          ${Math.abs(Number(row.amount || 0)).toFixed(2)}
                                                                                                      </td>
                                                                                                  </tr>
                                                                                              ))}
                                                                                          </tbody>
                                                                                      </table>
                                                                                  </div>
                                                                              );
                                                                          })()}
                                                                      </div>
                                                                  )}

                                                                  {/* PAMM MODE: 2 Tables */}
                                                                  {isPammMode && (
                                                                      <>
                                                                          <div>
                                                                              <h4 className="text-sm font-bold text-indigo-400 mb-3 flex items-center gap-2">
                                                                                  <History size={14} /> PAMM Rebates Generated By Client
                                                                              </h4>
                                                                              {(() => {
                                                                                  const pammRebates = rawLifetimePammLedger.filter(r => {
                                                                                      const rowEmail = String(r.client_email || '').toLowerCase().trim();
                                                                                      const cEmail = String(client.email || '').toLowerCase().trim();
                                                                                      return rowEmail && cEmail && rowEmail === cEmail && Number(r.pamm_rebate) > 0;
                                                                                  }).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

                                                                                  if (pammRebates.length === 0) return <div className="text-slate-500 text-xs italic">No PAMM rebate records found for this client.</div>;
                                                                                  
                                                                                  return (
                                                                                      <div className="overflow-x-auto rounded border border-slate-800">
                                                                                          <table className="w-full text-left text-xs">
                                                                                              <thead className="bg-slate-900 text-slate-400 uppercase font-bold">
                                                                                                  <tr>
                                                                                                      <th className="px-4 py-2">Date</th>
                                                                                                      <th className="px-4 py-2">Strategy</th>
                                                                                                      <th className="px-4 py-2 text-right">Alloc %</th>
                                                                                                      <th className="px-4 py-2 text-right">Volume</th>
                                                                                                      <th className="px-4 py-2 text-right">Amount</th>
                                                                                                  </tr>
                                                                                              </thead>
                                                                                              <tbody className="divide-y divide-slate-800">
                                                                                                  {pammRebates.map((row, idx) => (
                                                                                                      <tr key={idx} className="hover:bg-slate-800/50 transition-colors">
                                                                                                          <td className="px-4 py-2 text-slate-300 font-mono">{new Date(row.created_at).toLocaleDateString()}</td>
                                                                                                          <td className="px-4 py-2 text-slate-300">{row.pamm_commission_runs?.strategy_name || 'Unknown'}</td>
                                                                                                          <td className="px-4 py-2 text-right text-slate-400 font-mono">{formatAllocPct(row.allocation_percentage || 0)}</td>
                                                                                                          <td className="px-4 py-2 text-right text-slate-400 font-mono">{formatLots(row.pamm_volume || 0)}</td>
                                                                                                          <td className="px-4 py-2 text-right text-blue-400 font-bold font-mono">
                                                                                                              {formatCurrency(Math.abs(Number(row.pamm_rebate || 0)))}
                                                                                                          </td>
                                                                                                      </tr>
                                                                                                  ))}
                                                                                              </tbody>
                                                                                          </table>
                                                                                      </div>
                                                                                  );
                                                                              })()}
                                                                          </div>

                                                                          <div>
                                                                              <h4 className="text-sm font-bold text-green-400 mb-3 flex items-center gap-2">
                                                                                  <Award size={14} /> PAMM Performance Fee Bonus Generated By Client
                                                                              </h4>
                                                                              {(() => {
                                                                                  const pammPerf = rawLifetimePammLedger.filter(r => {
                                                                                      const rowEmail = String(r.client_email || '').toLowerCase().trim();
                                                                                      const cEmail = String(client.email || '').toLowerCase().trim();
                                                                                      return rowEmail && cEmail && rowEmail === cEmail && Number(r.profit_split) > 0;
                                                                                  }).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

                                                                                  if (pammPerf.length === 0) return <div className="text-slate-500 text-xs italic">No PAMM performance bonus records found for this client.</div>;
                                                                                  
                                                                                  return (
                                                                                      <div className="overflow-x-auto rounded border border-slate-800">
                                                                                          <table className="w-full text-left text-xs">
                                                                                              <thead className="bg-slate-900 text-slate-400 uppercase font-bold">
                                                                                                  <tr>
                                                                                                      <th className="px-4 py-2">Date</th>
                                                                                                      <th className="px-4 py-2">Strategy</th>
                                                                                                      <th className="px-4 py-2 text-right">Alloc %</th>
                                                                                                      <th className="px-4 py-2 text-right">Amount</th>
                                                                                                  </tr>
                                                                                              </thead>
                                                                                              <tbody className="divide-y divide-slate-800">
                                                                                                  {pammPerf.map((row, idx) => (
                                                                                                      <tr key={idx} className="hover:bg-slate-800/50 transition-colors">
                                                                                                          <td className="px-4 py-2 text-slate-300 font-mono">{new Date(row.created_at).toLocaleDateString()}</td>
                                                                                                          <td className="px-4 py-2 text-slate-300">{row.pamm_commission_runs?.strategy_name || 'Unknown'}</td>
                                                                                                          <td className="px-4 py-2 text-right text-slate-400 font-mono">{formatAllocPct(row.allocation_percentage || 0)}</td>
                                                                                                          <td className="px-4 py-2 text-right text-green-400 font-bold font-mono">
                                                                                                              {formatCurrency(Math.abs(Number(row.profit_split || 0)))}
                                                                                                          </td>
                                                                                                      </tr>
                                                                                                  ))}
                                                                                              </tbody>
                                                                                          </table>
                                                                                      </div>
                                                                                  );
                                                                              })()}
                                                                          </div>
                                                                      </>
                                                                  )}
                                                              </div>
                                                          )}
                                                      </div>
                                                  </td>
                                              </tr>
                                          )}
                                      </React.Fragment>
                                      )
                                  })}
                                  {activeClientsList.length === 0 && (
                                      <tr>
                                          <td colSpan="6" className="p-8 text-center text-slate-500 italic">No clients found in this tier.</td>
                                      </tr>
                                  )}
                              </tbody>
                          </table>
                      </div>
                      
                      {totalClientPages > 1 && (
                          <div className="flex justify-between items-center p-4 border-t border-slate-800 bg-slate-900">
                              <button onClick={() => setClientListPage(p => Math.max(1, p - 1))} disabled={clientListPage === 1} className="px-3 py-1.5 bg-slate-800 rounded text-xs font-bold text-slate-400 hover:text-white disabled:opacity-30 transition-colors">Previous</button>
                              <span className="text-xs text-slate-400 font-bold">Page {clientListPage} of {totalClientPages}</span>
                              <button onClick={() => setClientListPage(p => Math.min(totalClientPages, p + 1))} disabled={clientListPage === totalClientPages} className="px-3 py-1.5 bg-slate-800 rounded text-xs font-bold text-slate-400 hover:text-white disabled:opacity-30 transition-colors">Next</button>
                          </div>
                      )}
                              </div>
                          );
                      })()}
                  </div>
              </div>
          </div>
      );
  };

  // --- RENDER: MAIN LIST VIEW ---
  return (
    <div className="p-4 md:p-8 animate-fade-in relative">

      {/* MANUAL BONUS / BACKFILL MODAL */}
      {showManualBonusModal && selectedIB && (
          <div className="fixed inset-0 bg-slate-950/80 z-[100] flex items-center justify-center p-4 overflow-hidden" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}>
              <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl p-6 animate-fade-in relative">
                  <div className="flex justify-between items-center mb-6">
                      <h3 className="text-xl font-bold text-white flex items-center gap-2"><Award className="text-purple-500"/> Add Manual Bonus</h3>
                      <button onClick={() => setShowManualBonusModal(false)} className="text-slate-400 hover:text-white"><X size={20}/></button>
                  </div>
                  <div className="space-y-4">
                      <div>
                          <label className="text-xs font-bold text-slate-400 uppercase block mb-2">Bonus Type</label>
                          <select className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white focus:ring-2 focus:ring-purple-500 outline-none" value={manualBonusForm.type} onChange={(e) => setManualBonusForm({...manualBonusForm, type: e.target.value})}>
                              <option value="challenge_bonus">Challenge Sale Backfill</option>
                              <option value="membership_bonus">Membership Sale Backfill</option>
                              <option value="performance_bonus">Custom Performance Bonus</option>
                              <option value="commission">Manual Trading Commission</option>
                              <option value="pamm_commission">PAMM Trading Commission</option> 
                          </select>
                      </div>

                      <div>
                          <label className="text-xs font-bold text-slate-400 uppercase block mb-2">Commission Amount ($)</label>
                          <input type="number" className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white focus:ring-2 focus:ring-purple-500 outline-none" value={manualBonusForm.amount} onChange={(e) => setManualBonusForm({...manualBonusForm, amount: e.target.value})} placeholder="50.00" />
                      </div>

                      <div>
                          <label className="text-xs font-bold text-slate-400 uppercase block mb-2">Client Email (Optional)</label>
                          <input type="email" className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white focus:ring-2 focus:ring-purple-500 outline-none text-sm" value={manualBonusForm.clientEmail} onChange={(e) => setManualBonusForm({...manualBonusForm, clientEmail: e.target.value})} placeholder="trader@email.com" />
                      </div>

                      <div>
                          <label className="text-xs font-bold text-slate-400 uppercase block mb-2">Description</label>
                          <input type="text" className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white focus:ring-2 focus:ring-purple-500 outline-none text-sm" value={manualBonusForm.description} onChange={(e) => setManualBonusForm({...manualBonusForm, description: e.target.value})} placeholder="e.g. Backfilled missing 100k Challenge" />
                      </div>

                      <button onClick={handleAddManualBonus} disabled={isProcessingManualBonus || !manualBonusForm.amount || !manualBonusForm.description} className="w-full py-3 bg-purple-600 disabled:opacity-50 hover:bg-purple-500 text-white font-bold rounded-lg mt-4 transition-colors">
                          {isProcessingManualBonus ? 'Processing...' : 'Confirm & Inject Bonus'}
                      </button>
                  </div>
              </div>
          </div>
      )}
      
      {/* PAYOUT PROCESSING MODAL */}
      {showPayoutModal && selectedIB && (
          <div className="absolute inset-0 bg-slate-950/80 z-50 flex items-center justify-center p-4">
              <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl p-6 animate-fade-in">
                  <div className="flex justify-between items-center mb-6">
                      <h3 className="text-xl font-bold text-white">Process Payout</h3>
                      <button onClick={() => setShowPayoutModal(false)} className="text-slate-400 hover:text-white"><X size={20}/></button>
                  </div>
                  <div className="space-y-4">
                      <div className="bg-slate-950 p-4 rounded-xl border border-slate-800 mb-4 flex justify-between items-center">
                          <div>
                              <p className="text-xs text-slate-500 uppercase font-bold mb-1">Available Balance</p>
                              <p className="text-2xl font-bold text-green-400">${computedWalletBalance.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                          </div>
                          <Wallet size={32} className="text-slate-700" />
                      </div>

                      <div>
                          <label className="text-xs font-bold text-slate-400 uppercase block mb-2">Amount to Payout ($)</label>
                          <input type="number" max={computedWalletBalance} className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white focus:ring-2 focus:ring-amber-500 outline-none" value={payoutForm.amount} onChange={(e) => setPayoutForm({...payoutForm, amount: e.target.value})} placeholder="0.00" />
                      </div>

                      <div>
                          <label className="text-xs font-bold text-slate-400 uppercase block mb-2">Payout Method</label>
                          <select className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white focus:ring-2 focus:ring-amber-500 outline-none" value={payoutForm.method} onChange={(e) => setPayoutForm({...payoutForm, method: e.target.value})}>
                              <option>USDT (TRC20)</option>
                              <option>USDT (ERC20)</option>
                              <option>Bank Wire Transfer</option>
                              <option>Internal Trading Account</option>
                          </select>
                      </div>

                      <div>
                          <label className="text-xs font-bold text-slate-400 uppercase block mb-2">Destination Details</label>
                          <input type="text" className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white focus:ring-2 focus:ring-amber-500 outline-none" value={payoutForm.address} onChange={(e) => setPayoutForm({...payoutForm, address: e.target.value})} placeholder="Wallet Address, IBAN, or Account ID" />
                      </div>

                      <button onClick={handleProcessPayout} disabled={isProcessingPayout || !payoutForm.amount} className="w-full py-3 bg-amber-500 disabled:opacity-50 hover:bg-amber-400 text-slate-900 font-bold rounded-lg mt-4 transition-colors">
                          {isProcessingPayout ? 'Processing...' : 'Confirm & Record Payout'}
                      </button>
                  </div>
              </div>
          </div>
      )}

      {/* FILTER MODAL */}
      {showFilterModal && (
          <div className="absolute inset-0 bg-slate-950/80 z-50 flex items-center justify-center p-4">
              <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-sm shadow-2xl p-6 animate-fade-in">
                  <div className="flex justify-between items-center mb-6">
                      <h3 className="text-xl font-bold text-white">Filter Partners</h3>
                      <button onClick={() => setShowFilterModal(false)} className="text-slate-400 hover:text-white"><X size={20}/></button>
                  </div>
                  <div className="space-y-6">
                      <div><label className="text-xs font-bold text-slate-400 uppercase block mb-2">Minimum Clients</label><input type="number" className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white focus:ring-2 focus:ring-amber-500 outline-none" value={filters.minClients} onChange={(e) => setFilters({...filters, minClients: parseInt(e.target.value) || 0})}/></div>
                      <div><label className="text-xs font-bold text-slate-400 uppercase block mb-2">Min Revenue ($)</label><input type="number" className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white focus:ring-2 focus:ring-amber-500 outline-none" value={filters.minRebate} onChange={(e) => setFilters({...filters, minRebate: parseInt(e.target.value) || 0})}/></div>
                      <div className="flex gap-3 pt-2"><button onClick={clearFilters} className="flex-1 py-3 bg-slate-800 text-slate-300 font-bold rounded-lg hover:bg-slate-700 transition-colors">Clear</button><button onClick={applyFilters} className="flex-1 py-3 bg-amber-500 text-slate-900 font-bold rounded-lg hover:bg-amber-400 transition-colors">Apply</button></div>
                  </div>
              </div>
          </div>
      )}

      {/* REFERRAL LINK GENERATOR MODAL */}
      {showLinkModal && (
          <div className="absolute inset-0 bg-slate-950/80 z-50 flex items-center justify-center p-4">
              <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl p-6 animate-fade-in">
                  <div className="flex justify-between items-center mb-6">
                      <h3 className="text-xl font-bold text-white">Generate Referral Link</h3>
                      <button onClick={() => setShowLinkModal(false)} className="text-slate-400 hover:text-white"><X size={20}/></button>
                  </div>
                  <div className="space-y-4">
                      <div>
                          <label className="text-xs font-bold text-slate-400 uppercase block mb-2">Link Type</label>
                          <div className="flex gap-2">
                              <button onClick={() => setNewLinkData({...newLinkData, type: 'auto'})} className={`flex-1 py-2 rounded-lg text-xs font-bold border transition-colors ${newLinkData.type === 'auto' ? 'bg-blue-600 border-blue-500 text-white' : 'bg-slate-950 border-slate-700 text-slate-400'}`}>Auto-Generate</button>
                              <button onClick={() => setNewLinkData({...newLinkData, type: 'custom'})} className={`flex-1 py-2 rounded-lg text-xs font-bold border transition-colors ${newLinkData.type === 'custom' ? 'bg-blue-600 border-blue-500 text-white' : 'bg-slate-950 border-slate-700 text-slate-400'}`}>Custom</button>
                          </div>
                      </div>
                      {newLinkData.type === 'custom' && (
                          <div>
                              <label className="text-xs font-bold text-slate-400 uppercase block mb-2">Custom Suffix</label>
                              <div className="flex items-center bg-slate-800 border border-slate-600 rounded-lg px-3">
                                  <span className="text-slate-500 text-sm">nommia.com/ref/</span>
                                  <input className="bg-transparent w-full py-3 text-white text-sm outline-none" placeholder="my-custom-tag" value={newLinkData.customSuffix} onChange={(e) => setNewLinkData({...newLinkData, customSuffix: e.target.value})} />
                              </div>
                          </div>
                      )}
                      <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                          <p className="text-[10px] text-amber-200">
                              <strong>Note:</strong> This link will be permanently associated with the IB. Any user registering via this link will automatically appear in the 'Tier 1' client list.
                          </p>
                      </div>
                      <button onClick={() => { alert('New Link Generated!'); setShowLinkModal(false); }} className="w-full py-3 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-lg mt-2">Create Link</button>
                  </div>
              </div>
          </div>
      )}

      {/* ASSET MANAGER MODAL */}
      {showAssetModal && (
          <div className="absolute inset-0 bg-slate-950/80 z-50 flex items-center justify-center p-4">
              <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-2xl shadow-2xl p-6 animate-fade-in flex flex-col max-h-[80vh]">
                  <div className="flex justify-between items-center mb-6 border-b border-slate-800 pb-4">
                      <h3 className="text-xl font-bold text-white">Global Asset Manager</h3>
                      <button onClick={() => setShowAssetModal(false)} className="text-slate-400 hover:text-white"><X size={20}/></button>
                  </div>
                  <div className="flex-1 overflow-y-auto grid grid-cols-2 md:grid-cols-3 gap-4">
                      {[1,2,3,4,5,6].map(i => (
                          <div key={i} className="bg-slate-950 border border-slate-800 rounded-xl p-4 flex flex-col items-center justify-center cursor-pointer hover:border-blue-500/50 transition-colors group">
                              <FileImage size={32} className="text-slate-600 mb-2 group-hover:text-white" />
                              <span className="text-xs text-slate-300 font-bold">Banner_Pack_{i}.zip</span>
                              <span className="text-[10px] text-slate-500">12MB • Added today</span>
                          </div>
                      ))}
                      <div className="bg-slate-950 border border-dashed border-slate-700 rounded-xl p-4 flex flex-col items-center justify-center cursor-pointer hover:bg-slate-900 hover:border-slate-500 transition-colors">
                          <Plus size={32} className="text-slate-500 mb-2" />
                          <span className="text-xs text-slate-400 font-bold uppercase">Upload New</span>
                      </div>
                  </div>
              </div>
          </div>
      )}

      {/* PAYOUT HISTORY MODAL */}
      {showPayoutHistory && (
          <div className="absolute inset-0 bg-slate-950/80 z-50 flex items-center justify-center p-4">
              <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-3xl shadow-2xl p-6 animate-fade-in">
                  <div className="flex justify-between items-center mb-6">
                      <h3 className="text-xl font-bold text-white">Full Payout History</h3>
                      <button onClick={() => setShowPayoutHistory(false)} className="text-slate-400 hover:text-white"><X size={20}/></button>
                  </div>
                  <div className="overflow-x-auto">
                      <table className="w-full text-left">
                          <thead className="bg-slate-950 text-xs font-bold text-slate-400 uppercase">
                              <tr>
                                  <th className="px-6 py-4">Reference ID</th>
                                  <th className="px-6 py-4">Date</th>
                                  <th className="px-6 py-4">Method</th>
                                  <th className="px-6 py-4">Amount</th>
                                  <th className="px-6 py-4">Status</th>
                              </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-800 text-sm">
                              {(allWithdrawals || []).slice(0, 50).map((w, idx) => (
                                  <tr key={w.id || idx} className="hover:bg-slate-800/50">
                                      <td className="px-6 py-4 font-mono text-slate-400">{w.reference_id || `PAY-${w.id?.slice(0,8)}`}</td>
                                      <td className="px-6 py-4 text-white">{new Date(w.created_at).toLocaleDateString()}</td>
                                      <td className="px-6 py-4 text-slate-300">{w.method || 'USDT'}</td>
                                      <td className="px-6 py-4 font-bold text-white">${Number(w.amount || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                                      <td className="px-6 py-4">
                                          <span className={`px-2 py-1 rounded text-xs font-bold ${w.status === 'Completed' || w.status === 'Paid' ? 'text-green-400 bg-green-900/20' : w.status === 'Declined' ? 'text-red-400 bg-red-900/20' : 'text-amber-400 bg-amber-900/20'}`}>
                                              {w.status || 'Completed'}
                                          </span>
                                      </td>
                                  </tr>
                              ))}
                          </tbody>
                      </table>
                  </div>
              </div>
          </div>
      )}


      {viewMode === 'list' ? (
          <>
            <div className="flex justify-between items-end mb-8">
                <div>
                    <h2 className="text-2xl md:text-3xl font-bold text-white tracking-tight">IB Partner Network</h2>
                    <p className="text-slate-400 mt-1">Manage Introducers, Affiliates, and Rebate Structures.</p>
                </div>
                <div className="flex gap-2">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                        <input 
                            className="bg-slate-900 border border-slate-700 rounded-lg pl-10 pr-4 py-2 text-sm text-white focus:ring-2 focus:ring-amber-500 outline-none" 
                            placeholder="Search partners..." 
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <button 
                        onClick={() => setShowFilterModal(true)}
                        className={`px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 transition-colors ${filters.minClients > 0 || filters.minRebate > 0 ? 'bg-amber-500 text-slate-900' : 'bg-slate-800 text-slate-300 border border-slate-700 hover:bg-slate-700'}`}
                    >
                        <Filter size={16} /> Filter
                    </button>
                </div>
            </div>

            {/* --- NEW: MAIN TAB NAVIGATION --- */}
            <div className="flex space-x-2 border-b border-slate-800 mb-8 pb-px">
                <button
                    onClick={() => setMainTab('network')}
                    className={`px-6 py-3 text-sm font-bold border-b-2 transition-colors ${mainTab === 'network' ? 'border-amber-500 text-amber-500' : 'border-transparent text-slate-400 hover:text-slate-200'}`}
                >
                    <Network size={16} className="inline mr-2" /> Network Hub
                </button>
                <button
                    onClick={() => setMainTab('pamm')}
                    className={`px-6 py-3 text-sm font-bold border-b-2 transition-colors ${mainTab === 'pamm' ? 'border-purple-500 text-purple-500' : 'border-transparent text-slate-400 hover:text-slate-200'}`}
                >
                    <Layers size={16} className="inline mr-2" /> PAMM Hub
                </button>
                <button
                    onClick={() => setMainTab('payouts')}
                    className={`px-6 py-3 text-sm font-bold border-b-2 transition-colors flex items-center gap-2 ${mainTab === 'payouts' ? 'border-amber-500 text-amber-500' : 'border-transparent text-slate-400 hover:text-slate-200'}`}
                >
                    <Wallet size={16} /> Global Payouts
                    {allWithdrawals.filter(w => w.status === 'Pending').length > 0 && (
                        <span className="bg-red-500 text-white text-[10px] px-2 py-0.5 rounded-full">{allWithdrawals.filter(w => w.status === 'Pending').length}</span>
                    )}
                </button>
            </div>

            {/* CONDITIONAL RENDER: NETWORK HUB */}
            {mainTab === 'network' && (
                <div className="animate-fade-in">

            {/* LIVE GLOBAL STATS */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                 <div className="bg-slate-900 p-6 rounded-2xl border border-slate-800">
                     <p className="text-slate-500 text-xs font-bold uppercase flex items-center gap-2"><Network size={14}/> Total Partners</p>
                     <p className="text-2xl font-bold text-white mt-2">{totalPartners.toLocaleString()}</p>
                 </div>
                 <div className="bg-slate-900 p-6 rounded-2xl border border-slate-800">
                     <p className="text-slate-500 text-xs font-bold uppercase flex items-center gap-2"><Users size={14}/> Total Referrals</p>
                     <p className="text-2xl font-bold text-blue-400 mt-2">{totalReferrals.toLocaleString()}</p>
                 </div>
                 <div className="bg-slate-900 p-6 rounded-2xl border border-slate-800">
                     <p className="text-slate-500 text-xs font-bold uppercase flex items-center gap-2"><Activity size={14}/> Monthly Volume (30d)</p>
                     {/* 🚨 Replaced 'monthlyVolume' with 'trueGlobalVolume' to fix multi-tier double counting */}
                     <p className="text-2xl font-bold text-amber-500 mt-2">{formatCompact(trueGlobalVolume)} Lots</p>
                 </div>
                 <div className="bg-slate-900 p-6 rounded-2xl border border-slate-800">
                     <p className="text-slate-500 text-xs font-bold uppercase flex items-center gap-2"><DollarSign size={14}/> Payouts (30d)</p>
                     <p className="text-2xl font-bold text-green-400 mt-2">${formatCompact(payouts30d)}</p>
                 </div>
            </div>

            {/* NEW SECTION: GLOBAL TOOLS (Moved from Profile View) */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                  {/* Referral Link Manager */}
                  <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6">
                      <h3 className="font-bold text-white mb-4 flex items-center gap-2"><LinkIcon size={18} className="text-blue-500"/> Global Link Manager</h3>
                      <div className="space-y-4">
                          <p className="text-xs text-slate-400">Generate or manage links for any partner in the network.</p>
                          <div className="flex gap-2">
                             <button onClick={() => setShowLinkModal(true)} className="flex-1 py-2 bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold rounded border border-slate-700 transition-colors flex items-center justify-center gap-1">
                                 <Plus size={12} /> Create Link
                             </button>
                             <button className="flex-1 py-2 bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold rounded border border-slate-700 transition-colors flex items-center justify-center gap-1">
                                 <Settings size={12} /> Manage All
                             </button>
                          </div>
                      </div>
                  </div>

                  {/* Marketing Asset Access */}
                  <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6">
                      <h3 className="font-bold text-white mb-4 flex items-center gap-2"><FileImage size={18} className="text-purple-500"/> Marketing Assets</h3>
                      <div className="space-y-3">
                          <p className="text-xs text-slate-400">Upload banners and videos to be distributed to all IBs.</p>
                          <button onClick={() => setShowAssetModal(true)} className="w-full mt-2 py-2 text-xs bg-slate-800 hover:bg-slate-700 text-white font-bold rounded border border-slate-700 flex items-center justify-center gap-2">
                              <Plus size={12} /> Upload / Manage Assets
                          </button>
                      </div>
                  </div>

                  {/* Payout History */}
                  <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6">
                      <h3 className="font-bold text-white mb-4 flex items-center gap-2"><History size={18} className="text-green-500"/> Recent Payouts</h3>
                      <div className="space-y-3">
                          <div className="flex justify-between items-center text-xs border-b border-slate-800 pb-2">
                              <span className="text-slate-400">Global Total (24h)</span>
                              <span className="text-white font-bold">
                                  ${payouts24h.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                              </span>
                          </div>
                          <button onClick={() => setShowPayoutHistory(true)} className="w-full mt-4 text-xs text-slate-500 hover:text-white underline">View Global Payout History</button>
                      </div>
                  </div>
              </div>

              {/* --- NEW SECTION: MASTER FRANCHISE MANAGEMENT --- */}
            <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6 mb-8 shadow-sm">
                <div className="flex items-center gap-3 mb-6 border-b border-slate-800 pb-4">
                    <div className="w-10 h-10 rounded-lg bg-yellow-500/10 flex items-center justify-center border border-yellow-500/20">
                        <span className="text-xl">🌍</span>
                    </div>
                    <div>
                        <h3 className="font-bold text-white text-lg">Master Franchise Management</h3>
                        <p className="text-xs text-slate-400 mt-1">Promote partners to Country Leaders or Regional Managers to activate the $1/Lot volume overrides.</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                    <div className="md:col-span-1">
                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-2">Select Partner</label>
                        <select 
                            className="w-full bg-slate-950 border border-slate-700 rounded-lg p-3 text-sm text-white focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all outline-none"
                            value={leaderForm.email}
                            onChange={(e) => setLeaderForm({...leaderForm, email: e.target.value})}
                        >
                            <option value="">-- Choose an IB --</option>
                            {liveIBs.map(ib => (
                                <option key={ib.id} value={ib.email}>{ib.name} ({ib.email})</option>
                            ))}
                        </select>
                    </div>

                    <div className="md:col-span-1">
                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-2">Assign Role</label>
                        <select 
                            className="w-full bg-slate-950 border border-slate-700 rounded-lg p-3 text-sm text-white focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all outline-none"
                            value={leaderForm.role}
                            onChange={(e) => setLeaderForm({...leaderForm, role: e.target.value})}
                        >
                            <option value="country">Country Leader</option>
                            <option value="region">Regional Manager</option>
                        </select>
                    </div>

                    <div className="md:col-span-1">
                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-2">Code <span className="text-slate-600 lowercase ml-1">(Leave blank to remove)</span></label>
                        <input 
                            type="text" 
                            placeholder={leaderForm.role === 'country' ? "e.g., NG, GB, ZA" : "e.g., AFRICA, EUROPE"}
                            className="w-full bg-slate-950 border border-slate-700 rounded-lg p-3 text-sm text-white focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all outline-none uppercase placeholder:normal-case placeholder:text-slate-600"
                            value={leaderForm.regionCode}
                            onChange={(e) => setLeaderForm({...leaderForm, regionCode: e.target.value.toUpperCase()})}
                        />
                    </div>

                    <div className="md:col-span-1">
                        <button 
                            onClick={handleAssignLeadership}
                            disabled={isAssigningLeader || !leaderForm.email}
                            className="w-full py-3 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-slate-900 text-sm font-bold rounded-lg transition-colors"
                        >
                            {isAssigningLeader ? 'Updating Database...' : leaderForm.regionCode ? 'Promote Partner' : 'Remove Role'}
                        </button>
                    </div>
                </div>
            </div>
            {/* --- END MASTER FRANCHISE --- */}

              {/* --- NEW SECTION: HEADLESS COMMISSION HUB --- */}
              <div className="bg-slate-900 rounded-2xl border border-blue-500/50 p-6 mb-8 relative overflow-hidden shadow-lg shadow-blue-900/10">
                  <div className="absolute top-0 right-0 bg-blue-600 text-white text-[10px] font-bold px-3 py-1 rounded-bl-lg uppercase tracking-wider">Live Supabase Engine</div>
                  
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-6 gap-4">
                      <div>
                          <h3 className="text-xl font-bold text-white flex items-center gap-2"><Activity className="text-blue-500" /> Headless Commission Ledger</h3>
                          <p className="text-sm text-slate-400 mt-1">Live data streaming directly from the Supabase wallet_ledger table.</p>
                          
                          {/* 🚨 NEW: The Pill-Shaped Toggle Switch */}
                          <div className="flex space-x-2 mt-4 bg-slate-950 p-1 rounded-lg w-fit border border-slate-800">
                              <button onClick={() => { setLedgerView('earnings'); setLedgerPage(1); }} className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all ${ledgerView === 'earnings' ? 'bg-green-500/20 text-green-400 border border-green-500/30 shadow-sm' : 'text-slate-500 hover:text-slate-300'}`}>Earnings (Liability)</button>
                              <button onClick={() => { setLedgerView('withdrawals'); setLedgerPage(1); }} className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all ${ledgerView === 'withdrawals' ? 'bg-red-500/20 text-red-400 border border-red-500/30 shadow-sm' : 'text-slate-500 hover:text-slate-300'}`}>Withdrawals (Outflow)</button>
                              <button onClick={() => { setLedgerView('all'); setLedgerPage(1); }} className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all ${ledgerView === 'all' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30 shadow-sm' : 'text-slate-500 hover:text-slate-300'}`}>All Transactions</button>
                          </div>
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-3">
                          <div className="flex items-center gap-2">
                              <select 
                                  value={ledgerTimeframe} 
                                  onChange={(e) => { setLedgerTimeframe(e.target.value); setLedgerPage(1); }}
                                  className="bg-slate-950 border border-slate-700 text-slate-300 text-sm font-bold rounded-lg px-3 py-2 outline-none focus:border-blue-500 transition-colors"
                              >
                                  <option value="today">Today</option>
                                  <option value="week">Past 7 Days</option>
                                  <option value="month">Past 30 Days</option>
                                  <option value="quarter">Past Quarter</option>
                                  <option value="year">Past Year</option>
                                  <option value="all">All Time</option>
                                  <option value="custom">Custom Date Range</option>
                              </select>
                              
                              {/* 🚨 NEW: Custom Date Pickers */}
                              {ledgerTimeframe === 'custom' && (
                                  <div className="flex items-center gap-2 bg-slate-950 border border-slate-700 rounded-lg px-2 py-2">
                                      <input type="date" value={customStartDate} onChange={e => setCustomStartDate(e.target.value)} className="bg-transparent text-slate-300 text-sm outline-none cursor-pointer [color-scheme:dark]" />
                                      <span className="text-slate-500">-</span>
                                      <input type="date" value={customEndDate} onChange={e => setCustomEndDate(e.target.value)} className="bg-transparent text-slate-300 text-sm outline-none cursor-pointer [color-scheme:dark]" />
                                  </div>
                              )}
                          </div>
                          <button onClick={exportToCSV} className="bg-slate-800 hover:bg-slate-700 text-white border border-slate-700 px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 transition-colors">
                              <Download size={16} /> Export CSV
                          </button>
                          <div className="text-right ml-4 border-l border-slate-800 pl-4 min-w-[140px]">
                              <p className="text-[10px] font-bold text-slate-500 uppercase">Filtered Total</p>
                              {/* 🚨 DYNAMIC FILTERED TOTAL: Reflects active tab (Earnings, Withdrawals, or Net) */}
                              <p className={`text-2xl font-bold ${
                                  ledgerView === 'withdrawals' 
                                      ? 'text-red-400' 
                                      : ledgerView === 'earnings' 
                                          ? 'text-green-400' 
                                          : (filteredLedger.reduce((sum, item) => sum + (Math.floor(Number(item.amount || 0) * 100) / 100), 0) >= 0 ? 'text-green-400' : 'text-red-400')
                              }`}>
                                  {(() => {
                                      const total = filteredLedger.reduce((sum, item) => sum + (Math.floor(Number(item.amount || 0) * 100) / 100), 0);
                                      if (ledgerView === 'withdrawals') {
                                          return `$${Math.abs(total).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
                                      } else if (ledgerView === 'earnings') {
                                          return `$${Math.abs(total).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
                                      } else {
                                          const sign = total >= 0 ? '+' : '-';
                                          return `${sign}$${Math.abs(total).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
                                      }
                                  })()}
                              </p>
                          </div>
                      </div>
                  </div>
                  
                  <div className="border border-slate-800 rounded-xl overflow-hidden">
                      <div className="overflow-x-auto">
                          <table className="w-full text-left">
                              <thead className="bg-slate-950 text-xs font-bold text-slate-400 uppercase">
                                  <tr>
                                      <th className="px-6 py-4">Date</th>
                                      <th className="px-6 py-4">Partner Email</th>
                                      <th className="px-6 py-4">Description</th>
                                      <th className="px-6 py-4 text-right">Amount</th>
                                      <th className="px-6 py-4 text-center">Type</th>
                                  </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-800 text-sm bg-slate-900/50">
                                  {isLoadingLedger ? (
                                      <tr><td colSpan="5" className="text-center py-8 text-slate-500">Loading live ledger data...</td></tr>
                                  ) : paginatedLedger.length === 0 ? (
                                      <tr><td colSpan="5" className="text-center py-8 text-slate-500">No commissions found for this timeframe.</td></tr>
                                  ) : (
                                      paginatedLedger.map((entry, idx) => (
                                          <tr key={idx} className="hover:bg-slate-800/80 transition-colors">
                                              <td className="px-6 py-3 text-slate-400">{new Date(entry.created_at).toLocaleDateString()}</td>
                                              <td className="px-6 py-3 font-medium text-white">{resolvePartnerEmail(entry.partner_id)}</td>
                                              <td className="px-6 py-3 text-slate-400 text-xs">{entry.description}</td>
                                              <td className={`px-6 py-3 font-bold text-right ${Number(entry.amount) < 0 ? 'text-red-400' : 'text-green-400'}`}>
                                                  {Number(entry.amount) < 0 ? '-' : '+'}
                                                  ${Math.abs(Math.floor(Number(entry.amount || 0) * 100) / 100).toLocaleString(undefined, {minimumFractionDigits: 2})}
                                              </td>
                                              <td className="px-6 py-3 text-center"><span className="bg-blue-900/20 text-blue-400 border border-blue-500/20 px-2 py-1 rounded text-[10px] font-bold uppercase whitespace-nowrap">{entry.transaction_type?.replace('_', ' ')}</span></td>
                                          </tr>
                                      ))
                                  )}
                              </tbody>
                          </table>
                      </div>

                      {/* --- PAGINATION CONTROLS --- */}
                      {totalLedgerPages > 1 && (
                          <div className="flex items-center justify-between px-6 py-4 bg-slate-950 border-t border-slate-800">
                              <span className="text-xs text-slate-500 font-medium">
                                  Showing {(ledgerPage - 1) * LEDGER_PAGE_SIZE + 1} to {Math.min(ledgerPage * LEDGER_PAGE_SIZE, filteredLedger.length)} of {filteredLedger.length} entries
                              </span>
                              <div className="flex gap-2">
                                  <button 
                                      onClick={() => setLedgerPage(p => Math.max(1, p - 1))}
                                      disabled={ledgerPage === 1}
                                      className="px-4 py-1.5 bg-slate-800 text-slate-300 rounded-md text-xs font-bold disabled:opacity-30 hover:bg-slate-700 transition-colors"
                                  >Previous</button>
                                  <button 
                                      onClick={() => setLedgerPage(p => Math.min(totalLedgerPages, p + 1))}
                                      disabled={ledgerPage === totalLedgerPages}
                                      className="px-4 py-1.5 bg-slate-800 text-slate-300 rounded-md text-xs font-bold disabled:opacity-30 hover:bg-slate-700 transition-colors"
                                  >Next</button>
                              </div>
                          </div>
                      )}
                  </div>
              </div>
              {/* --- END NEW SECTION --- */}

            <div className="grid gap-6">
               {filteredIBs.length > 0 ? filteredIBs.map((ib) => (
                  <div key={ib.id} className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden group hover:border-slate-700 transition-all">
                     <div className="p-4 md:p-6 flex flex-col md:flex-row justify-between md:items-center border-b border-slate-800 bg-slate-800/30 gap-4">
                        <div className="flex items-center gap-4">
                           <div className="w-12 h-12 bg-amber-500/10 rounded-xl flex items-center justify-center text-amber-500 flex-shrink-0 border border-amber-500/20">
                              <Network size={24} />
                           </div>
                           <div>
                              <h3 className="font-bold text-white text-lg">{ib.name}</h3>
                              <div className="flex flex-wrap items-center gap-2 mt-1">
                                  <p className="text-xs text-slate-400 font-mono bg-slate-950 px-2 py-0.5 rounded border border-slate-800" title="Supabase UUID">{ib.id}</p>
                                  <p className="text-xs text-slate-400 font-mono bg-slate-950 px-2 py-0.5 rounded border border-slate-800" title="Email Address">{ib.email}</p>
                                  <span className="text-xs font-bold text-blue-400 bg-blue-900/20 px-2 py-0.5 rounded border border-blue-500/20">{ib.level}</span>
                              </div>
                           </div>
                        </div>
                        
                        <div className="flex items-center gap-8">
                            <div className="text-left">
                                <p className="text-xs text-slate-500 uppercase font-bold">Clients</p>
                                <p className="text-lg font-bold text-white">{ib.clients}</p>
                            </div>
                            <div className="text-left">
                                <p className="text-xs text-slate-500 uppercase font-bold">Total Rebate</p>
                                <p className="text-xl font-bold text-green-400">{ib.rebate}</p>
                            </div>
                            <button 
                                onClick={() => { setSelectedIB(ib); setViewMode('detail'); }}
                                className="bg-slate-800 hover:bg-slate-700 text-white px-4 py-2 rounded-lg text-sm font-bold transition-colors"
                            >
                                View Profile
                            </button>
                        </div>
                     </div>
      </div>
   )) : (
                   <div className="p-12 text-center text-slate-500 border border-dashed border-slate-800 rounded-xl">
                       No partners found matching your criteria.
                   </div>
               )}
            </div>
            </div>
            )}

            {/* CONDITIONAL RENDER: PAMM HUB */}
            {mainTab === 'pamm' && (
                <div className="animate-fade-in space-y-8">

                <div className="flex justify-between items-center bg-slate-900 border border-slate-800 p-6 rounded-2xl mb-8">
                    <div>
                        <h2 className="text-xl font-bold text-white">Pre-Payout Sync</h2>
                        <p className="text-sm text-slate-400 mt-1">Run this before calculating daily payouts to ensure the Defily tree and PAMM allocations are perfectly up-to-date.</p>
                    </div>
                    <button 
                        onClick={handlePrePayoutSync}
                        className="bg-purple-600 hover:bg-purple-500 text-white font-bold py-3 px-6 rounded-xl transition-colors shadow-lg flex items-center gap-2"
                    >
                        Sync Now
                    </button>
                </div>

                    
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* LEFT: PAMM EXCLUSIONS */}
                        <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6 flex flex-col h-full">
                            <div className="mb-6">
                                <h3 className="font-bold text-white text-lg flex items-center gap-2"><Settings size={18} className="text-amber-500"/> PAMM Exclusions</h3>
                                <p className="text-sm text-slate-400 mt-1">Accounts listed here are skipped by the Daily Math Engine.</p>
                            </div>

                            <div className="flex gap-3 items-end mb-6 bg-slate-950 p-4 rounded-xl border border-slate-800">
                                <div className="flex-1">
                                    <label className="text-[10px] uppercase text-slate-500 font-bold block mb-1">Account ID</label>
                                    <input type="text" placeholder="e.g. 19086" value={newPammAccountId} onChange={e => setNewPammAccountId(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-white outline-none focus:border-amber-500 text-sm font-mono" />
                                </div>
                                <div className="flex-1">
                                    <label className="text-[10px] uppercase text-slate-500 font-bold block mb-1">Strategy Name</label>
                                    <input type="text" placeholder="e.g. Quantum" value={newPammStrategyName} onChange={e => setNewPammStrategyName(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-white outline-none focus:border-amber-500 text-sm" />
                                </div>
                                <button onClick={handleAddPammExclusion} className="bg-amber-500 hover:bg-amber-400 text-slate-900 px-4 py-2.5 rounded-lg text-sm font-bold transition-colors shadow-lg">
                                    Exclude
                                </button>
                            </div>

                            <div className="flex-1 overflow-auto border border-slate-800 rounded-xl bg-slate-950">
                                <table className="w-full text-left">
                                    <thead className="bg-slate-900 text-xs font-bold text-slate-400 uppercase">
                                        <tr>
                                            <th className="px-4 py-3">Strategy</th>
                                            <th className="px-4 py-3">Account ID</th>
                                            <th className="px-4 py-3 text-center">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-800 text-sm">
                                        {pammExclusions.length === 0 ? (
                                            <tr><td colSpan="3" className="p-8 text-center text-slate-500 italic">No PAMM accounts excluded.</td></tr>
                                        ) : (
                                            pammExclusions.map(pamm => (
                                                <tr key={pamm.id} className="hover:bg-slate-900/50 transition-colors">
                                                    <td className="px-4 py-3 font-bold text-white">{pamm.strategy_name}</td>
                                                    <td className="px-4 py-3 text-blue-400 font-mono text-xs">{pamm.account_id}</td>
                                                    <td className="px-4 py-3 text-center whitespace-nowrap">
                                                        <button 
                                                            onClick={() => {
                                                                setPammMasterId(pamm.account_id);
                                                                setPammStrategy(pamm.strategy_name);
                                                                setPammCalculationMode('live');
                                                                window.scrollTo({ top: 0, behavior: 'smooth' });
                                                            }}
                                                            className="inline-flex items-center gap-1 bg-purple-600/20 hover:bg-purple-600/40 text-purple-300 border border-purple-500/30 px-2.5 py-1 rounded-lg text-xs font-bold transition-colors mr-2"
                                                            title="Load into PAMM Calculator"
                                                        >
                                                            ⚡ Run
                                                        </button>
                                                        <button onClick={() => handleDeletePammExclusion(pamm.id)} className="text-slate-500 hover:text-red-400 transition-colors p-1" title="Remove Exclusion">
                                                            <X size={16} />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* RIGHT: PAMM CALCULATION & PAYOUT */}
                        <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6 flex flex-col">
                            <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                <div>
                                    <h3 className="font-bold text-white text-lg flex items-center gap-2"><Layers size={18} className="text-purple-500"/> Weekly PAMM Payout</h3>
                                    <p className="text-sm text-slate-400 mt-1">Calculate and distribute pro-rata volume commissions.</p>
                                </div>
                                <div className="flex bg-slate-950 p-1 rounded-xl border border-slate-800 self-start sm:self-auto">
                                    <button
                                        type="button"
                                        onClick={() => setPammCalculationMode('live')}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${pammCalculationMode === 'live' ? 'bg-purple-600 text-white shadow' : 'text-slate-400 hover:text-white'}`}
                                    >
                                        ⚡ 1-Click Live
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setPammCalculationMode('csv')}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${pammCalculationMode === 'csv' ? 'bg-purple-600 text-white shadow' : 'text-slate-400 hover:text-white'}`}
                                    >
                                        📄 CSV Upload
                                    </button>
                                </div>
                            </div>

                            <div className="space-y-4 flex-1">
                                {/* 🚨 NEW: QUICK SELECT DROPDOWN */}
                                <div>
                                    <label className="text-[10px] uppercase text-slate-500 font-bold block mb-1">Quick Select Saved Strategy</label>
                                    <select 
                                        className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2.5 text-slate-300 outline-none focus:border-purple-500 text-sm cursor-pointer hover:border-slate-600 transition-colors"
                                        value={activePamms.find(p => String(p.account_id) === String(pammMasterId))?.id || ''}
                                        onChange={(e) => {
                                            if (e.target.value) {
                                                const selected = activePamms.find(p => p.id.toString() === e.target.value);
                                                if (selected) {
                                                    setPammMasterId(selected.account_id);
                                                    setPammStrategy(selected.strategy_name);
                                                    setPammStrategyProviderEmail(selected.strategy_provider_email || '');
                                                    setPammCommunityStructure(selected.community_structure || 'standard');
                                                }
                                            } else {
                                                setPammMasterId('');
                                                setPammStrategy('');
                                                setPammStrategyProviderEmail('');
                                                setPammCommunityStructure('standard');
                                            }
                                        }}
                                    >
                                        <option value="">-- Choose from Saved Harvesters --</option>
                                        {activePamms
                                            .slice()
                                            .sort((a, b) => (a.strategy_name || '').localeCompare(b.strategy_name || ''))
                                            .map(pamm => (
                                                <option key={pamm.id} value={pamm.id}>
                                                    {pamm.strategy_name} (ID: {pamm.account_id}){pamm.strategy_provider_email ? ` • ${pamm.strategy_provider_email}` : ''}
                                                </option>
                                            ))
                                        }
                                    </select>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-[10px] uppercase text-slate-500 font-bold block mb-1">Master Account ID</label>
                                        <input type="text" placeholder="e.g. 19086" value={pammMasterId} onChange={e => setPammMasterId(e.target.value)} className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2.5 text-white outline-none focus:border-purple-500 text-sm font-mono" />
                                    </div>
                                    <div>
                                        <label className="text-[10px] uppercase text-slate-500 font-bold block mb-1">Strategy Name</label>
                                        <input type="text" placeholder="e.g. Quantum" value={pammStrategy} onChange={e => setPammStrategy(e.target.value)} className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2.5 text-white outline-none focus:border-purple-500 text-sm" />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4 mt-4">
                                    <div>
                                        <label className="text-[10px] uppercase text-slate-500 font-bold block mb-1">Strategy Provider Email</label>
                                        <input type="email" placeholder="e.g. quantum@gmail.com" value={pammStrategyProviderEmail} onChange={e => setPammStrategyProviderEmail(e.target.value)} className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2.5 text-white outline-none focus:border-purple-500 text-sm" />
                                    </div>
                                    <div>
                                        <label className="text-[10px] uppercase text-slate-500 font-bold block mb-1">Community Structure</label>
                                        <select value={pammCommunityStructure} onChange={e => setPammCommunityStructure(e.target.value)} className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2.5 text-white outline-none focus:border-purple-500 text-sm appearance-none">
                                            <option value="standard">Standard</option>
                                            <option value="defily">Defily</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4 mt-4">
                                    <div>
                                        <label className="text-[10px] uppercase text-slate-500 font-bold block mb-1">Start Date</label>
                                        <input type="date" value={pammStartDate} onChange={e => setPammStartDate(e.target.value)} className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2.5 text-slate-300 outline-none focus:border-purple-500 text-sm" />
                                    </div>
                                    <div>
                                        <label className="text-[10px] uppercase text-slate-500 font-bold block mb-1">End Date</label>
                                        <input type="date" value={pammEndDate} onChange={e => setPammEndDate(e.target.value)} className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2.5 text-slate-300 outline-none focus:border-purple-500 text-sm" />
                                    </div>
                                </div>

                                {pammCalculationMode === 'live' ? (
                                    <div className="mt-4 bg-purple-950/20 border border-purple-500/30 rounded-xl p-5 flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-xl bg-purple-600/20 border border-purple-500/30 flex items-center justify-center text-purple-400 text-xl font-bold flex-shrink-0">
                                            ⚡
                                        </div>
                                        <div>
                                            <h4 className="text-sm font-bold text-white">Live Allocations Table Mode Active</h4>
                                            <p className="text-xs text-slate-400 mt-1">
                                                Follower capital (Am), live equity, and allocation ratios are automatically queried from <span className="text-purple-300 font-mono">public.pamm_live_allocations</span> for this strategy. No manual CSV export required.
                                            </p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="mt-4 border-2 border-dashed border-slate-700 rounded-xl p-8 flex flex-col items-center justify-center relative hover:border-purple-500/50 transition-colors bg-slate-950/50">
                                        <input 
                                            type="file" 
                                            accept=".csv" 
                                            onChange={(e) => setPammFile(e.target.files[0])}
                                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                        />
                                        <FileImage size={32} className={`mb-3 ${pammFile ? 'text-purple-500' : 'text-slate-600'}`} />
                                        {pammFile ? (
                                            <div className="text-center">
                                                <p className="text-sm font-bold text-white">{pammFile.name}</p>
                                                <p className="text-xs text-emerald-400 mt-1">Ready to process</p>
                                            </div>
                                        ) : (
                                            <div className="text-center pointer-events-none">
                                                <p className="text-sm font-bold text-white">Click or drag CSV here</p>
                                                <p className="text-xs text-slate-500 mt-1">Needs 'Allocation' and 'CreatedBy' columns</p>
                                            </div>
                                        )}
                                    </div>
                                )}
                                <div className="mt-4 flex items-start gap-3 bg-slate-950/50 p-4 rounded-xl border border-slate-700">
                                    <input 
                                        type="checkbox" 
                                        id="writeLedgerOnly" 
                                        checked={pammWriteLedgerOnly}
                                        onChange={(e) => setPammWriteLedgerOnly(e.target.checked)}
                                        className="mt-1 w-4 h-4 rounded border-slate-600 text-purple-600 focus:ring-purple-500 bg-slate-900"
                                    />
                                    <div>
                                        <label htmlFor="writeLedgerOnly" className="text-sm font-bold text-white cursor-pointer">
                                            Silent Backfill Mode (Do not pay wallets)
                                        </label>
                                        <p className="text-xs text-slate-400 mt-1">
                                            Check this if you are uploading historical data. It will save the commission ledger but bypass all wallet payments.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <button 
                                onClick={handleUploadPammCsv} 
                                disabled={isUploadingPamm || (pammCalculationMode === 'csv' && !pammFile) || !pammMasterId || !pammStrategy || !pammStartDate || !pammEndDate}
                                className="w-full mt-6 bg-purple-600 hover:bg-purple-500 disabled:bg-slate-800 disabled:text-slate-500 text-white py-3 rounded-xl text-sm font-bold transition-colors shadow-lg flex items-center justify-center gap-2"
                            >
                                {isUploadingPamm 
                                    ? 'Processing PAMM Calculation...' 
                                    : (pammCalculationMode === 'live' ? '⚡ Run PAMM Calculation (1-Click)' : 'Distribute IB Commissions (CSV)')}
                            </button>
                        </div>
                    </div>

                    {/* NEW: HARVESTER MANAGEMENT */}
                    <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6 mt-8">
                        <div className="mb-6">
                            <h3 className="font-bold text-white text-lg flex items-center gap-2">
                                <span className="text-blue-500">⚙️</span> Harvester Management
                            </h3>
                            <p className="text-sm text-slate-400 mt-1">Manage the list of active PAMM managers tracked by backend cron jobs.</p>
                        </div>
                        
                        <div className="flex gap-3 items-end mb-6 bg-slate-950 p-4 rounded-xl border border-slate-800">
                            <div className="flex-1">
                                <label className="text-[10px] uppercase text-slate-500 font-bold block mb-1">Master Account ID</label>
                                <input type="number" placeholder="e.g. 18282" value={newPamm.account_id} onChange={e => setNewPamm({...newPamm, account_id: e.target.value})} className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-white outline-none focus:border-blue-500 text-sm font-mono" />
                            </div>
                            <div className="flex-1">
                                <label className="text-[10px] uppercase text-slate-500 font-bold block mb-1">Strategy Name</label>
                                <input type="text" placeholder="e.g. Gold Fund 101" value={newPamm.strategy_name} onChange={e => setNewPamm({...newPamm, strategy_name: e.target.value})} className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-white outline-none focus:border-blue-500 text-sm" />
                            </div>
                        </div>
                        
                        <div className="flex gap-3 items-end mb-6 bg-slate-950 p-4 rounded-xl border border-slate-800 mt-3">
                            <div className="flex-1">
                                <label className="text-[10px] uppercase text-slate-500 font-bold block mb-1">Provider Email</label>
                                <input type="email" placeholder="e.g. email@gmail.com" value={newPamm.strategy_provider_email} onChange={e => setNewPamm({...newPamm, strategy_provider_email: e.target.value})} className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-white outline-none focus:border-blue-500 text-sm" />
                            </div>
                            <div className="flex-1">
                                <label className="text-[10px] uppercase text-slate-500 font-bold block mb-1">Community Structure</label>
                                <select value={newPamm.community_structure} onChange={e => setNewPamm({...newPamm, community_structure: e.target.value})} className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-white outline-none focus:border-blue-500 text-sm appearance-none">
                                    <option value="standard">Standard</option>
                                    <option value="defily">Defily</option>
                                </select>
                            </div>
                            <button onClick={handleAddPamm} className="bg-blue-600 hover:bg-blue-500 text-white px-6 py-2.5 rounded-lg text-sm font-bold transition-colors shadow-lg whitespace-nowrap">
                                Add Harvester
                            </button>
                        </div>

                        <div className="overflow-x-auto border border-slate-800 rounded-xl bg-slate-950">
                            <table className="w-full text-left">
                                <thead className="bg-slate-900 text-xs font-bold text-slate-400 uppercase">
                                    <tr>
                                        <th className="px-4 py-3">Strategy Name</th>
                                        <th className="px-4 py-3">Account ID</th>
                                        <th className="px-4 py-3">Provider Email</th>
                                        <th className="px-4 py-3">Community</th>
                                        <th className="px-4 py-3">Status</th>
                                        <th className="px-4 py-3 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-800 text-sm">
                                    {isLoadingPamms ? (
                                        <tr><td colSpan="4" className="p-8 text-center text-slate-500 italic">Loading harvesters...</td></tr>
                                    ) : activePamms.length === 0 ? (
                                        <tr><td colSpan="4" className="p-8 text-center text-slate-500 italic">No active harvesters found.</td></tr>
                                    ) : (
                                        activePamms.map(pamm => (
                                            <tr key={pamm.id} className="hover:bg-slate-900/50 transition-colors">
                                                {editingPammId === pamm.id ? (
                                                    <>
                                                        <td className="px-4 py-3">
                                                            <input type="text" value={editingPammData.strategy_name} onChange={e => setEditingPammData({...editingPammData, strategy_name: e.target.value})} className="w-full bg-slate-950 border border-slate-700 rounded p-1 text-white text-xs" />
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <input type="number" value={editingPammData.account_id} onChange={e => setEditingPammData({...editingPammData, account_id: e.target.value})} className="w-full bg-slate-950 border border-slate-700 rounded p-1 text-white text-xs font-mono" />
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <input type="email" value={editingPammData.strategy_provider_email} onChange={e => setEditingPammData({...editingPammData, strategy_provider_email: e.target.value})} className="w-full bg-slate-950 border border-slate-700 rounded p-1 text-white text-xs" />
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <select value={editingPammData.community_structure} onChange={e => setEditingPammData({...editingPammData, community_structure: e.target.value})} className="w-full bg-slate-950 border border-slate-700 rounded p-1 text-white text-xs">
                                                                <option value="standard">Standard</option>
                                                                <option value="defily">Defily</option>
                                                            </select>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <button disabled className="px-3 py-1 rounded-full text-xs font-bold border border-slate-700 bg-slate-800 text-slate-500">
                                                                Editing...
                                                            </button>
                                                        </td>
                                                        <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                                                            <button onClick={handleSaveEditPamm} className="text-green-400 hover:text-green-300 transition-colors p-1 text-xs font-bold">
                                                                Save
                                                            </button>
                                                            <button onClick={() => setEditingPammId(null)} className="text-slate-400 hover:text-slate-300 transition-colors p-1 text-xs font-bold">
                                                                Cancel
                                                            </button>
                                                        </td>
                                                    </>
                                                ) : (
                                                    <>
                                                        <td className="px-4 py-3 font-bold text-white">{pamm.strategy_name}</td>
                                                        <td className="px-4 py-3 text-blue-400 font-mono text-xs">{pamm.account_id}</td>
                                                        <td className="px-4 py-3 text-slate-300 text-xs">{pamm.strategy_provider_email || '-'}</td>
                                                        <td className="px-4 py-3 text-slate-300 text-xs capitalize">{pamm.community_structure || 'standard'}</td>
                                                        <td className="px-4 py-3">
                                                            <button 
                                                                onClick={() => handleTogglePamm(pamm.id, pamm.is_active)}
                                                                className={`px-3 py-1 rounded-full text-xs font-bold border transition-colors ${pamm.is_active ? 'bg-green-500/10 text-green-400 border-green-500/20 hover:bg-green-500/20' : 'bg-slate-800 text-slate-400 border-slate-700 hover:bg-slate-700'}`}
                                                            >
                                                                {pamm.is_active ? 'Active' : 'Inactive'}
                                                            </button>
                                                        </td>
                                                        <td className="px-4 py-3 text-right whitespace-nowrap">
                                                            <button 
                                                                onClick={() => {
                                                                    setPammMasterId(pamm.account_id);
                                                                    setPammStrategy(pamm.strategy_name);
                                                                    if (pamm.strategy_provider_email) setPammStrategyProviderEmail(pamm.strategy_provider_email);
                                                                    if (pamm.community_structure) setPammCommunityStructure(pamm.community_structure);
                                                                    setPammCalculationMode('live');
                                                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                                                }} 
                                                                className="inline-flex items-center gap-1 bg-purple-600/20 hover:bg-purple-600/40 text-purple-300 border border-purple-500/30 px-2.5 py-1 rounded-lg text-xs font-bold transition-colors mr-2"
                                                                title="Load into PAMM Calculator"
                                                            >
                                                                ⚡ Run
                                                            </button>
                                                            <button onClick={() => handleEditClick(pamm)} className="text-blue-400 hover:text-blue-300 transition-colors p-2" title="Edit Harvester">
                                                                <Edit2 size={16} />
                                                            </button>
                                                            <button onClick={() => handleDeletePamm(pamm.id)} className="text-slate-500 hover:text-red-400 transition-colors p-2" title="Delete Harvester">
                                                                <X size={16} />
                                                            </button>
                                                        </td>
                                                    </>
                                                )}
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* BOTTOM: PAMM PAYOUT HISTORY */}
                    <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6 mt-8">
                        <div className="mb-6 flex justify-between items-end">
                            <div>
                                <h3 className="font-bold text-white text-lg flex items-center gap-2">
                                    <span className="text-emerald-500">📊</span> PAMM Payout History
                                </h3>
                                <p className="text-sm text-slate-400 mt-1">A permanent log of processed PAMM runs and total Master lots traded to calculate franchise bonuses.</p>
                            </div>
                            <button
                                onClick={exportPammLogsToCSV}
                                className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg text-sm font-bold border border-slate-700 transition-colors"
                            >
                                <Download size={16} /> Export CSV
                            </button>
                        </div>
                        <div className="overflow-x-auto border border-slate-800 rounded-xl bg-slate-950">
                            <table className="w-full text-left">
                                <thead className="bg-slate-900 text-xs font-bold text-slate-400 uppercase">
                                    <tr>
                                        <th className="px-4 py-3">Date Run</th>
                                        <th className="px-4 py-3">Strategy Name</th>
                                        <th className="px-4 py-3">Date Range</th>
                                        <th className="px-4 py-3 text-right">Total Lots Traded</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-800 text-sm">
                                    {pammLogs.length === 0 ? (
                                        <tr><td colSpan="4" className="p-8 text-center text-slate-500 italic">No payout history found.</td></tr>
                                    ) : (
                                        pammLogs.map(log => (
                                            <tr key={log.id} className="hover:bg-slate-900/50 transition-colors">
                                                <td className="px-4 py-3 text-slate-400 text-xs">{new Date(log.created_at).toLocaleDateString()}</td>
                                                <td className="px-4 py-3 font-bold text-white">{log.strategy_name}</td>
                                                <td className="px-4 py-3 text-slate-300">{log.start_date} to {log.end_date}</td>
                                                <td className="px-4 py-3 text-emerald-400 font-mono text-right font-bold">{Number(log.total_lots).toFixed(2)} Lots</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* 🚨 NEW: PERFORMANCE FEE & PROFIT SPLITS TABLE */}
                    <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6 mt-6 shadow-xl">
                        <div className="mb-6 flex justify-between items-end">
                            <div>
                                <h3 className="font-bold text-white text-lg flex items-center gap-2">
                                    <div className="p-1.5 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                                        <span className="text-emerald-500 font-bold">$</span>
                                    </div>
                                    Performance Fee & Profit Splits
                                </h3>
                                <p className="text-sm text-slate-400 mt-1">A permanent ledger of 30% performance fee deductions, corporate sweeps, and IB distributions.</p>
                            </div>
                        </div>

                        <div className="overflow-x-auto border border-slate-800 rounded-xl bg-slate-950">
                            <table className="w-full text-left border-collapse">
                                <thead className="bg-slate-900">
                                    <tr className="border-b border-slate-800 text-[10px] uppercase tracking-widest text-slate-500">
                                        <th className="px-4 py-3 font-bold">Date</th>
                                        <th className="px-4 py-3 font-bold">Strategy</th>
                                        <th className="px-4 py-3 font-bold">Trigger Event</th>
                                        <th className="px-4 py-3 font-bold text-right">Gross Profit</th>
                                        <th className="px-4 py-3 font-bold text-right text-emerald-400">Net to Clients (70%)</th>
                                        <th className="px-4 py-3 font-bold text-right">Trader Cut</th>
                                        <th className="px-4 py-3 font-bold text-right">IB Network</th>
                                        <th className="px-4 py-3 font-bold text-right text-yellow-500">Corporate Cut</th>
                                    </tr>
                                </thead>
                                <tbody className="text-sm">
                                    {profitLogs.length > 0 ? (
                                        profitLogs.map((log) => (
                                            <tr key={log.id} className="border-b border-slate-800/50 hover:bg-slate-900/50 transition-colors group">
                                                <td className="px-4 py-4 text-slate-400 font-mono text-xs">
                                                    {new Date(log.run_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })}
                                                </td>
                                                <td className="px-4 py-4 font-bold text-white">{log.strategy_name}</td>
                                                <td className="px-4 py-4">
                                                    <span className={`px-2 py-0.5 rounded text-[9px] uppercase font-bold tracking-wider border ${log.trigger_type === 'Weekly Run' ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' : 'bg-purple-500/10 text-purple-400 border-purple-500/20'}`}>
                                                        {log.trigger_type}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-4 text-slate-300 font-mono text-right font-bold">${Number(log.gross_profit).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                                                <td className="px-4 py-4 text-emerald-400 font-mono font-bold text-right">${Number(log.net_follower_profit).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                                                <td className="px-4 py-4 text-slate-400 font-mono text-right">${Number(log.trader_cut).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                                                <td className="px-4 py-4 text-slate-400 font-mono text-right">${Number(log.total_ib_cut).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                                                <td className="px-4 py-4 text-yellow-500 font-mono font-bold text-right">${Number(log.nommia_cut).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="8" className="p-8 text-center text-slate-500 italic">
                                                No performance fees recorded yet.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                </div>
            )}

            {/* CONDITIONAL RENDER: GLOBAL PAYOUTS */}
            {mainTab === 'payouts' && (
                <div className="animate-fade-in space-y-8">
                    <div className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden shadow-xl">
                        <div className="p-6 border-b border-slate-800 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-slate-800/30">
                            <div>
                                <h3 className="font-bold text-white text-lg flex items-center gap-2"><Activity className="text-amber-500"/> Admin Withdrawals Management</h3>
                                <p className="text-sm text-slate-400 mt-1">Review, filter, and manually process all transaction records across the network.</p>
                            </div>
                            <label className="flex items-center space-x-2 cursor-pointer bg-slate-950 px-4 py-2 rounded-lg border border-slate-800 hover:border-slate-700 transition-colors">
                                <input
                                    type="checkbox"
                                    className="form-checkbox h-4 w-4 text-amber-500 rounded border-slate-600 bg-slate-800 focus:ring-amber-500 focus:ring-offset-slate-900"
                                    checked={showIssuesOnly}
                                    onChange={(e) => setShowIssuesOnly(e.target.checked)}
                                />
                                <span className="text-sm font-bold text-slate-300">
                                    Show Failed / Manual Review Only
                                </span>
                            </label>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-left">
                                <thead className="bg-slate-950 text-xs font-bold text-slate-400 uppercase tracking-wider">
                                    <tr>
                                        <th className="px-6 py-4">Date</th>
                                        <th className="px-6 py-4">Partner Email</th>
                                        <th className="px-6 py-4">Reference ID</th>
                                        <th className="px-6 py-4">Method & Dest.</th>
                                        <th className="px-6 py-4 text-right">Amount</th>
                                        <th className="px-6 py-4 text-center">Status</th>
                                        <th className="px-6 py-4 text-center">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-800 text-sm">
                                    {(() => {
                                        // Filter Logic
                                        let displayData = allWithdrawals;
                                        if (showIssuesOnly) {
                                            displayData = displayData.filter(w => w.status === 'Failed' || w.status === 'Manual Review Required');
                                        }

                                        if (displayData.length === 0) return <tr><td colSpan="7" className="p-12 text-center text-slate-500 italic">No withdrawals found matching your current filters.</td></tr>;
                                        
                                        return displayData.map(req => (
                                            <tr key={req.id} className="hover:bg-slate-800/50 transition-colors">
                                                <td className="px-6 py-4">
                                                    <span className="text-slate-300 block">{new Date(req.created_at).toLocaleDateString()}</span>
                                                    <span className="text-slate-500 text-xs font-mono">{new Date(req.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                                                </td>
                                                <td className="px-6 py-4 font-bold text-white">{resolvePartnerEmail(req.partner_id)}</td>
                                                <td className="px-6 py-4 font-mono text-slate-500 text-xs">{req.reference_id || `PAY-${req.id.toString().substring(0,6)}`}</td>
                                                <td className="px-6 py-4">
                                                    <div className="flex flex-col gap-1">
                                                        <span className="font-bold text-blue-400 text-xs">{req.method}</span>
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-slate-300 font-mono text-[10px] bg-slate-950 px-2 py-1 rounded border border-slate-800 max-w-[150px] truncate" title={req.destination}>{req.destination || 'N/A'}</span>
                                                            {req.destination && (
                                                                <button onClick={() => {navigator.clipboard.writeText(req.destination); alert('Address copied!');}} className="text-slate-500 hover:text-white transition-colors" title="Copy to clipboard">
                                                                    <Copy size={12}/>
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-right font-bold text-amber-500 text-base">${Number(req.amount).toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                                                <td className="px-6 py-4 text-center">
                                                    <span className={`px-2.5 py-1 rounded text-[10px] font-bold uppercase tracking-wider border ${getStatusColor(req.status)}`}>
                                                        {req.status || 'Pending'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    {/* Shows buttons for Pending, Failed, or Manual Review */}
                                                    {(req.status === 'Failed' || req.status === 'Manual Review Required' || req.status === 'Pending' || req.status === 'Pending Network') && (
                                                        <div className="flex flex-col items-center gap-2">
                                                            <button 
                                                                onClick={() => processAdminPayout(req.id, 'Completed')} 
                                                                disabled={isUpdatingStatus} 
                                                                className="w-full bg-blue-600 hover:bg-blue-500 text-white text-[10px] uppercase tracking-wider font-bold px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50 whitespace-nowrap"
                                                            >
                                                                Mark as Paid
                                                            </button>
                                                            <button 
                                                                onClick={() => processAdminPayout(req.id, 'Dismissed')} 
                                                                disabled={isUpdatingStatus} 
                                                                className="w-full bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 text-[10px] uppercase tracking-wider font-bold px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50 whitespace-nowrap"
                                                            >
                                                                Dismiss (No Email)
                                                            </button>
                                                            <button 
                                                                onClick={() => processAdminPayout(req.id, 'Declined')} 
                                                                disabled={isUpdatingStatus} 
                                                                className="w-full bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30 text-[10px] uppercase tracking-wider font-bold px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50 whitespace-nowrap"
                                                            >
                                                                Decline
                                                            </button>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        ));
                                    })()}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
          </>
      ) : (
          renderIBDetail()
      )}
{/* --- CLIENT PROFILE MODAL --- */}
      {selectedClient && (
        <ClientProfile 
          key={selectedClient.id}
          client={selectedClient} 
          onClose={() => setSelectedClient(null)} 
          onClientSwitch={(newClient) => setSelectedClient(newClient)}
          userRole="Manager"
          currentUser={currentUser}
        />
      )}

      {/* --- PAMM PREVIEW APPROVAL MODAL --- */}
      {previewModalOpen && previewData && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
            <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm" onClick={() => !isExecutingPayout && setPreviewModalOpen(false)} />
            <div className="relative w-full max-w-6xl bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden">
                {/* Header */}
                <div className="p-6 border-b border-slate-800 flex justify-between items-center bg-slate-800/50">
                    <div>
                        <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                            <span className="text-blue-500">📊</span> PAMM Commission Dry-Run Preview
                        </h2>
                        <p className="text-sm text-slate-400 mt-1">Review the mathematical breakdown before finalizing the payouts. No database writes or wallet deductions have occurred yet.</p>
                    </div>
                    <div className="flex gap-2">
                        <button 
                            onClick={() => {
                                if (!previewData || !previewData.partnerEarningsBreakdown) return;
                                let csvContent = "data:text/csv;charset=utf-8,Partner (Earner),Source Client,Type,Level,Amount Earned\n";
                                Object.entries(previewData.partnerEarningsBreakdown).forEach(([partnerEmail, breakdown]) => {
                                    breakdown.forEach(item => {
                                        csvContent += `${partnerEmail},${item.clientEmail},${item.type},${item.level},${item.amount.toFixed(2)}\n`;
                                    });
                                });
                                const encodedUri = encodeURI(csvContent);
                                const link = document.createElement("a");
                                link.setAttribute("href", encodedUri);
                                link.setAttribute("download", "pamm_commission_preview.csv");
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                            }}
                            className="text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 px-3 py-2 rounded-lg transition-colors flex items-center gap-2 text-sm font-bold tracking-wide"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                            Export CSV
                        </button>
                        <button onClick={() => !isExecutingPayout && setPreviewModalOpen(false)} className="text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 p-2 rounded-lg transition-colors">
                            <X size={24} />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-8">
                    {/* Global Totals */}
                    <div>
                        <h3 className="text-lg font-bold text-white mb-4 border-b border-slate-800 pb-2">Global Totals</h3>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                            <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700">
                                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-bold mb-1">Total Lots</p>
                                <p className="text-xl font-black text-white">{previewData.totalPammLots?.toFixed(2)}</p>
                            </div>
                            <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700">
                                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-bold mb-1">Total Gross Profit</p>
                                <p className="text-xl font-black text-white">${previewData.totalGrossProfit?.toFixed(2)}</p>
                            </div>
                            <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700">
                                <p className="text-[10px] text-blue-400 uppercase tracking-wider font-bold mb-1">Total Trader Cut</p>
                                <p className="text-xl font-black text-blue-400">${previewData.totalTraderCut?.toFixed(2)}</p>
                            </div>
                            <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700">
                                <p className="text-[10px] text-purple-400 uppercase tracking-wider font-bold mb-1">Total IB Cut</p>
                                <p className="text-xl font-black text-purple-400">${previewData.totalIbCut?.toFixed(2)}</p>
                            </div>
                            <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700">
                                <p className="text-[10px] text-green-400 uppercase tracking-wider font-bold mb-1">Net Follower Profit</p>
                                <p className="text-xl font-black text-green-400">${previewData.totalNetFollowerProfit?.toFixed(2)}</p>
                            </div>
                        </div>
                    </div>

                    {/* Top Earners */}
                    <div>
                        <h3 className="text-lg font-bold text-white mb-4 border-b border-slate-800 pb-2">Top Earners</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {/* Partners */}
                            <div className="bg-slate-800/30 p-4 rounded-xl border border-slate-700">
                                <h4 className="text-sm font-bold text-slate-300 mb-3 uppercase tracking-wider">Partner Wallets</h4>
                                <div className="space-y-2 max-h-48 overflow-y-auto pr-2 custom-scrollbar">
                                    {previewData.partnerWallets && Object.entries(previewData.partnerWallets).sort((a,b) => b[1] - a[1]).map(([email, amount]) => (
                                        <div key={email} className="flex justify-between items-center text-sm">
                                            <span className="text-slate-400 truncate pr-2">{email}</span>
                                            <span className="text-green-400 font-bold">${amount.toFixed(2)}</span>
                                        </div>
                                    ))}
                                    {(!previewData.partnerWallets || Object.keys(previewData.partnerWallets).length === 0) && <p className="text-xs text-slate-500 italic">No partner payouts.</p>}
                                </div>
                            </div>
                            
                            {/* Countries */}
                            <div className="bg-slate-800/30 p-4 rounded-xl border border-slate-700">
                                <h4 className="text-sm font-bold text-slate-300 mb-3 uppercase tracking-wider">Country Wallets</h4>
                                <div className="space-y-2 max-h-48 overflow-y-auto pr-2 custom-scrollbar">
                                    {previewData.countryWallets && Object.entries(previewData.countryWallets).sort((a,b) => b[1] - a[1]).map(([email, amount]) => (
                                        <div key={email} className="flex justify-between items-center text-sm">
                                            <span className="text-slate-400 truncate pr-2">{email}</span>
                                            <span className="text-yellow-400 font-bold">${amount.toFixed(2)}</span>
                                        </div>
                                    ))}
                                    {(!previewData.countryWallets || Object.keys(previewData.countryWallets).length === 0) && <p className="text-xs text-slate-500 italic">No country payouts.</p>}
                                </div>
                            </div>

                            {/* Regions */}
                            <div className="bg-slate-800/30 p-4 rounded-xl border border-slate-700">
                                <h4 className="text-sm font-bold text-slate-300 mb-3 uppercase tracking-wider">Regional Wallets</h4>
                                <div className="space-y-2 max-h-48 overflow-y-auto pr-2 custom-scrollbar">
                                    {previewData.regionalWallets && Object.entries(previewData.regionalWallets).sort((a,b) => b[1] - a[1]).map(([email, amount]) => (
                                        <div key={email} className="flex justify-between items-center text-sm">
                                            <span className="text-slate-400 truncate pr-2">{email}</span>
                                            <span className="text-indigo-400 font-bold">${amount.toFixed(2)}</span>
                                        </div>
                                    ))}
                                    {(!previewData.regionalWallets || Object.keys(previewData.regionalWallets).length === 0) && <p className="text-xs text-slate-500 italic">No regional payouts.</p>}
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Level 1 Activity Detection */}
                    <div>
                        <h3 className="text-lg font-bold text-white mb-4 border-b border-slate-800 pb-2">Level 1 Activity Detection</h3>
                        <div className="bg-slate-800/30 rounded-xl border border-slate-700 overflow-hidden">
                            <div className="max-h-64 overflow-y-auto custom-scrollbar">
                                <table className="w-full text-left text-sm text-slate-400">
                                    <thead className="text-[10px] text-slate-400 uppercase bg-slate-800 sticky top-0">
                                        <tr>
                                            <th className="px-4 py-3">Leader (Sponsor)</th>
                                            <th className="px-4 py-3">Active Direct Referrals (L1)</th>
                                            <th className="px-4 py-3 text-center">Count</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-800">
                                        {previewData.level1Activity && Object.entries(previewData.level1Activity).map(([sponsor, followers]) => (
                                            <tr key={sponsor} className="hover:bg-slate-800/50">
                                                <td className="px-4 py-3 font-medium text-white">{sponsor}</td>
                                                <td className="px-4 py-3 text-xs">
                                                    <div className="flex flex-wrap gap-1">
                                                        {followers.slice(0, 5).map(f => <span key={f} className="bg-slate-700 px-2 py-0.5 rounded text-slate-300">{f}</span>)}
                                                        {followers.length > 5 && <span className="bg-slate-800 border border-slate-700 px-2 py-0.5 rounded text-slate-400">+{followers.length - 5} more</span>}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 text-center text-white font-bold">{followers.length}</td>
                                            </tr>
                                        ))}
                                        {(!previewData.level1Activity || Object.keys(previewData.level1Activity).length === 0) && (
                                            <tr><td colSpan="3" className="px-4 py-6 text-center text-slate-500 italic">No Level 1 Activity detected.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* Deep-Dive Analytics */}
                    <div>
                        <h3 className="text-lg font-bold text-white mb-4 border-b border-slate-800 pb-2">Deep-Dive Follower Analytics</h3>
                        <div className="bg-slate-800/30 rounded-xl border border-slate-700 overflow-hidden">
                            <div className="max-h-96 overflow-y-auto custom-scrollbar">
                                <table className="w-full text-left text-sm text-slate-400">
                                    <thead className="text-[10px] text-slate-400 uppercase bg-slate-800 sticky top-0 z-10">
                                        <tr>
                                            <th className="px-4 py-3">Follower</th>
                                            <th className="px-4 py-3 text-right">Lots</th>
                                            <th className="px-4 py-3 text-right">Gross Profit</th>
                                            <th className="px-4 py-3 text-right">Theor. Master Rebate</th>
                                            <th className="px-4 py-3 text-right">Theor. Master Profit</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-800">
                                        {previewData.followerAnalytics && previewData.followerAnalytics.map((fa, i) => (
                                            <tr key={i} className="hover:bg-slate-800/50">
                                                <td className="px-4 py-3">
                                                    <div className="text-white font-medium">{fa.followerEmail}</div>
                                                    <div className="text-[10px] text-slate-500 mt-1 font-mono">Tree: {JSON.stringify(fa.sponsorTreeArray)}</div>
                                                </td>
                                                <td className="px-4 py-3 text-right font-medium">{formatLots(fa.userLots)}</td>
                                                <td className="px-4 py-3 text-right text-green-400">{formatCurrency(fa.grossProfit)}</td>
                                                <td className="px-4 py-3 text-right">{formatCurrency(fa.theoreticalMasterRebate)}</td>
                                                <td className="px-4 py-3 text-right">{formatCurrency(fa.theoreticalMasterProfit)}</td>
                                            </tr>
                                        ))}
                                        {(!previewData.followerAnalytics || previewData.followerAnalytics.length === 0) && (
                                            <tr><td colSpan="5" className="px-4 py-6 text-center text-slate-500 italic">No follower analytics data available.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* Itemized Profit Splits (Performance Fees) */}
                    <div className="mt-8">
                        <h3 className="text-lg font-bold text-white mb-4 border-b border-slate-800 pb-2">Itemized Profit Splits (Performance Fees)</h3>
                        <div className="bg-slate-800/30 rounded-xl border border-slate-700 overflow-hidden">
                            <div className="max-h-64 overflow-y-auto custom-scrollbar">
                                <table className="w-full text-left text-sm text-slate-400">
                                    <thead className="text-[10px] text-slate-400 uppercase bg-slate-800 sticky top-0 z-10">
                                        <tr>
                                            <th className="px-4 py-3">Partner (Earner)</th>
                                            <th className="px-4 py-3">Source Client</th>
                                            <th className="px-4 py-3 text-center">Level / Type</th>
                                            <th className="px-4 py-3 text-right">Amount Earned</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-800">
                                        {previewData.partnerEarningsBreakdown && Object.entries(previewData.partnerEarningsBreakdown).some(([_, b]) => b.some(i => i.type !== 'rebate')) ? (
                                            Object.entries(previewData.partnerEarningsBreakdown).map(([partnerEmail, breakdown]) => {
                                                const splits = breakdown.filter(i => i.type !== 'rebate');
                                                if (splits.length === 0) return null;
                                                return splits.map((item, idx) => (
                                                    <tr key={partnerEmail + idx} className="hover:bg-slate-800/50">
                                                        <td className="px-4 py-3 text-white font-medium">{idx === 0 ? partnerEmail : ''}</td>
                                                        <td className="px-4 py-3">{item.clientEmail}</td>
                                                        <td className="px-4 py-3 text-center">
                                                            <span className="px-2 py-0.5 rounded text-[10px] uppercase font-bold bg-purple-500/20 text-purple-400">
                                                                {item.level === 'master' ? 'Master Split' : `L${item.level} Split`}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3 text-right text-green-400 font-medium">{formatCurrency(item.amount)}</td>
                                                    </tr>
                                                ));
                                            })
                                        ) : (
                                            <tr><td colSpan="4" className="px-4 py-6 text-center text-slate-500 italic">No profit splits generated in this run.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* Itemized Volume Rebates */}
                    <div className="mt-8">
                        <h3 className="text-lg font-bold text-white mb-4 border-b border-slate-800 pb-2">Itemized Volume Rebates</h3>
                        <div className="bg-slate-800/30 rounded-xl border border-slate-700 overflow-hidden">
                            <div className="max-h-64 overflow-y-auto custom-scrollbar">
                                <table className="w-full text-left text-sm text-slate-400">
                                    <thead className="text-[10px] text-slate-400 uppercase bg-slate-800 sticky top-0 z-10">
                                        <tr>
                                            <th className="px-4 py-3">Partner (Earner)</th>
                                            <th className="px-4 py-3">Source Client</th>
                                            <th className="px-4 py-3 text-center">Level / Type</th>
                                            <th className="px-4 py-3 text-right">Amount Earned</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-800">
                                        {previewData.partnerEarningsBreakdown && Object.entries(previewData.partnerEarningsBreakdown).some(([_, b]) => b.some(i => i.type === 'rebate')) ? (
                                            Object.entries(previewData.partnerEarningsBreakdown).map(([partnerEmail, breakdown]) => {
                                                const rebates = breakdown.filter(i => i.type === 'rebate');
                                                if (rebates.length === 0) return null;
                                                return rebates.map((item, idx) => (
                                                    <tr key={partnerEmail + idx} className="hover:bg-slate-800/50">
                                                        <td className="px-4 py-3 text-white font-medium">{idx === 0 ? partnerEmail : ''}</td>
                                                        <td className="px-4 py-3">{item.clientEmail}</td>
                                                        <td className="px-4 py-3 text-center">
                                                            <span className="px-2 py-0.5 rounded text-[10px] uppercase font-bold bg-blue-500/20 text-blue-400">
                                                                {item.level === 'master' ? 'Master Rebate' : `L${item.level} Rebate`}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3 text-right text-green-400 font-medium">{formatCurrency(item.amount)}</td>
                                                    </tr>
                                                ));
                                            })
                                        ) : (
                                            <tr><td colSpan="4" className="px-4 py-6 text-center text-slate-500 italic">No volume rebates generated in this run.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer Actions */}
                <div className="p-6 border-t border-slate-800 bg-slate-800/50 flex justify-end gap-3">
                    <button 
                        onClick={() => {
                            setPreviewModalOpen(false);
                            setPammPayloadCache(null);
                            setPreviewData(null);
                        }}
                        disabled={isExecutingPayout}
                        className="px-6 py-2.5 rounded-lg font-bold text-sm text-slate-300 hover:text-white bg-slate-800 hover:bg-slate-700 border border-slate-700 transition-colors"
                    >
                        Cancel
                    </button>
                    <button 
                        onClick={handleExecutePammPayouts}
                        disabled={isExecutingPayout}
                        className="px-6 py-2.5 rounded-lg font-bold text-sm text-white bg-green-600 hover:bg-green-500 transition-colors flex items-center gap-2 shadow-lg shadow-green-500/20 disabled:opacity-50"
                    >
                        {isExecutingPayout ? 'Executing...' : '✅ Approve & Execute Payouts'}
                    </button>
                </div>
            </div>
        </div>
      )}
    </div>
  );
};

export default PartnersPage;